import { TOOL_TYPES } from '../store/useToolStore';
import { LOADER_TYPES, getLoaderMetadata } from '../data/loaderTypes';
import {
  parseStoryboardWithBackend,
  analyzeConstraintsWithBackend,
  extractStylesWithBackend,
  generateSwiftUIWithBackend,
  checkBackendAvailability
} from './storyboardBackendApi';

/**
 * ============================================================================
 * BASE LOADER CODE GENERATOR
 * ============================================================================
 * 
 * This is the base implementation for generating SwiftUI loader code.
 * All loaders follow this pattern:
 * - Simplified API: Only `isLoading` binding is exposed
 * - Default values: All configuration is hardcoded with defaults
 * - Separate components: Each loader has its own struct and ViewModifier
 * - Usage: .loaderName(isLoading: $isLoading)
 * 
 * Pattern:
 * 1. Loader Component (e.g., ParticleLoader) with default values
 * 2. ViewModifier (e.g., ParticleLoaderModifier) with only isLoading binding
 * 3. View Extension for easy usage
 * 4. Usage example
 * 
 * ============================================================================
 */

/**
 * Generate SwiftUI Loader code with separate reusable components
 */
export const generateLoaderCode = (config) => {
  const {
    style,
    // eslint-disable-next-line no-unused-vars
    showBackground,
    // eslint-disable-next-line no-unused-vars
    backgroundOpacity,
    includeHelperClasses = true,
  } = config;

  // Generate the specific loader component
  const loaderComponentCode = generateSpecificLoaderComponent(style, config);
  
  // Generate loader-specific ViewModifier
  const modifierCode = generateLoaderSpecificModifier(style, config);
  
  // Generate usage example
  const usageExample = generateUsageExample(style, config);
  
  // Get loader name for block title
  const loaderMetadata = getLoaderMetadata(style);
  const loaderName = loaderMetadata.name;

  // Build blocks array
  const blocks = [
    {
      name: 'Usage Example',
      code: usageExample,
      requiresImport: true,
      order: 1
    },
    {
      name: `${loaderName} Loader`,
      code: loaderComponentCode,
      requiresImport: false,
      order: 2
    },
    {
      name: 'Loader Modifier',
      code: modifierCode,
      requiresImport: false,
      order: 3
    }
  ];

  // Add helper extensions if enabled
  if (includeHelperClasses) {
    blocks.push({
      name: 'Color Extension',
      code: generateColorExtension(),
      requiresImport: false,
      order: 4,
      optional: true
    });
  }

  return {
    blocks,
    importStatement: 'import SwiftUI'
  };
};

// This function is kept for potential future use but currently unused
// eslint-disable-next-line no-unused-vars
const _generateLoaderStyleCases = (config) => {
  const style = config.style;
  
  switch (style) {
    case LOADER_TYPES.BARS:
      return `case .bars(let barCount, let barWidth, let barHeight, let barSpacing, let useGradient):
            BarsLoader(size: size, barCount: barCount, barWidth: barWidth, barHeight: barHeight, barSpacing: barSpacing, primaryColor: primaryColor, secondaryColor: secondaryColor, speed: speed, useGradient: useGradient)`;
    case LOADER_TYPES.WAVE:
      return `case .wave(let waveCount, let waveHeight, let waveWidth, let useGradient):
            WaveLoader(size: size, waveCount: waveCount, waveHeight: waveHeight, waveWidth: waveWidth, primaryColor: primaryColor, secondaryColor: secondaryColor, speed: speed, useGradient: useGradient)`;
    case LOADER_TYPES.SPINNER:
      return `case .spinner(let armCount, let armLength, let useGradient):
            SpinnerLoader(size: size, armCount: armCount, armLength: armLength, primaryColor: primaryColor, secondaryColor: secondaryColor, speed: speed, useGradient: useGradient)`;
    case LOADER_TYPES.PROGRESS_RING:
      return `case .progressRing(let progress, let strokeWidth):
            ProgressRingLoader(size: size, progress: progress, strokeWidth: strokeWidth, primaryColor: primaryColor, secondaryColor: secondaryColor, speed: speed)`;
    case LOADER_TYPES.SKELETON:
      return `case .skeleton(let lineCount, let lineHeight, let lineSpacing):
            SkeletonLoader(size: size, lineCount: lineCount, lineHeight: lineHeight, lineSpacing: lineSpacing, primaryColor: primaryColor, speed: speed)`;
    case LOADER_TYPES.BOUNCE:
      return `case .bounce(let dotCount, let dotSize, let bounceHeight, let useGradient):
            BounceLoader(size: size, dotCount: dotCount, dotSize: dotSize, bounceHeight: bounceHeight, primaryColor: primaryColor, secondaryColor: secondaryColor, speed: speed, useGradient: useGradient)`;
    case LOADER_TYPES.ROTATE:
      return `case .rotate(let elementCount, let elementSize, let useGradient):
            RotateLoader(size: size, elementCount: elementCount, elementSize: elementSize, primaryColor: primaryColor, secondaryColor: secondaryColor, speed: speed, useGradient: useGradient)`;
    case LOADER_TYPES.FADE:
      return `case .fade(let elementCount, let elementSize, let useGradient):
            FadeLoader(size: size, elementCount: elementCount, elementSize: elementSize, primaryColor: primaryColor, secondaryColor: secondaryColor, speed: speed, useGradient: useGradient)`;
    case LOADER_TYPES.SCALE:
      return `case .scale(let elementCount, let minScale, let maxScale, let useGradient):
            ScaleLoader(size: size, elementCount: elementCount, minScale: minScale, maxScale: maxScale, primaryColor: primaryColor, secondaryColor: secondaryColor, speed: speed, useGradient: useGradient)`;
    case LOADER_TYPES.RIPPLE:
      return `case .ripple(let rippleCount, let rippleWidth, let useGradient):
            RippleLoader(size: size, rippleCount: rippleCount, rippleWidth: rippleWidth, primaryColor: primaryColor, secondaryColor: secondaryColor, speed: speed, useGradient: useGradient)`;
    case LOADER_TYPES.ORBIT:
      return `case .orbit(let orbitCount, let orbitSize, let orbitRadius, let useGradient):
            OrbitLoader(size: size, orbitCount: orbitCount, orbitSize: orbitSize, orbitRadius: orbitRadius, primaryColor: primaryColor, secondaryColor: secondaryColor, speed: speed, useGradient: useGradient)`;
    case LOADER_TYPES.GRID:
      return `case .grid(let gridRows, let gridCols, let cellSize, let useGradient):
            GridLoader(size: size, gridRows: gridRows, gridCols: gridCols, cellSize: cellSize, primaryColor: primaryColor, secondaryColor: secondaryColor, speed: speed, useGradient: useGradient)`;
    case LOADER_TYPES.LINES:
      return `case .lines(let lineCount, let lineWidth, let lineLength, let useGradient):
            LinesLoader(size: size, lineCount: lineCount, lineWidth: lineWidth, lineLength: lineLength, primaryColor: primaryColor, secondaryColor: secondaryColor, speed: speed, useGradient: useGradient)`;
    case LOADER_TYPES.SHIMMER:
      return `case .shimmer(let width, let height, let shimmerColor):
            ShimmerLoader(width: width, height: height, primaryColor: primaryColor, shimmerColor: shimmerColor, speed: speed)`;
    case LOADER_TYPES.STAGGER:
      return `case .stagger(let elementCount, let elementSize, let staggerDelay, let useGradient):
            StaggerLoader(size: size, elementCount: elementCount, elementSize: elementSize, staggerDelay: staggerDelay, primaryColor: primaryColor, secondaryColor: secondaryColor, speed: speed, useGradient: useGradient)`;
    case LOADER_TYPES.MORPH:
      return `case .morph(let shapeCount, let useGradient):
            MorphLoader(size: size, shapeCount: shapeCount, primaryColor: primaryColor, secondaryColor: secondaryColor, speed: speed, useGradient: useGradient)`;
    case LOADER_TYPES.PARTICLE:
      return `case .particle(let particleCount, let particleSize, let useGradient):
            ParticleLoader(size: size, particleCount: particleCount, particleSize: particleSize, primaryColor: primaryColor, secondaryColor: secondaryColor, speed: speed, useGradient: useGradient)`;
    default:
      return '';
  }
};

// eslint-disable-next-line no-unused-vars
const generateLoaderStyleEnumCases = (config) => {
  // Generate enum cases for all loader types
  return `
    case bars(barCount: Int, barWidth: CGFloat, barHeight: CGFloat, barSpacing: CGFloat, useGradient: Bool)
    case wave(waveCount: Int, waveHeight: CGFloat, waveWidth: CGFloat, useGradient: Bool)
    case spinner(armCount: Int, armLength: CGFloat, useGradient: Bool)
    case progressRing(progress: Double, strokeWidth: CGFloat)
    case skeleton(lineCount: Int, lineHeight: CGFloat, lineSpacing: CGFloat)
    case bounce(dotCount: Int, dotSize: CGFloat, bounceHeight: CGFloat, useGradient: Bool)
    case rotate(elementCount: Int, elementSize: CGFloat, useGradient: Bool)
    case fade(elementCount: Int, elementSize: CGFloat, useGradient: Bool)
    case scale(elementCount: Int, minScale: Double, maxScale: Double, useGradient: Bool)
    case ripple(rippleCount: Int, rippleWidth: CGFloat, useGradient: Bool)
    case orbit(orbitCount: Int, orbitSize: CGFloat, orbitRadius: CGFloat, useGradient: Bool)
    case grid(gridRows: Int, gridCols: Int, cellSize: CGFloat, useGradient: Bool)
    case lines(lineCount: Int, lineWidth: CGFloat, lineLength: CGFloat, useGradient: Bool)
    case shimmer(width: CGFloat, height: CGFloat, shimmerColor: Color)
    case stagger(elementCount: Int, elementSize: CGFloat, staggerDelay: Double, useGradient: Bool)
    case morph(shapeCount: Int, useGradient: Bool)
    case particle(particleCount: Int, particleSize: CGFloat, useGradient: Bool)`;
};

const generateSpecificLoaderComponent = (style, config) => {
  switch (style) {
    case LOADER_TYPES.RING:
      return generateRingLoader(config);
    case LOADER_TYPES.PULSE:
      return generatePulseLoader(config);
    case LOADER_TYPES.DOTS:
      return generateDotsLoader(config);
    case LOADER_TYPES.BARS:
      return generateBarsLoader(config);
    case LOADER_TYPES.WAVE:
      return generateWaveLoader(config);
    case LOADER_TYPES.SPINNER:
      return generateSpinnerLoader(config);
    case LOADER_TYPES.PROGRESS_RING:
      return generateProgressRingLoader(config);
    case LOADER_TYPES.SKELETON:
      return generateSkeletonLoader(config);
    case LOADER_TYPES.BOUNCE:
      return generateBounceLoader(config);
    case LOADER_TYPES.ROTATE:
      return generateRotateLoader(config);
    case LOADER_TYPES.FADE:
      return generateFadeLoader(config);
    case LOADER_TYPES.SCALE:
      return generateScaleLoader(config);
    case LOADER_TYPES.RIPPLE:
      return generateRippleLoader(config);
    case LOADER_TYPES.ORBIT:
      return generateOrbitLoader(config);
    case LOADER_TYPES.GRID:
      return generateGridLoader(config);
    case LOADER_TYPES.LINES:
      return generateLinesLoader(config);
    case LOADER_TYPES.SHIMMER:
      return generateShimmerLoader(config);
    case LOADER_TYPES.STAGGER:
      return generateStaggerLoader(config);
    case LOADER_TYPES.MORPH:
      return generateMorphLoader(config);
    case LOADER_TYPES.PARTICLE:
      return generateParticleLoader(config);
    default:
      return generateRingLoader(config);
  }
};

const generateLoaderSpecificModifier = (style, config) => {
  const { showBackground, backgroundOpacity } = config;
  
  switch (style) {
    case LOADER_TYPES.RING:
      return generateRingLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.PULSE:
      return generatePulseLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.DOTS:
      return generateDotsLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.BARS:
      return generateBarsLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.WAVE:
      return generateWaveLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.SPINNER:
      return generateSpinnerLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.PROGRESS_RING:
      return generateProgressRingLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.SKELETON:
      return generateSkeletonLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.BOUNCE:
      return generateBounceLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.ROTATE:
      return generateRotateLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.FADE:
      return generateFadeLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.SCALE:
      return generateScaleLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.RIPPLE:
      return generateRippleLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.ORBIT:
      return generateOrbitLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.GRID:
      return generateGridLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.LINES:
      return generateLinesLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.SHIMMER:
      return generateShimmerLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.STAGGER:
      return generateStaggerLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.MORPH:
      return generateMorphLoaderModifier(config, showBackground, backgroundOpacity);
    case LOADER_TYPES.PARTICLE:
      return generateParticleLoaderModifier(config, showBackground, backgroundOpacity);
    default:
      return generateRingLoaderModifier(config, showBackground, backgroundOpacity);
  }
};

const generateRingLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.RING, config);
  return generateModifierCode('Ring', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.RING);
};

const generatePulseLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.PULSE, config);
  return generateModifierCode('Pulse', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.PULSE);
};

const generateDotsLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.DOTS, config);
  return generateModifierCode('Dots', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.DOTS);
};

const generateBarsLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.BARS, config);
  return generateModifierCode('Bars', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.BARS);
};

const generateWaveLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.WAVE, config);
  return generateModifierCode('Wave', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.WAVE);
};

const generateSpinnerLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.SPINNER, config);
  return generateModifierCode('Spinner', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.SPINNER);
};

const generateProgressRingLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.PROGRESS_RING, config);
  return generateModifierCode('ProgressRing', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.PROGRESS_RING);
};

const generateSkeletonLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.SKELETON, config);
  return generateModifierCode('Skeleton', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.SKELETON);
};

const generateBounceLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.BOUNCE, config);
  return generateModifierCode('Bounce', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.BOUNCE);
};

const generateRotateLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.ROTATE, config);
  return generateModifierCode('Rotate', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.ROTATE);
};

const generateFadeLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.FADE, config);
  return generateModifierCode('Fade', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.FADE);
};

const generateScaleLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.SCALE, config);
  return generateModifierCode('Scale', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.SCALE);
};

const generateRippleLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.RIPPLE, config);
  return generateModifierCode('Ripple', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.RIPPLE);
};

const generateOrbitLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.ORBIT, config);
  return generateModifierCode('Orbit', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.ORBIT);
};

const generateGridLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.GRID, config);
  return generateModifierCode('Grid', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.GRID);
};

const generateLinesLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.LINES, config);
  return generateModifierCode('Lines', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.LINES);
};

const generateShimmerLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.SHIMMER, config);
  return generateModifierCode('Shimmer', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.SHIMMER);
};

const generateStaggerLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.STAGGER, config);
  return generateModifierCode('Stagger', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.STAGGER);
};

const generateMorphLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.MORPH, config);
  return generateModifierCode('Morph', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.MORPH);
};

const generateParticleLoaderModifier = (config, showBackground, backgroundOpacity) => {
  const loaderCall = getDirectLoaderUsage(LOADER_TYPES.PARTICLE, config);
  return generateModifierCode('Particle', loaderCall, config, showBackground, backgroundOpacity, LOADER_TYPES.PARTICLE);
};

const generateModifierCode = (loaderName, loaderCall, config, showBackground, backgroundOpacity, style) => {
  const modifierName = `${loaderName}LoaderModifier`;
  // Use the proper method name from getLoaderMethodName for correct camelCase
  const methodName = getLoaderMethodName(style || LOADER_TYPES.RING);
  
  const backgroundCode = showBackground ? `Color.black.opacity(${backgroundOpacity})\n                    .ignoresSafeArea()\n                ` : '';
  
  return `
// MARK: - ${loaderName} Loader Modifier
struct ${modifierName}: ViewModifier {
    @Binding var isLoading: Bool
    
    func body(content: Content) -> some View {
        ZStack {
            content
            
            if isLoading {
                ${backgroundCode}${loaderCall}
            }
        }
    }
}

extension View {
    func ${methodName}(isLoading: Binding<Bool>) -> some View {
        self.modifier(${modifierName}(isLoading: isLoading))
    }
}`;
};

const generateRingLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size = 50, primaryColor = '#F26F29', secondaryColor = '#F03224', speed = 1.5, strokeWidth = 4 } = config;
  return `
// MARK: - Ring Loader
struct RingLoader: View {
    @State var size: CGFloat = ${size}
    @State var primaryColor: Color = Color(hex: "${primaryColor}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor}")
    @State var speed: Double = ${speed}
    @State var strokeWidth: CGFloat = ${strokeWidth}
    
    @State private var rotation: Double = 0
    
    var body: some View {
        Circle()
            .trim(from: 0, to: 0.7)
            .stroke(
                LinearGradient(
                    colors: [primaryColor, secondaryColor],
                    startPoint: .topLeading,
                    endPoint: .bottomTrailing
                ),
                style: StrokeStyle(lineWidth: strokeWidth, lineCap: .round)
            )
            .frame(width: size, height: size)
            .rotationEffect(.degrees(rotation))
            .onAppear {
                withAnimation(.linear(duration: speed).repeatForever(autoreverses: false)) {
                    rotation = 360
                }
            }
    }
}`;
};

const generatePulseLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size = 50, minSize = 30, maxSize = 50, primaryColor = '#F26F29', secondaryColor = '#F03224', speed = 1.5, useGradient = false } = config;
  return `
// MARK: - Pulse Loader
struct PulseLoader: View {
    @State var size: CGFloat = ${size}
    @State var minSize: CGFloat = ${minSize}
    @State var maxSize: CGFloat = ${maxSize}
    @State var primaryColor: Color = Color(hex: "${primaryColor}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor}")
    @State var speed: Double = ${speed}
    @State var useGradient: Bool = ${useGradient}
    
    @State private var scale: CGFloat = ${minSize / size}
    @State private var opacity: Double = 0.7
    
    var body: some View {
        Circle()
            .fill(useGradient ? 
                LinearGradient(colors: [primaryColor, secondaryColor], startPoint: .topLeading, endPoint: .bottomTrailing) :
                LinearGradient(colors: [primaryColor], startPoint: .top, endPoint: .bottom)
            )
            .frame(width: size, height: size)
            .scaleEffect(scale)
            .opacity(opacity)
            .onAppear {
                withAnimation(.easeInOut(duration: speed).repeatForever(autoreverses: true)) {
                    scale = ${maxSize / size}
                    opacity = 1.0
                }
            }
    }
}`;
};

const generateDotsLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size = 50, dotCount = 3, minDotSize = 8, maxDotSize = 20, primaryColor = '#0ea5e9', secondaryColor = '#7dd3fc', speed = 1.5, useGradient = false } = config;
  return `
// MARK: - Dots Loader
struct DotsLoader: View {
    @State var size: CGFloat = ${size}
    @State var dotCount: Int = ${dotCount}
    @State var minDotSize: CGFloat = ${minDotSize}
    @State var maxDotSize: CGFloat = ${maxDotSize}
    @State var primaryColor: Color = Color(hex: "${primaryColor}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor}")
    @State var speed: Double = ${speed}
    @State var useGradient: Bool = ${useGradient}
    
    var body: some View {
        HStack(spacing: 8) {
            ForEach(0..<dotCount, id: \\.self) { index in
                DotView(
                    minSize: minDotSize,
                    maxSize: maxDotSize,
                    primaryColor: primaryColor,
                    secondaryColor: secondaryColor,
                    speed: speed,
                    useGradient: useGradient,
                    delay: Double(index) * (speed / Double(dotCount))
                )
            }
        }
    }
}

struct DotView: View {
    let minSize: CGFloat
    let maxSize: CGFloat
    let primaryColor: Color
    let secondaryColor: Color
    let speed: Double
    let useGradient: Bool
    let delay: Double
    
    @State private var scale: CGFloat = 1.0
    
    var body: some View {
        Circle()
            .fill(useGradient ? 
                LinearGradient(colors: [primaryColor, secondaryColor], startPoint: .topLeading, endPoint: .bottomTrailing) :
                LinearGradient(colors: [primaryColor], startPoint: .top, endPoint: .bottom)
            )
            .frame(width: minSize, height: minSize)
            .scaleEffect(scale)
            .onAppear {
                DispatchQueue.main.asyncAfter(deadline: .now() + delay) {
                    withAnimation(.easeInOut(duration: speed).repeatForever(autoreverses: true)) {
                        scale = maxSize / minSize
                    }
                }
            }
    }
}`;
};

const generateBarsLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size, barCount = 5, barWidth = 4, barHeight = 30, barSpacing = 4, primaryColor, secondaryColor, speed, useGradient = false } = config;
  return `
// MARK: - Bars Loader
struct BarsLoader: View {
    @State var size: CGFloat = ${size || 50}
    @State var barCount: Int = ${barCount || 5}
    @State var barWidth: CGFloat = ${barWidth || 4}
    @State var barHeight: CGFloat = ${barHeight || 30}
    @State var barSpacing: CGFloat = ${barSpacing || 4}
    @State var primaryColor: Color = Color(hex: "${primaryColor || '#F26F29'}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor || '#F03224'}")
    @State var speed: Double = ${speed || 1.0}
    @State var useGradient: Bool = ${useGradient || false}
    
    var body: some View {
        HStack(spacing: barSpacing) {
            ForEach(0..<barCount, id: \\.self) { index in
                BarView(
                    width: barWidth,
                    height: barHeight,
                    primaryColor: primaryColor,
                    secondaryColor: secondaryColor,
                    speed: speed,
                    useGradient: useGradient,
                    delay: Double(index) * (speed / Double(barCount))
                )
            }
        }
    }
}

struct BarView: View {
    let width: CGFloat
    let height: CGFloat
    let primaryColor: Color
    let secondaryColor: Color
    let speed: Double
    let useGradient: Bool
    let delay: Double
    
    @State private var scale: CGFloat = 0.3
    @State private var opacity: Double = 0.5
    
    var body: some View {
        RoundedRectangle(cornerRadius: width / 2)
            .fill(useGradient ? 
                LinearGradient(colors: [primaryColor, secondaryColor], startPoint: .top, endPoint: .bottom) :
                LinearGradient(colors: [primaryColor], startPoint: .top, endPoint: .bottom)
            )
            .frame(width: width, height: height)
            .scaleEffect(y: scale)
            .opacity(opacity)
            .onAppear {
                DispatchQueue.main.asyncAfter(deadline: .now() + delay) {
                    withAnimation(.easeInOut(duration: speed).repeatForever(autoreverses: true)) {
                        scale = 1.0
                        opacity = 1.0
                    }
                }
            }
    }
}`;
};

const generateWaveLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size, waveCount = 4, waveHeight = 20, waveWidth = 6, primaryColor, secondaryColor, speed, useGradient = false } = config;
  return `
// MARK: - Wave Loader
struct WaveLoader: View {
    @State var size: CGFloat = ${size || 50}
    @State var waveCount: Int = ${waveCount || 4}
    @State var waveHeight: CGFloat = ${waveHeight || 20}
    @State var waveWidth: CGFloat = ${waveWidth || 6}
    @State var primaryColor: Color = Color(hex: "${primaryColor || '#F26F29'}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor || '#F03224'}")
    @State var speed: Double = ${speed || 1.0}
    @State var useGradient: Bool = ${useGradient || false}
    
    var body: some View {
        HStack(spacing: 4) {
            ForEach(0..<waveCount, id: \\.self) { index in
                WaveView(
                    width: waveWidth,
                    height: waveHeight,
                    primaryColor: primaryColor,
                    secondaryColor: secondaryColor,
                    speed: speed,
                    useGradient: useGradient,
                    delay: Double(index) * (speed / Double(waveCount))
                )
            }
        }
    }
}

struct WaveView: View {
    let width: CGFloat
    let height: CGFloat
    let primaryColor: Color
    let secondaryColor: Color
    let speed: Double
    let useGradient: Bool
    let delay: Double
    
    @State private var scale: CGFloat = 0.3
    
    var body: some View {
        RoundedRectangle(cornerRadius: width / 2)
            .fill(useGradient ? 
                LinearGradient(colors: [primaryColor, secondaryColor], startPoint: .top, endPoint: .bottom) :
                LinearGradient(colors: [primaryColor], startPoint: .top, endPoint: .bottom)
            )
            .frame(width: width, height: height)
            .scaleEffect(y: scale)
            .onAppear {
                DispatchQueue.main.asyncAfter(deadline: .now() + delay) {
                    withAnimation(.easeInOut(duration: speed).repeatForever(autoreverses: true)) {
                        scale = 1.0
                    }
                }
            }
    }
}`;
};

const generateSpinnerLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size, armCount = 8, armLength = 20, primaryColor, secondaryColor, speed, useGradient = false } = config;
  return `
// MARK: - Spinner Loader
struct SpinnerLoader: View {
    @State var size: CGFloat = ${size || 50}
    @State var armCount: Int = ${armCount || 8}
    @State var armLength: CGFloat = ${armLength || 20}
    @State var primaryColor: Color = Color(hex: "${primaryColor || '#F26F29'}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor || '#F03224'}")
    @State var speed: Double = ${speed || 1.0}
    @State var useGradient: Bool = ${useGradient || false}
    
    @State private var rotation: Double = 0
    
    var body: some View {
        ZStack {
            ForEach(0..<armCount, id: \\.self) { index in
                let angle = Double(index) * (360.0 / Double(armCount))
                let opacity = 0.3 + (Double(index) / Double(armCount)) * 0.7
                
                Capsule()
                    .fill(useGradient ? 
                        LinearGradient(colors: [primaryColor, secondaryColor], startPoint: .leading, endPoint: .trailing) :
                        LinearGradient(colors: [primaryColor], startPoint: .leading, endPoint: .trailing)
                    )
                    .frame(width: 3, height: armLength)
                    .offset(y: -size / 3)
                    .rotationEffect(.degrees(angle))
                    .opacity(opacity)
            }
        }
        .frame(width: size, height: size)
        .rotationEffect(.degrees(rotation))
        .onAppear {
            withAnimation(.linear(duration: speed).repeatForever(autoreverses: false)) {
                rotation = 360
            }
        }
    }
}`;
};

const generateProgressRingLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size, progress = 0.7, strokeWidth = 4, primaryColor, secondaryColor, speed } = config;
  return `
// MARK: - Progress Ring Loader
struct ProgressRingLoader: View {
    @State var size: CGFloat = ${size || 50}
    @State var progress: Double = ${progress || 0.7}
    @State var strokeWidth: CGFloat = ${strokeWidth || 4}
    @State var primaryColor: Color = Color(hex: "${primaryColor || '#F26F29'}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor || '#F03224'}")
    @State var speed: Double = ${speed || 1.0}
    
    private var progressAnimation: Double {
        progress
    }
    
    var body: some View {
        ZStack {
            Circle()
                .stroke(secondaryColor.opacity(0.2), lineWidth: strokeWidth)
            
            Circle()
                .trim(from: 0, to: progressAnimation)
                .stroke(
                    LinearGradient(
                        colors: [primaryColor, secondaryColor],
                        startPoint: .topLeading,
                        endPoint: .bottomTrailing
                    ),
                    style: StrokeStyle(lineWidth: strokeWidth, lineCap: .round)
                )
                .rotationEffect(.degrees(-90))
                .animation(.easeInOut(duration: speed), value: progressAnimation)
        }
        .frame(width: size, height: size)
    }
}`;
};

const generateSkeletonLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size, skeletonLineCount = 3, skeletonLineHeight = 12, skeletonLineSpacing = 8, primaryColor, speed } = config;
  return `
// MARK: - Skeleton Loader
struct SkeletonLoader: View {
    @State var size: CGFloat = ${size || 50}
    @State var lineCount: Int = ${skeletonLineCount || 3}
    @State var lineHeight: CGFloat = ${skeletonLineHeight || 12}
    @State var lineSpacing: CGFloat = ${skeletonLineSpacing || 8}
    @State var primaryColor: Color = Color(hex: "${primaryColor || '#e5e7eb'}")
    @State var speed: Double = ${speed || 1.5}
    
    var body: some View {
        VStack(alignment: .leading, spacing: lineSpacing) {
            ForEach(0..<lineCount, id: \\.self) { index in
                SkeletonLineView(
                    width: index == lineCount - 1 ? size * 0.6 : size,
                    height: lineHeight,
                    primaryColor: primaryColor,
                    speed: speed,
                    delay: Double(index) * 0.1
                )
            }
        }
        .frame(width: size)
    }
}

struct SkeletonLineView: View {
    let width: CGFloat
    let height: CGFloat
    let primaryColor: Color
    let speed: Double
    let delay: Double
    
    @State private var opacity: Double = 0.3
    
    var body: some View {
        RoundedRectangle(cornerRadius: 4)
            .fill(primaryColor)
            .frame(width: width, height: height)
            .opacity(opacity)
            .onAppear {
                DispatchQueue.main.asyncAfter(deadline: .now() + delay) {
                    withAnimation(.easeInOut(duration: speed).repeatForever(autoreverses: true)) {
                        opacity = 0.8
                    }
                }
            }
    }
}`;
};

const generateBounceLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size, dotCount = 3, dotSize = 12, bounceHeight = 20, primaryColor, secondaryColor, speed, useGradient = false } = config;
  return `
// MARK: - Bounce Loader
struct BounceLoader: View {
    @State var size: CGFloat = ${size || 50}
    @State var dotCount: Int = ${dotCount || 3}
    @State var dotSize: CGFloat = ${dotSize || 12}
    @State var bounceHeight: CGFloat = ${bounceHeight || 20}
    @State var primaryColor: Color = Color(hex: "${primaryColor || '#F26F29'}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor || '#F03224'}")
    @State var speed: Double = ${speed || 1.0}
    @State var useGradient: Bool = ${useGradient || false}
    
    var body: some View {
        HStack(spacing: 8) {
            ForEach(0..<dotCount, id: \\.self) { index in
                BounceDotView(
                    size: dotSize,
                    bounceHeight: bounceHeight,
                    primaryColor: primaryColor,
                    secondaryColor: secondaryColor,
                    speed: speed,
                    useGradient: useGradient,
                    delay: Double(index) * (speed / Double(dotCount))
                )
            }
        }
    }
}

struct BounceDotView: View {
    let size: CGFloat
    let bounceHeight: CGFloat
    let primaryColor: Color
    let secondaryColor: Color
    let speed: Double
    let useGradient: Bool
    let delay: Double
    
    @State private var offset: CGFloat = 0
    
    var body: some View {
        Circle()
            .fill(useGradient ? 
                LinearGradient(colors: [primaryColor, secondaryColor], startPoint: .topLeading, endPoint: .bottomTrailing) :
                LinearGradient(colors: [primaryColor], startPoint: .top, endPoint: .bottom)
            )
            .frame(width: size, height: size)
            .offset(y: offset)
            .onAppear {
                DispatchQueue.main.asyncAfter(deadline: .now() + delay) {
                    withAnimation(.easeInOut(duration: speed).repeatForever(autoreverses: true)) {
                        offset = -bounceHeight
                    }
                }
            }
    }
}`;
};

const generateRotateLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size, elementCount = 6, elementSize = 8, primaryColor, secondaryColor, speed, useGradient = false } = config;
  return `
// MARK: - Rotate Loader
struct RotateLoader: View {
    @State var size: CGFloat = ${size || 50}
    @State var elementCount: Int = ${elementCount || 6}
    @State var elementSize: CGFloat = ${elementSize || 8}
    @State var primaryColor: Color = Color(hex: "${primaryColor || '#F26F29'}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor || '#F03224'}")
    @State var speed: Double = ${speed || 1.0}
    @State var useGradient: Bool = ${useGradient || false}
    
    @State private var rotation: Double = 0
    
    var body: some View {
        ZStack {
            ForEach(0..<elementCount, id: \\.self) { index in
                let angle = Double(index) * (360.0 / Double(elementCount))
                let radius = size / 3
                
                Circle()
                    .fill(useGradient ? 
                        LinearGradient(colors: [primaryColor, secondaryColor], startPoint: .topLeading, endPoint: .bottomTrailing) :
                        LinearGradient(colors: [primaryColor], startPoint: .top, endPoint: .bottom)
                    )
                    .frame(width: elementSize, height: elementSize)
                    .offset(x: radius * cos(angle * .pi / 180), y: radius * sin(angle * .pi / 180))
                    .opacity(0.3 + (Double(index) / Double(elementCount)) * 0.7)
            }
        }
        .frame(width: size, height: size)
        .rotationEffect(.degrees(rotation))
        .onAppear {
            withAnimation(.linear(duration: speed).repeatForever(autoreverses: false)) {
                rotation = 360
            }
        }
    }
}`;
};

const generateFadeLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size, elementCount = 4, elementSize = 12, primaryColor, secondaryColor, speed, useGradient = false } = config;
  return `
// MARK: - Fade Loader
struct FadeLoader: View {
    @State var size: CGFloat = ${size || 50}
    @State var elementCount: Int = ${elementCount || 4}
    @State var elementSize: CGFloat = ${elementSize || 12}
    @State var primaryColor: Color = Color(hex: "${primaryColor || '#F26F29'}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor || '#F03224'}")
    @State var speed: Double = ${speed || 1.0}
    @State var useGradient: Bool = ${useGradient || false}
    
    var body: some View {
        HStack(spacing: 8) {
            ForEach(0..<elementCount, id: \\.self) { index in
                FadeDotView(
                    size: elementSize,
                    primaryColor: primaryColor,
                    secondaryColor: secondaryColor,
                    speed: speed,
                    useGradient: useGradient,
                    delay: Double(index) * (speed / Double(elementCount))
                )
            }
        }
    }
}

struct FadeDotView: View {
    let size: CGFloat
    let primaryColor: Color
    let secondaryColor: Color
    let speed: Double
    let useGradient: Bool
    let delay: Double
    
    @State private var opacity: Double = 0.2
    
    var body: some View {
        Circle()
            .fill(useGradient ? 
                LinearGradient(colors: [primaryColor, secondaryColor], startPoint: .topLeading, endPoint: .bottomTrailing) :
                LinearGradient(colors: [primaryColor], startPoint: .top, endPoint: .bottom)
            )
            .frame(width: size, height: size)
            .opacity(opacity)
            .onAppear {
                DispatchQueue.main.asyncAfter(deadline: .now() + delay) {
                    withAnimation(.easeInOut(duration: speed).repeatForever(autoreverses: true)) {
                        opacity = 1.0
                    }
                }
            }
    }
}`;
};

const generateScaleLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size, elementCount = 3, minScale = 0.5, maxScale = 1.0, primaryColor, secondaryColor, speed, useGradient = false } = config;
  return `
// MARK: - Scale Loader
struct ScaleLoader: View {
    @State var size: CGFloat = ${size || 50}
    @State var elementCount: Int = ${elementCount || 3}
    @State var minScale: Double = ${minScale || 0.5}
    @State var maxScale: Double = ${maxScale || 1.0}
    @State var primaryColor: Color = Color(hex: "${primaryColor || '#F26F29'}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor || '#F03224'}")
    @State var speed: Double = ${speed || 1.0}
    @State var useGradient: Bool = ${useGradient || false}
    
    var body: some View {
        HStack(spacing: 8) {
            ForEach(0..<elementCount, id: \\.self) { index in
                ScaleDotView(
                    size: size / CGFloat(elementCount),
                    minScale: minScale,
                    maxScale: maxScale,
                    primaryColor: primaryColor,
                    secondaryColor: secondaryColor,
                    speed: speed,
                    useGradient: useGradient,
                    delay: Double(index) * (speed / Double(elementCount))
                )
            }
        }
    }
}

struct ScaleDotView: View {
    let size: CGFloat
    let minScale: Double
    let maxScale: Double
    let primaryColor: Color
    let secondaryColor: Color
    let speed: Double
    let useGradient: Bool
    let delay: Double
    
    @State private var scale: CGFloat = 0.5
    
    var body: some View {
        Circle()
            .fill(useGradient ? 
                LinearGradient(colors: [primaryColor, secondaryColor], startPoint: .topLeading, endPoint: .bottomTrailing) :
                LinearGradient(colors: [primaryColor], startPoint: .top, endPoint: .bottom)
            )
            .frame(width: size, height: size)
            .scaleEffect(scale)
            .onAppear {
                scale = CGFloat(minScale)
                DispatchQueue.main.asyncAfter(deadline: .now() + delay) {
                    withAnimation(.easeInOut(duration: speed).repeatForever(autoreverses: true)) {
                        scale = CGFloat(maxScale)
                    }
                }
            }
    }
}`;
};

const generateRippleLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size, rippleCount = 3, rippleWidth = 2, primaryColor, secondaryColor, speed, useGradient = false } = config;
  return `
// MARK: - Ripple Loader
struct RippleLoader: View {
    @State var size: CGFloat = ${size || 50}
    @State var rippleCount: Int = ${rippleCount || 3}
    @State var rippleWidth: CGFloat = ${rippleWidth || 2}
    @State var primaryColor: Color = Color(hex: "${primaryColor || '#F26F29'}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor || '#F03224'}")
    @State var speed: Double = ${speed || 1.5}
    @State var useGradient: Bool = ${useGradient || false}
    
    var body: some View {
        ZStack {
            ForEach(0..<rippleCount, id: \\.self) { index in
                RippleView(
                    size: size,
                    rippleWidth: rippleWidth,
                    primaryColor: primaryColor,
                    secondaryColor: secondaryColor,
                    speed: speed,
                    useGradient: useGradient,
                    delay: Double(index) * (speed / Double(rippleCount))
                )
            }
        }
        .frame(width: size, height: size)
    }
}

struct RippleView: View {
    let size: CGFloat
    let rippleWidth: CGFloat
    let primaryColor: Color
    let secondaryColor: Color
    let speed: Double
    let useGradient: Bool
    let delay: Double
    
    @State private var scale: CGFloat = 0.8
    @State private var opacity: Double = 0.8
    
    var body: some View {
        Circle()
            .stroke(useGradient ? 
                LinearGradient(colors: [primaryColor, secondaryColor], startPoint: .topLeading, endPoint: .bottomTrailing) :
                LinearGradient(colors: [primaryColor], startPoint: .top, endPoint: .bottom),
                lineWidth: rippleWidth
            )
            .frame(width: size * 0.3, height: size * 0.3)
            .scaleEffect(scale)
            .opacity(opacity)
            .onAppear {
                DispatchQueue.main.asyncAfter(deadline: .now() + delay) {
                    withAnimation(.easeOut(duration: speed).repeatForever(autoreverses: false)) {
                        scale = 2.0
                        opacity = 0
                    }
                }
            }
    }
}`;
};

const generateOrbitLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size, orbitCount = 3, orbitSize = 8, orbitRadius = 20, primaryColor, secondaryColor, speed, useGradient = false } = config;
  return `
// MARK: - Orbit Loader
struct OrbitLoader: View {
    @State var size: CGFloat = ${size || 50}
    @State var orbitCount: Int = ${orbitCount || 3}
    @State var orbitSize: CGFloat = ${orbitSize || 8}
    @State var orbitRadius: CGFloat = ${orbitRadius || 20}
    @State var primaryColor: Color = Color(hex: "${primaryColor || '#F26F29'}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor || '#F03224'}")
    @State var speed: Double = ${speed || 1.0}
    @State var useGradient: Bool = ${useGradient || false}
    
    @State private var rotation: Double = 0
    
    var body: some View {
        ZStack {
            ForEach(0..<orbitCount, id: \\.self) { index in
                let angle = Double(index) * (360.0 / Double(orbitCount))
                
                Circle()
                    .fill(useGradient ? 
                        LinearGradient(colors: [primaryColor, secondaryColor], startPoint: .topLeading, endPoint: .bottomTrailing) :
                        LinearGradient(colors: [primaryColor], startPoint: .top, endPoint: .bottom)
                    )
                    .frame(width: orbitSize, height: orbitSize)
                    .offset(x: orbitRadius * cos(angle * .pi / 180), y: orbitRadius * sin(angle * .pi / 180))
            }
        }
        .frame(width: size, height: size)
        .rotationEffect(.degrees(rotation))
        .onAppear {
            withAnimation(.linear(duration: speed).repeatForever(autoreverses: false)) {
                rotation = 360
            }
        }
    }
}`;
};

const generateGridLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size, gridRows = 3, gridCols = 3, cellSize = 10, primaryColor, secondaryColor, speed, useGradient = false } = config;
  return `
// MARK: - Grid Loader
struct GridLoader: View {
    @State var size: CGFloat = ${size || 50}
    @State var gridRows: Int = ${gridRows || 3}
    @State var gridCols: Int = ${gridCols || 3}
    @State var cellSize: CGFloat = ${cellSize || 10}
    @State var primaryColor: Color = Color(hex: "${primaryColor || '#F26F29'}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor || '#F03224'}")
    @State var speed: Double = ${speed || 1.0}
    @State var useGradient: Bool = ${useGradient || false}
    
    var body: some View {
        let columns = Array(repeating: GridItem(.fixed(cellSize), spacing: 4), count: gridCols)
        
        LazyVGrid(columns: columns, spacing: 4) {
            ForEach(0..<(gridRows * gridCols), id: \\.self) { index in
                GridCellView(
                    size: cellSize,
                    primaryColor: primaryColor,
                    secondaryColor: secondaryColor,
                    speed: speed,
                    useGradient: useGradient,
                    delay: Double(index % (gridRows * gridCols)) * (speed / Double(gridRows * gridCols))
                )
            }
        }
    }
}

struct GridCellView: View {
    let size: CGFloat
    let primaryColor: Color
    let secondaryColor: Color
    let speed: Double
    let useGradient: Bool
    let delay: Double
    
    @State private var scale: CGFloat = 0.8
    @State private var opacity: Double = 0.3
    
    var body: some View {
        Rectangle()
            .fill(useGradient ? 
                LinearGradient(colors: [primaryColor, secondaryColor], startPoint: .topLeading, endPoint: .bottomTrailing) :
                LinearGradient(colors: [primaryColor], startPoint: .top, endPoint: .bottom)
            )
            .frame(width: size, height: size)
            .scaleEffect(scale)
            .opacity(opacity)
            .onAppear {
                DispatchQueue.main.asyncAfter(deadline: .now() + delay) {
                    withAnimation(.easeInOut(duration: speed).repeatForever(autoreverses: true)) {
                        scale = 1.0
                        opacity = 1.0
                    }
                }
            }
    }
}`;
};

const generateLinesLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size, lineCount = 5, lineWidth = 3, lineLength = 30, primaryColor, secondaryColor, speed, useGradient = false } = config;
  return `
// MARK: - Lines Loader
struct LinesLoader: View {
    @State var size: CGFloat = ${size || 50}
    @State var lineCount: Int = ${lineCount || 5}
    @State var lineWidth: CGFloat = ${lineWidth || 3}
    @State var lineLength: CGFloat = ${lineLength || 30}
    @State var primaryColor: Color = Color(hex: "${primaryColor || '#F26F29'}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor || '#F03224'}")
    @State var speed: Double = ${speed || 1.0}
    @State var useGradient: Bool = ${useGradient || false}
    
    var body: some View {
        VStack(spacing: 4) {
            ForEach(0..<lineCount, id: \\.self) { index in
                LineView(
                    length: lineLength,
                    width: lineWidth,
                    primaryColor: primaryColor,
                    secondaryColor: secondaryColor,
                    speed: speed,
                    useGradient: useGradient,
                    delay: Double(index) * (speed / Double(lineCount))
                )
            }
        }
    }
}

struct LineView: View {
    let length: CGFloat
    let width: CGFloat
    let primaryColor: Color
    let secondaryColor: Color
    let speed: Double
    let useGradient: Bool
    let delay: Double
    
    @State private var scale: CGFloat = 0.5
    @State private var opacity: Double = 0.3
    
    var body: some View {
        Capsule()
            .fill(useGradient ? 
                LinearGradient(colors: [primaryColor, secondaryColor], startPoint: .leading, endPoint: .trailing) :
                LinearGradient(colors: [primaryColor], startPoint: .leading, endPoint: .trailing)
            )
            .frame(width: length, height: width)
            .scaleEffect(x: scale)
            .opacity(opacity)
            .onAppear {
                DispatchQueue.main.asyncAfter(deadline: .now() + delay) {
                    withAnimation(.easeInOut(duration: speed).repeatForever(autoreverses: true)) {
                        scale = 1.0
                        opacity = 1.0
                    }
                }
            }
    }
}`;
};

const generateShimmerLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { width = 200, height = 20, primaryColor, shimmerColor = '#ffffff', speed } = config;
  return `
// MARK: - Shimmer Loader
struct ShimmerLoader: View {
    @State var width: CGFloat = ${width || 200}
    @State var height: CGFloat = ${height || 20}
    @State var primaryColor: Color = Color(hex: "${primaryColor || '#e5e7eb'}")
    @State var shimmerColor: Color = Color(hex: "${shimmerColor || '#ffffff'}")
    @State var speed: Double = ${speed || 1.5}
    
    @State private var offset: CGFloat = 0
    
    var body: some View {
        ZStack(alignment: .leading) {
            RoundedRectangle(cornerRadius: 4)
                .fill(primaryColor)
                .frame(width: width, height: height)
            
            RoundedRectangle(cornerRadius: 4)
                .fill(
                    LinearGradient(
                        colors: [Color.clear, shimmerColor.opacity(0.6), Color.clear],
                        startPoint: .leading,
                        endPoint: .trailing
                    )
                )
                .frame(width: width * 0.3, height: height)
                .offset(x: offset)
        }
        .clipped()
        .onAppear {
            offset = -width
            withAnimation(.linear(duration: speed).repeatForever(autoreverses: false)) {
                offset = width
            }
        }
    }
}`;
};

const generateStaggerLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size, elementCount = 5, elementSize = 10, staggerDelay = 0.1, primaryColor, secondaryColor, speed, useGradient = false } = config;
  return `
// MARK: - Stagger Loader
struct StaggerLoader: View {
    @State var size: CGFloat = ${size || 50}
    @State var elementCount: Int = ${elementCount || 5}
    @State var elementSize: CGFloat = ${elementSize || 10}
    @State var staggerDelay: Double = ${staggerDelay || 0.1}
    @State var primaryColor: Color = Color(hex: "${primaryColor || '#F26F29'}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor || '#F03224'}")
    @State var speed: Double = ${speed || 1.0}
    @State var useGradient: Bool = ${useGradient || false}
    
    var body: some View {
        HStack(spacing: 6) {
            ForEach(0..<elementCount, id: \\.self) { index in
                StaggerDotView(
                    size: elementSize,
                    primaryColor: primaryColor,
                    secondaryColor: secondaryColor,
                    speed: speed,
                    useGradient: useGradient,
                    delay: Double(index) * staggerDelay
                )
            }
        }
    }
}

struct StaggerDotView: View {
    let size: CGFloat
    let primaryColor: Color
    let secondaryColor: Color
    let speed: Double
    let useGradient: Bool
    let delay: Double
    
    @State private var offset: CGFloat = 0
    @State private var scale: CGFloat = 1.0
    @State private var opacity: Double = 0.6
    
    var body: some View {
        Circle()
            .fill(useGradient ? 
                LinearGradient(colors: [primaryColor, secondaryColor], startPoint: .topLeading, endPoint: .bottomTrailing) :
                LinearGradient(colors: [primaryColor], startPoint: .top, endPoint: .bottom)
            )
            .frame(width: size, height: size)
            .offset(y: offset)
            .scaleEffect(scale)
            .opacity(opacity)
            .onAppear {
                DispatchQueue.main.asyncAfter(deadline: .now() + delay) {
                    withAnimation(.easeInOut(duration: speed).repeatForever(autoreverses: true)) {
                        offset = -10
                        scale = 1.2
                        opacity = 1.0
                    }
                }
            }
    }
}`;
};

const generateMorphLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size, shapeCount = 4, primaryColor, secondaryColor, speed, useGradient = false } = config;
  return `
// MARK: - Morph Loader
struct MorphLoader: View {
    @State var size: CGFloat = ${size || 50}
    @State var shapeCount: Int = ${shapeCount || 4}
    @State var primaryColor: Color = Color(hex: "${primaryColor || '#F26F29'}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor || '#F03224'}")
    @State var speed: Double = ${speed || 2.0}
    @State var useGradient: Bool = ${useGradient || false}
    
    @State private var rotation: Double = 0
    
    var body: some View {
        ZStack {
            ForEach(0..<shapeCount, id: \\.self) { index in
                let angle = Double(index) * (360.0 / Double(shapeCount))
                let radius = size / 3
                let opacity = 0.3 + (Double(index) / Double(shapeCount)) * 0.7
                
                Circle()
                    .fill(useGradient ? 
                        LinearGradient(colors: [primaryColor, secondaryColor], startPoint: .topLeading, endPoint: .bottomTrailing) :
                        LinearGradient(colors: [primaryColor], startPoint: .top, endPoint: .bottom)
                    )
                    .frame(width: 12, height: 12)
                    .offset(
                        x: radius * cos(angle * .pi / 180),
                        y: radius * sin(angle * .pi / 180)
                    )
                    .opacity(opacity)
            }
        }
        .frame(width: size, height: size)
        .rotationEffect(.degrees(rotation))
        .onAppear {
            withAnimation(.linear(duration: speed).repeatForever(autoreverses: false)) {
                rotation = 360
            }
        }
    }
}`;
};

const generateParticleLoader = (config) => {
  // eslint-disable-next-line no-unused-vars
  const { size = 50, particleCount = 12, particleSize = 4, primaryColor = '#0ea5e9', secondaryColor = '#7dd3fc', speed = 1.5, useGradient = false } = config;
  return `
// MARK: - Particle Loader
struct ParticleLoader: View {
    @State var size: CGFloat = ${size}
    @State var particleCount: Int = ${particleCount}
    @State var particleSize: CGFloat = ${particleSize}
    @State var primaryColor: Color = Color(hex: "${primaryColor}")
    @State var secondaryColor: Color = Color(hex: "${secondaryColor}")
    @State var speed: Double = ${speed}
    @State var useGradient: Bool = ${useGradient}
    
    var body: some View {
        ZStack {
            ForEach(0..<particleCount, id: \\.self) { index in
                let angle = Double(index) * (360.0 / Double(particleCount))
                let radius = size / 3
                
                ParticleView(
                    size: particleSize,
                    primaryColor: primaryColor,
                    secondaryColor: secondaryColor,
                    speed: speed,
                    useGradient: useGradient,
                    delay: Double(index) * (speed / Double(particleCount)),
                    baseX: radius * cos(angle * .pi / 180),
                    baseY: radius * sin(angle * .pi / 180)
                )
            }
        }
        .frame(width: size, height: size)
    }
}

struct ParticleView: View {
    let size: CGFloat
    let primaryColor: Color
    let secondaryColor: Color
    let speed: Double
    let useGradient: Bool
    let delay: Double
    let baseX: CGFloat
    let baseY: CGFloat
    
    @State private var offsetX: CGFloat = 0
    @State private var offsetY: CGFloat = 0
    @State private var scale: CGFloat = 1.0
    @State private var opacity: Double = 0.6
    
    var body: some View {
        Circle()
            .fill(useGradient ? 
                LinearGradient(colors: [primaryColor, secondaryColor], startPoint: .topLeading, endPoint: .bottomTrailing) :
                LinearGradient(colors: [primaryColor], startPoint: .top, endPoint: .bottom)
            )
            .frame(width: size, height: size)
            .offset(x: baseX + offsetX, y: baseY + offsetY)
            .scaleEffect(scale)
            .opacity(opacity)
            .onAppear {
                DispatchQueue.main.asyncAfter(deadline: .now() + delay) {
                    withAnimation(.easeInOut(duration: speed).repeatForever(autoreverses: true)) {
                        offsetX = 5 * cos(45 * .pi / 180)
                        offsetY = 5 * sin(45 * .pi / 180)
                        scale = 1.3
                        opacity = 1.0
                    }
                }
            }
    }
}`;
};

const generateColorExtension = () => {
  return `
// MARK: - Color Extension
extension Color {
    init(hex: String) {
        let hex = hex.trimmingCharacters(in: CharacterSet.alphanumerics.inverted)
        var int: UInt64 = 0
        Scanner(string: hex).scanHexInt64(&int)
        let a, r, g, b: UInt64
        switch hex.count {
        case 3: // RGB (12-bit)
            (a, r, g, b) = (255, (int >> 8) * 17, (int >> 4 & 0xF) * 17, (int & 0xF) * 17)
        case 6: // RGB (24-bit)
            (a, r, g, b) = (255, int >> 16, int >> 8 & 0xFF, int & 0xFF)
        case 8: // ARGB (32-bit)
            (a, r, g, b) = (int >> 24, int >> 16 & 0xFF, int >> 8 & 0xFF, int & 0xFF)
        default:
            (a, r, g, b) = (255, 0, 0, 0)
        }
        self.init(
            .sRGB,
            red: Double(r) / 255,
            green: Double(g) / 255,
            blue: Double(b) / 255,
            opacity: Double(a) / 255
        )
    }
}`;
};

const generateUsageExample = (style, config) => {
  const methodName = getLoaderMethodName(style);
  
  return `
// MARK: - Usage Example
struct ContentView: View {
    @State private var isLoading = false
    
    var body: some View {
        VStack(spacing: 20) {
            Button("Load Data") {
                isLoading = true
                // Simulate async work
                DispatchQueue.main.asyncAfter(deadline: .now() + 2) {
                    isLoading = false
                }
            }
        }
        .${methodName}(isLoading: $isLoading)
    }
}`;
};

const getLoaderMethodName = (style) => {
  const nameMap = {
    [LOADER_TYPES.RING]: 'ringLoader',
    [LOADER_TYPES.PULSE]: 'pulseLoader',
    [LOADER_TYPES.DOTS]: 'dotsLoader',
    [LOADER_TYPES.BARS]: 'barsLoader',
    [LOADER_TYPES.WAVE]: 'waveLoader',
    [LOADER_TYPES.SPINNER]: 'spinnerLoader',
    [LOADER_TYPES.PROGRESS_RING]: 'progressRingLoader',
    [LOADER_TYPES.SKELETON]: 'skeletonLoader',
    [LOADER_TYPES.BOUNCE]: 'bounceLoader',
    [LOADER_TYPES.ROTATE]: 'rotateLoader',
    [LOADER_TYPES.FADE]: 'fadeLoader',
    [LOADER_TYPES.SCALE]: 'scaleLoader',
    [LOADER_TYPES.RIPPLE]: 'rippleLoader',
    [LOADER_TYPES.ORBIT]: 'orbitLoader',
    [LOADER_TYPES.GRID]: 'gridLoader',
    [LOADER_TYPES.LINES]: 'linesLoader',
    [LOADER_TYPES.SHIMMER]: 'shimmerLoader',
    [LOADER_TYPES.STAGGER]: 'staggerLoader',
    [LOADER_TYPES.MORPH]: 'morphLoader',
    [LOADER_TYPES.PARTICLE]: 'particleLoader',
  };
  return nameMap[style] || 'ringLoader';
};

// eslint-disable-next-line no-unused-vars
const getLoaderUsageParams = (style, config) => {
  const params = [];
  
  // Common params
  if (config.size !== undefined) params.push(`size: ${config.size}`);
  if (config.primaryColor !== undefined) params.push(`primaryColor: Color(hex: "${config.primaryColor}")`);
  if (config.secondaryColor !== undefined) params.push(`secondaryColor: Color(hex: "${config.secondaryColor}")`);
  if (config.speed !== undefined) params.push(`speed: ${config.speed}`);
  
  // Loader-specific params
  switch (style) {
    default:
      break;
    case LOADER_TYPES.RING:
      if (config.strokeWidth !== undefined) params.push(`strokeWidth: ${config.strokeWidth}`);
      break;
    case LOADER_TYPES.PULSE:
      if (config.minSize !== undefined) params.push(`minSize: ${config.minSize}`);
      if (config.maxSize !== undefined) params.push(`maxSize: ${config.maxSize}`);
      if (config.useGradient !== undefined) params.push(`useGradient: ${config.useGradient}`);
      break;
    case LOADER_TYPES.DOTS:
      if (config.dotCount !== undefined) params.push(`dotCount: ${config.dotCount}`);
      if (config.minDotSize !== undefined) params.push(`minDotSize: ${config.minDotSize}`);
      if (config.maxDotSize !== undefined) params.push(`maxDotSize: ${config.maxDotSize}`);
      if (config.useGradient !== undefined) params.push(`useGradient: ${config.useGradient}`);
      break;
    case LOADER_TYPES.BARS:
      if (config.barCount !== undefined) params.push(`barCount: ${config.barCount}`);
      if (config.barWidth !== undefined) params.push(`barWidth: ${config.barWidth}`);
      if (config.barHeight !== undefined) params.push(`barHeight: ${config.barHeight}`);
      if (config.barSpacing !== undefined) params.push(`barSpacing: ${config.barSpacing}`);
      if (config.useGradient !== undefined) params.push(`useGradient: ${config.useGradient}`);
      break;
    case LOADER_TYPES.WAVE:
      if (config.waveCount !== undefined) params.push(`waveCount: ${config.waveCount}`);
      if (config.waveHeight !== undefined) params.push(`waveHeight: ${config.waveHeight}`);
      if (config.waveWidth !== undefined) params.push(`waveWidth: ${config.waveWidth}`);
      if (config.useGradient !== undefined) params.push(`useGradient: ${config.useGradient}`);
      break;
    case LOADER_TYPES.SPINNER:
      if (config.armCount !== undefined) params.push(`armCount: ${config.armCount}`);
      if (config.armLength !== undefined) params.push(`armLength: ${config.armLength}`);
      if (config.useGradient !== undefined) params.push(`useGradient: ${config.useGradient}`);
      break;
    case LOADER_TYPES.PROGRESS_RING:
      if (config.progress !== undefined) params.push(`progress: ${config.progress}`);
      if (config.strokeWidth !== undefined) params.push(`strokeWidth: ${config.strokeWidth}`);
      break;
    case LOADER_TYPES.SKELETON:
      if (config.skeletonLineCount !== undefined) params.push(`skeletonLineCount: ${config.skeletonLineCount}`);
      if (config.skeletonLineHeight !== undefined) params.push(`skeletonLineHeight: ${config.skeletonLineHeight}`);
      if (config.skeletonLineSpacing !== undefined) params.push(`skeletonLineSpacing: ${config.skeletonLineSpacing}`);
      break;
    case LOADER_TYPES.BOUNCE:
      if (config.dotCount !== undefined) params.push(`dotCount: ${config.dotCount}`);
      if (config.dotSize !== undefined) params.push(`dotSize: ${config.dotSize}`);
      if (config.bounceHeight !== undefined) params.push(`bounceHeight: ${config.bounceHeight}`);
      if (config.useGradient !== undefined) params.push(`useGradient: ${config.useGradient}`);
      break;
    case LOADER_TYPES.ROTATE:
      if (config.elementCount !== undefined) params.push(`elementCount: ${config.elementCount}`);
      if (config.elementSize !== undefined) params.push(`elementSize: ${config.elementSize}`);
      if (config.useGradient !== undefined) params.push(`useGradient: ${config.useGradient}`);
      break;
    case LOADER_TYPES.FADE:
      if (config.elementCount !== undefined) params.push(`elementCount: ${config.elementCount}`);
      if (config.elementSize !== undefined) params.push(`elementSize: ${config.elementSize}`);
      if (config.useGradient !== undefined) params.push(`useGradient: ${config.useGradient}`);
      break;
    case LOADER_TYPES.SCALE:
      if (config.elementCount !== undefined) params.push(`elementCount: ${config.elementCount}`);
      if (config.minScale !== undefined) params.push(`minScale: ${config.minScale}`);
      if (config.maxScale !== undefined) params.push(`maxScale: ${config.maxScale}`);
      if (config.useGradient !== undefined) params.push(`useGradient: ${config.useGradient}`);
      break;
    case LOADER_TYPES.RIPPLE:
      if (config.rippleCount !== undefined) params.push(`rippleCount: ${config.rippleCount}`);
      if (config.rippleWidth !== undefined) params.push(`rippleWidth: ${config.rippleWidth}`);
      if (config.useGradient !== undefined) params.push(`useGradient: ${config.useGradient}`);
      break;
    case LOADER_TYPES.ORBIT:
      if (config.orbitCount !== undefined) params.push(`orbitCount: ${config.orbitCount}`);
      if (config.orbitSize !== undefined) params.push(`orbitSize: ${config.orbitSize}`);
      if (config.orbitRadius !== undefined) params.push(`orbitRadius: ${config.orbitRadius}`);
      if (config.useGradient !== undefined) params.push(`useGradient: ${config.useGradient}`);
      break;
    case LOADER_TYPES.GRID:
      if (config.gridRows !== undefined) params.push(`gridRows: ${config.gridRows}`);
      if (config.gridCols !== undefined) params.push(`gridCols: ${config.gridCols}`);
      if (config.cellSize !== undefined) params.push(`cellSize: ${config.cellSize}`);
      if (config.useGradient !== undefined) params.push(`useGradient: ${config.useGradient}`);
      break;
    case LOADER_TYPES.LINES:
      if (config.lineCount !== undefined) params.push(`lineCount: ${config.lineCount}`);
      if (config.lineWidth !== undefined) params.push(`lineWidth: ${config.lineWidth}`);
      if (config.lineLength !== undefined) params.push(`lineLength: ${config.lineLength}`);
      if (config.useGradient !== undefined) params.push(`useGradient: ${config.useGradient}`);
      break;
    case LOADER_TYPES.SHIMMER:
      if (config.width !== undefined) params.push(`width: ${config.width}`);
      if (config.height !== undefined) params.push(`height: ${config.height}`);
      if (config.shimmerColor !== undefined) params.push(`shimmerColor: Color(hex: "${config.shimmerColor}")`);
      break;
    case LOADER_TYPES.STAGGER:
      if (config.elementCount !== undefined) params.push(`elementCount: ${config.elementCount}`);
      if (config.elementSize !== undefined) params.push(`elementSize: ${config.elementSize}`);
      if (config.staggerDelay !== undefined) params.push(`staggerDelay: ${config.staggerDelay}`);
      if (config.useGradient !== undefined) params.push(`useGradient: ${config.useGradient}`);
      break;
    case LOADER_TYPES.MORPH:
      if (config.shapeCount !== undefined) params.push(`shapeCount: ${config.shapeCount}`);
      if (config.useGradient !== undefined) params.push(`useGradient: ${config.useGradient}`);
      break;
    case LOADER_TYPES.PARTICLE:
      if (config.particleCount !== undefined) params.push(`particleCount: ${config.particleCount}`);
      if (config.particleSize !== undefined) params.push(`particleSize: ${config.particleSize}`);
      if (config.useGradient !== undefined) params.push(`useGradient: ${config.useGradient}`);
      break;
  }
  
  return params.length > 0 ? `, ${params.join(', ')}` : '';
};

// eslint-disable-next-line no-unused-vars
const getStyleEnumValue = (style, config) => {
  switch (style) {
    case LOADER_TYPES.RING:
      return `.ring(strokeWidth: ${config.strokeWidth || 4})`;
    case LOADER_TYPES.PULSE:
      return `.pulse(minSize: ${config.minSize || 30}, maxSize: ${config.maxSize || 50}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.DOTS:
      return `.dots(dotCount: ${config.dotCount || 3}, minDotSize: ${config.minDotSize || 8}, maxDotSize: ${config.maxDotSize || 20}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.BARS:
      return `.bars(barCount: ${config.barCount || 5}, barWidth: ${config.barWidth || 4}, barHeight: ${config.barHeight || 30}, barSpacing: ${config.barSpacing || 4}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.WAVE:
      return `.wave(waveCount: ${config.waveCount || 4}, waveHeight: ${config.waveHeight || 20}, waveWidth: ${config.waveWidth || 6}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.SPINNER:
      return `.spinner(armCount: ${config.armCount || 8}, armLength: ${config.armLength || 20}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.PROGRESS_RING:
      return `.progressRing(progress: ${config.progress || 0.7}, strokeWidth: ${config.strokeWidth || 4})`;
    case LOADER_TYPES.SKELETON:
      return `.skeleton(lineCount: ${config.skeletonLineCount || 3}, lineHeight: ${config.skeletonLineHeight || 12}, lineSpacing: ${config.skeletonLineSpacing || 8})`;
    case LOADER_TYPES.BOUNCE:
      return `.bounce(dotCount: ${config.dotCount || 3}, dotSize: ${config.dotSize || 12}, bounceHeight: ${config.bounceHeight || 20}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.ROTATE:
      return `.rotate(elementCount: ${config.elementCount || 6}, elementSize: ${config.elementSize || 8}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.FADE:
      return `.fade(elementCount: ${config.elementCount || 4}, elementSize: ${config.elementSize || 12}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.SCALE:
      return `.scale(elementCount: ${config.elementCount || 3}, minScale: ${config.minScale || 0.5}, maxScale: ${config.maxScale || 1.0}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.RIPPLE:
      return `.ripple(rippleCount: ${config.rippleCount || 3}, rippleWidth: ${config.rippleWidth || 2}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.ORBIT:
      return `.orbit(orbitCount: ${config.orbitCount || 3}, orbitSize: ${config.orbitSize || 8}, orbitRadius: ${config.orbitRadius || 20}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.GRID:
      return `.grid(gridRows: ${config.gridRows || 3}, gridCols: ${config.gridCols || 3}, cellSize: ${config.cellSize || 10}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.LINES:
      return `.lines(lineCount: ${config.lineCount || 5}, lineWidth: ${config.lineWidth || 3}, lineLength: ${config.lineLength || 30}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.SHIMMER:
      return `.shimmer(width: ${config.width || 200}, height: ${config.height || 20}, shimmerColor: Color(hex: "${config.shimmerColor || '#ffffff'}"))`;
    case LOADER_TYPES.STAGGER:
      return `.stagger(elementCount: ${config.elementCount || 5}, elementSize: ${config.elementSize || 10}, staggerDelay: ${config.staggerDelay || 0.1}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.MORPH:
      return `.morph(shapeCount: ${config.shapeCount || 4}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.PARTICLE:
      return `.particle(particleCount: ${config.particleCount || 12}, particleSize: ${config.particleSize || 4}, useGradient: ${config.useGradient || false})`;
    default:
      return `.ring(strokeWidth: 4)`;
  }
};

// eslint-disable-next-line no-unused-vars
const getDirectLoaderUsage = (style, config) => {
  switch (style) {
    case LOADER_TYPES.RING:
      return `RingLoader(size: ${config.size}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, strokeWidth: ${config.strokeWidth || 4})`;
    case LOADER_TYPES.PULSE:
      return `PulseLoader(size: ${config.size}, minSize: ${config.minSize || 30}, maxSize: ${config.maxSize || 50}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.DOTS:
      return `DotsLoader(size: ${config.size}, dotCount: ${config.dotCount || 3}, minDotSize: ${config.minDotSize || 8}, maxDotSize: ${config.maxDotSize || 20}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.BARS:
      return `BarsLoader(size: ${config.size}, barCount: ${config.barCount || 5}, barWidth: ${config.barWidth || 4}, barHeight: ${config.barHeight || 30}, barSpacing: ${config.barSpacing || 4}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.WAVE:
      return `WaveLoader(size: ${config.size}, waveCount: ${config.waveCount || 4}, waveHeight: ${config.waveHeight || 20}, waveWidth: ${config.waveWidth || 6}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.SPINNER:
      return `SpinnerLoader(size: ${config.size}, armCount: ${config.armCount || 8}, armLength: ${config.armLength || 20}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.PROGRESS_RING:
      return `ProgressRingLoader(size: ${config.size}, progress: ${config.progress || 0.7}, strokeWidth: ${config.strokeWidth || 4}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed})`;
    case LOADER_TYPES.SKELETON:
      return `SkeletonLoader(size: ${config.size}, lineCount: ${config.skeletonLineCount || 3}, lineHeight: ${config.skeletonLineHeight || 12}, lineSpacing: ${config.skeletonLineSpacing || 8}, primaryColor: Color(hex: "${config.primaryColor}"), speed: ${config.speed})`;
    case LOADER_TYPES.BOUNCE:
      return `BounceLoader(size: ${config.size}, dotCount: ${config.dotCount || 3}, dotSize: ${config.dotSize || 12}, bounceHeight: ${config.bounceHeight || 20}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.ROTATE:
      return `RotateLoader(size: ${config.size}, elementCount: ${config.elementCount || 6}, elementSize: ${config.elementSize || 8}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.FADE:
      return `FadeLoader(size: ${config.size}, elementCount: ${config.elementCount || 4}, elementSize: ${config.elementSize || 12}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.SCALE:
      return `ScaleLoader(size: ${config.size}, elementCount: ${config.elementCount || 3}, minScale: ${config.minScale || 0.5}, maxScale: ${config.maxScale || 1.0}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.RIPPLE:
      return `RippleLoader(size: ${config.size}, rippleCount: ${config.rippleCount || 3}, rippleWidth: ${config.rippleWidth || 2}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.ORBIT:
      return `OrbitLoader(size: ${config.size}, orbitCount: ${config.orbitCount || 3}, orbitSize: ${config.orbitSize || 8}, orbitRadius: ${config.orbitRadius || 20}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.GRID:
      return `GridLoader(size: ${config.size}, gridRows: ${config.gridRows || 3}, gridCols: ${config.gridCols || 3}, cellSize: ${config.cellSize || 10}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.LINES:
      return `LinesLoader(size: ${config.size}, lineCount: ${config.lineCount || 5}, lineWidth: ${config.lineWidth || 3}, lineLength: ${config.lineLength || 30}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.SHIMMER:
      return `ShimmerLoader(width: ${config.width || 200}, height: ${config.height || 20}, primaryColor: Color(hex: "${config.primaryColor}"), shimmerColor: Color(hex: "${config.shimmerColor || '#ffffff'}"), speed: ${config.speed})`;
    case LOADER_TYPES.STAGGER:
      return `StaggerLoader(size: ${config.size}, elementCount: ${config.elementCount || 5}, elementSize: ${config.elementSize || 10}, staggerDelay: ${config.staggerDelay || 0.1}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.MORPH:
      return `MorphLoader(size: ${config.size}, shapeCount: ${config.shapeCount || 4}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, useGradient: ${config.useGradient || false})`;
    case LOADER_TYPES.PARTICLE:
      return `ParticleLoader(size: ${config.size}, particleCount: ${config.particleCount || 12}, particleSize: ${config.particleSize || 4}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, useGradient: ${config.useGradient || false})`;
    default:
      return `RingLoader(size: ${config.size}, primaryColor: Color(hex: "${config.primaryColor}"), secondaryColor: Color(hex: "${config.secondaryColor}"), speed: ${config.speed}, strokeWidth: ${config.strokeWidth || 4})`;
  }
};

/**
 * Generate SwiftUI Gradient code
 */
export const generateGradientCode = (config) => {
  const {
    colors = ['#F26F29', '#F03224'],
    mode = 'linear',
    direction = 'vertical',
    customAngle = 0,
    angle = 0, // Keep for backward compatibility
    useStops = false,
    stops = [],
    radialCenter = 'center',
    radialCenterX = 0.5,
    radialCenterY = 0.5,
    startRadius = 0,
    endRadius = 100,
    angularCenter = 'center',
    angularCenterX = 0.5,
    angularCenterY = 0.5,
    startAngle = 0,
    endAngle = 360,
    gradientOpacity = 1.0,
    componentType = 'view',
    hasCornerRadius = false,
    cornerRadius = 0,
    shape = 'rectangle',
    buttonTitle = 'Button',
    textColor = '#FFFFFF',
    fontName = 'system',
    fontSize = 16,
    fontWeight = 'regular',
    sizingMode = 'fixed',
    width = 200,
    height = 50,
    autolayoutType = 'fullWidth',
    padding = 16,
    aspectRatio = 1.0,
  } = config;
  
  // Use stops if enabled and available, otherwise use colors
  const useGradientStops = useStops && stops && stops.length > 0;
  const colorStrings = colors.map(c => `Color(hex: "${c}")`).join(', ');
  
  let gradientCode = '';
  
  if (mode === 'linear') {
    const startPoint = getStartPoint(direction, customAngle);
    const endPoint = getEndPoint(direction, customAngle);
    
    if (useGradientStops) {
      gradientCode = `LinearGradient(
    stops: [
        ${stops.map(s => `.init(color: Color(hex: "${s.color}"), location: ${s.location})`).join(',\n        ')}
    ],
    startPoint: ${startPoint},
    endPoint: ${endPoint}
)`;
    } else {
      gradientCode = `LinearGradient(
    colors: [${colorStrings}],
    startPoint: ${startPoint},
    endPoint: ${endPoint}
)`;
    }
  } else if (mode === 'radial') {
    const centerCode = getUnitPointCode(radialCenter, radialCenterX, radialCenterY);
    if (useGradientStops) {
      const stopsCode = getGradientStopsCode(stops);
      gradientCode = `RadialGradient(
    gradient: ${stopsCode},
    center: ${centerCode},
    startRadius: ${startRadius},
    endRadius: ${endRadius}
)`;
    } else {
      gradientCode = `RadialGradient(
    colors: [${colorStrings}],
    center: ${centerCode},
    startRadius: ${startRadius},
    endRadius: ${endRadius}
)`;
    }
  } else if (mode === 'angular') {
    const centerCode = getUnitPointCode(angularCenter, angularCenterX, angularCenterY);
    // Use startAngle/endAngle if they differ, otherwise use angle for backward compatibility
    const angleValue = (startAngle !== 0 || endAngle !== 360) ? startAngle : angle;
    if (useGradientStops) {
      const stopsCode = getGradientStopsCode(stops);
      if (startAngle !== 0 || endAngle !== 360) {
        // Note: SwiftUI AngularGradient doesn't support startAngle/endAngle directly
        // We'll use the angle parameter and note the limitation
        gradientCode = `AngularGradient(
    gradient: ${stopsCode},
    center: ${centerCode},
    angle: .degrees(${startAngle})
)`;
      } else {
        gradientCode = `AngularGradient(
    gradient: ${stopsCode},
    center: ${centerCode},
    angle: .degrees(${angleValue})
)`;
      }
    } else {
      if (startAngle !== 0 || endAngle !== 360) {
        // Note: SwiftUI AngularGradient doesn't support startAngle/endAngle directly
        gradientCode = `AngularGradient(
    colors: [${colorStrings}],
    center: ${centerCode},
    angle: .degrees(${startAngle})
)`;
      } else {
        gradientCode = `AngularGradient(
    colors: [${colorStrings}],
    center: ${centerCode},
    angle: .degrees(${angleValue})
)`;
      }
    }
  } else if (mode === 'elliptical') {
    const centerCode = getUnitPointCode(radialCenter, radialCenterX, radialCenterY);
    if (useGradientStops) {
      const stopsCode = getGradientStopsCode(stops);
      gradientCode = `EllipticalGradient(
    gradient: ${stopsCode},
    center: ${centerCode},
    startRadius: ${startRadius},
    endRadius: ${endRadius}
)`;
    } else {
      gradientCode = `EllipticalGradient(
    colors: [${colorStrings}],
    center: ${centerCode},
    startRadius: ${startRadius},
    endRadius: ${endRadius}
)`;
    }
  }

  // Helper function to get font weight
  const getFontWeightCode = () => {
    switch (fontWeight) {
      case 'medium':
        return '.medium';
      case 'semibold':
        return '.semibold';
      case 'bold':
        return '.bold';
      default:
        return '.regular';
    }
  };

  // Helper function to get font code
  const getFontCode = () => {
    if (fontName === 'system' || !fontName) {
      return `.font(.system(size: ${fontSize}, weight: ${getFontWeightCode()}))`;
    } else {
      return `.font(.custom("${fontName}", size: ${fontSize}))`;
    }
  };

  // Helper function to get shape modifier
  const getShapeModifier = () => {
    switch (shape) {
      case 'circle':
        return '.clipShape(Circle())';
      case 'square':
        return '.aspectRatio(1, contentMode: .fit)';
      case 'capsule':
        return '.clipShape(Capsule())';
      case 'roundedRectangle':
        if (hasCornerRadius) {
          return `.clipShape(RoundedRectangle(cornerRadius: ${cornerRadius}))`;
        } else {
          return `.clipShape(RoundedRectangle(cornerRadius: 8))`;
        }
      default:
        return '';
    }
  };

  // Helper function to get corner radius modifier
  const getCornerRadiusModifier = () => {
    if (hasCornerRadius && shape !== 'circle' && shape !== 'capsule' && shape !== 'roundedRectangle') {
      return `.cornerRadius(${cornerRadius})`;
    }
    return '';
  };

  // Helper function to get sizing modifiers
  const getSizingModifiers = () => {
    const modifiers = [];
    
    if (sizingMode === 'fixed') {
      modifiers.push(`.frame(width: ${width}, height: ${height})`);
    } else {
      // AutoLayout mode
      switch (autolayoutType) {
        case 'fullWidth':
          modifiers.push('.frame(maxWidth: .infinity)');
          break;
        case 'fullHeight':
          modifiers.push('.frame(maxHeight: .infinity)');
          break;
        case 'padding':
          modifiers.push(`.padding(${padding})`);
          modifiers.push('.frame(maxWidth: .infinity)');
          break;
        case 'aspectRatio':
          modifiers.push(`.aspectRatio(${aspectRatio}, contentMode: .fit)`);
          break;
        default:
          break;
      }
    }
    
    return modifiers.join('\n        ');
  };

  // Helper function to get opacity modifier
  const getOpacityModifier = () => {
    if (gradientOpacity !== undefined && gradientOpacity < 1.0) {
      return `.opacity(${gradientOpacity})`;
    }
    return '';
  };

  // Generate component code
  let componentCode = '';
  
  if (componentType === 'button') {
    const textColorCode = `Color(hex: "${textColor}")`;
    componentCode = `Button(action: {
    // Button action
}) {
    Text("${buttonTitle}")
        ${getFontCode()}
        ${fontName === 'system' ? `.fontWeight(${getFontWeightCode()})` : ''}
        .foregroundColor(${textColorCode})
}
.background(${gradientCode})
${getOpacityModifier()}
${getCornerRadiusModifier()}
${getShapeModifier()}
${getSizingModifiers()}`;
  } else {
    // View component
    let shapeCode = '';
    switch (shape) {
      case 'circle':
        shapeCode = 'Circle()';
        break;
      case 'square':
        shapeCode = 'Rectangle()';
        break;
      case 'capsule':
        shapeCode = 'Capsule()';
        break;
      case 'roundedRectangle':
        if (hasCornerRadius) {
          shapeCode = `RoundedRectangle(cornerRadius: ${cornerRadius})`;
        } else {
          shapeCode = 'RoundedRectangle(cornerRadius: 8)';
        }
        break;
      default:
        shapeCode = 'Rectangle()';
        break;
    }

    componentCode = `${shapeCode}
    .fill(${gradientCode})
    ${getOpacityModifier()}
    ${getCornerRadiusModifier()}
    ${shape === 'square' ? '.aspectRatio(1, contentMode: .fit)' : ''}
    ${getSizingModifiers()}`;
  }
  
  const usageExample = `
// MARK: - Usage Example
// Usage Example:
struct ContentView: View {
    var body: some View {
        VStack {
            ${componentCode.split('\n').map(line => '            ' + line).join('\n')}
        }
        .padding()
    }
}
`;
  
  const helperExtensionCode = `
// MARK: - Helper Extensions
// Helper extension for hex colors
extension Color {
    init(hex: String) {
        let hex = hex.trimmingCharacters(in: CharacterSet.alphanumerics.inverted)
        var int: UInt64 = 0
        Scanner(string: hex).scanHexInt64(&int)
        let a, r, g, b: UInt64
        switch hex.count {
        case 3: // RGB (12-bit)
            (a, r, g, b) = (255, (int >> 8) * 17, (int >> 4 & 0xF) * 17, (int & 0xF) * 17)
        case 6: // RGB (24-bit)
            (a, r, g, b) = (255, int >> 16, int >> 8 & 0xFF, int & 0xFF)
        case 8: // ARGB (32-bit)
            (a, r, g, b) = (int >> 24, int >> 16 & 0xFF, int >> 8 & 0xFF, int & 0xFF)
        default:
            (a, r, g, b) = (255, 0, 0, 0)
        }
        self.init(
            .sRGB,
            red: Double(r) / 255,
            green: Double(g) / 255,
            blue: Double(b) / 255,
            opacity: Double(a) / 255
        )
    }
}
`;

  return {
    blocks: [
      {
        name: 'Usage Example',
        code: usageExample,
        requiresImport: true,
        order: 1
      },
      {
        name: 'Gradient Component',
        code: `// MARK: - Main Component\n// Generated Gradient Component\n${componentCode}`,
        requiresImport: false,
        order: 2
      },
      {
        name: 'Color Extension',
        code: helperExtensionCode,
        requiresImport: false,
        order: 3,
        optional: true
      }
    ],
    importStatement: 'import SwiftUI'
  };
};

// Helper function to convert center preset/custom to SwiftUI UnitPoint
const getUnitPointCode = (center, centerX, centerY) => {
  if (center === 'custom') {
    return `UnitPoint(x: ${centerX}, y: ${centerY})`;
  }
  const presetMap = {
    'center': '.center',
    'top': '.top',
    'bottom': '.bottom',
    'leading': '.leading',
    'trailing': '.trailing',
    'topLeading': '.topLeading',
    'topTrailing': '.topTrailing',
    'bottomLeading': '.bottomLeading',
    'bottomTrailing': '.bottomTrailing',
  };
  return presetMap[center] || '.center';
};

// Helper function to generate gradient stops code
const getGradientStopsCode = (stops) => {
  if (!stops || stops.length === 0) return '';
  const stopStrings = stops.map(s => 
    `.init(color: Color(hex: "${s.color}"), location: ${s.location})`
  );
  return `Gradient(stops: [\n        ${stopStrings.join(',\n        ')}\n    ])`;
};

// Helper function to calculate start point from angle
const getStartPointFromAngle = (angle) => {
  // Convert angle to radians, then to UnitPoint coordinates
  // angle 0° = top (0, 0), 90° = trailing (1, 0), 180° = bottom (0.5, 1), 270° = leading (0, 0.5)
  const radians = (angle * Math.PI) / 180;
  const x = 0.5 + 0.5 * Math.sin(radians);
  const y = 0.5 - 0.5 * Math.cos(radians);
  return `UnitPoint(x: ${x.toFixed(3)}, y: ${y.toFixed(3)})`;
};

// Helper function to calculate end point from angle (opposite of start)
const getEndPointFromAngle = (angle) => {
  const radians = (angle * Math.PI) / 180;
  const x = 0.5 - 0.5 * Math.sin(radians);
  const y = 0.5 + 0.5 * Math.cos(radians);
  return `UnitPoint(x: ${x.toFixed(3)}, y: ${y.toFixed(3)})`;
};

const getStartPoint = (direction, customAngle) => {
  if (direction === 'custom' && customAngle !== undefined) {
    return getStartPointFromAngle(customAngle);
  }
  switch (direction) {
    case 'vertical': return '.top';
    case 'horizontal': return '.leading';
    case 'diagonalTL': return '.topLeading';
    case 'diagonalTR': return '.topTrailing';
    case 'diagonalBL': return '.bottomLeading';
    case 'diagonalBR': return '.bottomTrailing';
    case 'topToBottom': return '.top';
    case 'leftToRight': return '.leading';
    default: return '.top';
  }
};

const getEndPoint = (direction, customAngle) => {
  if (direction === 'custom' && customAngle !== undefined) {
    return getEndPointFromAngle(customAngle);
  }
  switch (direction) {
    case 'vertical': return '.bottom';
    case 'horizontal': return '.trailing';
    case 'diagonalTL': return '.bottomTrailing';
    case 'diagonalTR': return '.bottomLeading';
    case 'diagonalBL': return '.topTrailing';
    case 'diagonalBR': return '.topLeading';
    case 'topToBottom': return '.bottom';
    case 'leftToRight': return '.trailing';
    default: return '.bottom';
  }
};

/**
 * Generate SwiftUI Card code
 */
export const generateCardCode = (config) => {
  const {
    mode = 'card',
    cardStyle = {},
    viewTree = [],
  } = config;

  // Validate configuration
  const validateCardConfig = (config) => {
    const errors = [];
    
    if (!Array.isArray(config.viewTree)) {
      errors.push('viewTree must be an array');
    }
    
    if (config.cardStyle) {
      if (config.cardStyle.padding !== undefined && (typeof config.cardStyle.padding !== 'number' || config.cardStyle.padding < 0)) {
        errors.push('cardStyle.padding must be a non-negative number');
      }
      if (config.cardStyle.cornerRadius !== undefined && (typeof config.cardStyle.cornerRadius !== 'number' || config.cardStyle.cornerRadius < 0)) {
        errors.push('cardStyle.cornerRadius must be a non-negative number');
      }
      if (config.cardStyle.shadowRadius !== undefined && (typeof config.cardStyle.shadowRadius !== 'number' || config.cardStyle.shadowRadius < 0)) {
        errors.push('cardStyle.shadowRadius must be a non-negative number');
      }
      if (config.cardStyle.shadowOpacity !== undefined && (typeof config.cardStyle.shadowOpacity !== 'number' || config.cardStyle.shadowOpacity < 0 || config.cardStyle.shadowOpacity > 1)) {
        errors.push('cardStyle.shadowOpacity must be a number between 0 and 1');
      }
    }
    
    return errors;
  };

  const validationErrors = validateCardConfig(config);
  if (validationErrors.length > 0) {
    return `// Error: Invalid configuration\n// ${validationErrors.join('\n// ')}\n\nimport SwiftUI\n\nstruct CardView: View {\n    var body: some View {\n        Text("Invalid configuration")\n    }\n}`;
  }

  // Constants for default values
  const CARD_DEFAULTS = {
    padding: 16,
    cornerRadius: 12,
    shadowRadius: 8,
    shadowOpacity: 0.2,
    spacing: 8,
    defaultFontSize: 16,
  };

  // Helper function to get alignment SwiftUI code
  const getAlignmentCode = (alignment, type) => {
    if (type === 'HStack') {
      switch (alignment) {
        case 'leading':
          return '.leading';
        case 'center':
          return '.center';
        case 'trailing':
          return '.trailing';
        default:
          return '.center';
      }
    } else if (type === 'VStack') {
      switch (alignment) {
        case 'top':
          return '.top';
        case 'center':
          return '.center';
        case 'bottom':
          return '.bottom';
        default:
          return '.center';
      }
    } else if (type === 'ZStack') {
      switch (alignment) {
        case 'topLeading':
          return '.topLeading';
        case 'top':
          return '.top';
        case 'topTrailing':
          return '.topTrailing';
        case 'leading':
          return '.leading';
        case 'center':
          return '.center';
        case 'trailing':
          return '.trailing';
        case 'bottomLeading':
          return '.bottomLeading';
        case 'bottom':
          return '.bottom';
        case 'bottomTrailing':
          return '.bottomTrailing';
        default:
          return '.center';
      }
    }
    return '.center';
  };

  // Helper function to get font weight code
  const getFontWeightCode = (weight) => {
    switch (weight) {
      case 'medium':
        return '.medium';
      case 'semibold':
        return '.semibold';
      case 'bold':
        return '.bold';
      default:
        return '.regular';
    }
  };

  // Helper function to generate modifier code with proper SwiftUI ordering
  // Order: Layout → Visual → Shape → Effects → Interaction
  const generateModifiersCode = (modifiers, indent = 0) => {
    if (!modifiers) return '';
    
    const indentStr = '    '.repeat(indent);
    
    // Separate modifiers into categories for proper ordering
    const layoutModifiers = [];
    const visualModifiers = [];
    const shapeModifiers = [];
    const effectModifiers = [];
    const interactionModifiers = [];

    // Layout Modifiers: frame, padding, offset
    if (modifiers.padding) {
      const p = modifiers.padding;
      if (p.all !== null && p.all !== undefined) {
        layoutModifiers.push(`${indentStr}.padding(${p.all})`);
      } else {
        if (p.horizontal !== null && p.horizontal !== undefined) {
          layoutModifiers.push(`${indentStr}.padding(.horizontal, ${p.horizontal})`);
        }
        if (p.vertical !== null && p.vertical !== undefined) {
          layoutModifiers.push(`${indentStr}.padding(.vertical, ${p.vertical})`);
        }
        if (p.top !== null && p.top !== undefined) {
          layoutModifiers.push(`${indentStr}.padding(.top, ${p.top})`);
        }
        if (p.bottom !== null && p.bottom !== undefined) {
          layoutModifiers.push(`${indentStr}.padding(.bottom, ${p.bottom})`);
        }
        if (p.leading !== null && p.leading !== undefined) {
          layoutModifiers.push(`${indentStr}.padding(.leading, ${p.leading})`);
        }
        if (p.trailing !== null && p.trailing !== undefined) {
          layoutModifiers.push(`${indentStr}.padding(.trailing, ${p.trailing})`);
        }
      }
    }

    if (modifiers.frame) {
      const f = modifiers.frame;
      const frameParts = [];
      if (f.width !== null && f.width !== undefined) frameParts.push(`width: ${f.width}`);
      if (f.height !== null && f.height !== undefined) frameParts.push(`height: ${f.height}`);
      if (f.minWidth !== null && f.minWidth !== undefined) frameParts.push(`minWidth: ${f.minWidth}`);
      if (f.maxWidth !== null && f.maxWidth !== undefined && !f.maxWidthInfinity) {
        frameParts.push(`maxWidth: ${f.maxWidth}`);
      }
      if (f.minHeight !== null && f.minHeight !== undefined) frameParts.push(`minHeight: ${f.minHeight}`);
      if (f.maxHeight !== null && f.maxHeight !== undefined && !f.maxHeightInfinity) {
        frameParts.push(`maxHeight: ${f.maxHeight}`);
      }
      if (f.idealWidth !== null && f.idealWidth !== undefined) frameParts.push(`idealWidth: ${f.idealWidth}`);
      if (f.idealHeight !== null && f.idealHeight !== undefined) frameParts.push(`idealHeight: ${f.idealHeight}`);
      if (f.maxWidthInfinity) frameParts.push(`maxWidth: .infinity`);
      if (f.maxHeightInfinity) frameParts.push(`maxHeight: .infinity`);
      
      if (frameParts.length > 0) {
        if (frameParts.length === 1 && frameParts[0].includes('maxWidth: .infinity')) {
          layoutModifiers.push(`${indentStr}.frame(maxWidth: .infinity)`);
        } else if (frameParts.length === 1 && frameParts[0].includes('maxHeight: .infinity')) {
          layoutModifiers.push(`${indentStr}.frame(maxHeight: .infinity)`);
        } else {
          layoutModifiers.push(`${indentStr}.frame(${frameParts.join(', ')})`);
        }
      }
    }

    if (modifiers.offset) {
      const ox = modifiers.offset.x || 0;
      const oy = modifiers.offset.y || 0;
      if (ox !== 0 || oy !== 0) {
        layoutModifiers.push(`${indentStr}.offset(x: ${ox}, y: ${oy})`);
      }
    }

    // Visual Modifiers: background, foregroundColor, opacity
    if (modifiers.background && modifiers.background.type !== 'none') {
      if (modifiers.background.type === 'color') {
        visualModifiers.push(`${indentStr}.background(Color(hex: "${modifiers.background.color || '#FFFFFF'}"))`);
      } else if (modifiers.background.type === 'gradient') {
        const colors = modifiers.background.gradientColors || ['#F26F29', '#F03224'];
        visualModifiers.push(`${indentStr}.background(LinearGradient(
${indentStr}    colors: [Color(hex: "${colors[0]}"), Color(hex: "${colors[1]}")],
${indentStr}    startPoint: .topLeading,
${indentStr}    endPoint: .bottomTrailing
${indentStr}))`);
      }
    }

    if (modifiers.foregroundColor) {
      const colorCode = getColorCode(modifiers.foregroundColor);
      visualModifiers.push(`${indentStr}.foregroundColor(${colorCode})`);
    }

    if (modifiers.opacity !== null && modifiers.opacity !== undefined && modifiers.opacity !== 1.0) {
      visualModifiers.push(`${indentStr}.opacity(${modifiers.opacity})`);
    }

    // Shape Modifiers: clipShape, aspectRatio
    if (modifiers.cornerRadius && modifiers.cornerRadius > 0) {
      shapeModifiers.push(`${indentStr}.clipShape(RoundedRectangle(cornerRadius: ${modifiers.cornerRadius}))`);
    }

    if (modifiers.clipShape && modifiers.clipShape !== 'none') {
      switch (modifiers.clipShape) {
        case 'circle':
          shapeModifiers.push(`${indentStr}.clipShape(Circle())`);
          break;
        case 'roundedRectangle':
          const radius = modifiers.cornerRadius || 8;
          shapeModifiers.push(`${indentStr}.clipShape(RoundedRectangle(cornerRadius: ${radius}))`);
          break;
        case 'capsule':
          shapeModifiers.push(`${indentStr}.clipShape(Capsule())`);
          break;
        case 'ellipse':
          shapeModifiers.push(`${indentStr}.clipShape(Ellipse())`);
          break;
        default:
          break;
      }
    }

    if (modifiers.aspectRatio && modifiers.aspectRatio.enabled) {
      const ratio = modifiers.aspectRatio.ratio || 1.0;
      const contentMode = modifiers.aspectRatio.contentMode === 'fill' ? '.fill' : '.fit';
      shapeModifiers.push(`${indentStr}.aspectRatio(${ratio}, contentMode: ${contentMode})`);
    }

    // Effect Modifiers: shadow, rotationEffect, scaleEffect
    if (modifiers.shadow && modifiers.shadow.enabled) {
      const s = modifiers.shadow;
      effectModifiers.push(`${indentStr}.shadow(color: Color.black.opacity(${s.opacity || 0.2}), radius: ${s.radius || 8}, x: ${s.x || 0}, y: ${s.y || 4})`);
    }

    if (modifiers.rotation && modifiers.rotation !== 0) {
      effectModifiers.push(`${indentStr}.rotationEffect(.degrees(${modifiers.rotation}))`);
    }

    if (modifiers.scale) {
      const sx = modifiers.scale.x || 1.0;
      const sy = modifiers.scale.y || 1.0;
      if (sx !== 1.0 || sy !== 1.0) {
        if (sx === sy) {
          effectModifiers.push(`${indentStr}.scaleEffect(${sx})`);
        } else {
          effectModifiers.push(`${indentStr}.scaleEffect(x: ${sx}, y: ${sy})`);
        }
      }
    }

    // Border (visual effect, but placed after shape)
    if (modifiers.border && modifiers.border.enabled) {
      effectModifiers.push(`${indentStr}.border(Color(hex: "${modifiers.border.color || '#CCCCCC'}"), width: ${modifiers.border.width || 1})`);
    }

    // Interaction Modifiers: onTapGesture, animation
    if (modifiers.animation && modifiers.animation.enabled) {
      const anim = modifiers.animation;
      const animationType = anim.type || 'easeInOut';
      const duration = anim.duration || 0.3;
      const value = anim.value || 'nil';
      
      let animationCode = '';
      switch (animationType) {
        case 'spring':
          animationCode = `.animation(.spring(response: ${duration}, dampingFraction: ${anim.dampingFraction || 0.6}), value: ${value})`;
          break;
        case 'linear':
          animationCode = `.animation(.linear(duration: ${duration}), value: ${value})`;
          break;
        case 'easeIn':
          animationCode = `.animation(.easeIn(duration: ${duration}), value: ${value})`;
          break;
        case 'easeOut':
          animationCode = `.animation(.easeOut(duration: ${duration}), value: ${value})`;
          break;
        case 'easeInOut':
        default:
          animationCode = `.animation(.easeInOut(duration: ${duration}), value: ${value})`;
          break;
      }
      interactionModifiers.push(`${indentStr}${animationCode}`);
    }

    if (modifiers.transition && modifiers.transition.enabled) {
      const trans = modifiers.transition;
      const transitionType = trans.type || 'opacity';
      let transitionCode = '';
      switch (transitionType) {
        case 'opacity':
          transitionCode = '.transition(.opacity)';
          break;
        case 'scale':
          transitionCode = '.transition(.scale)';
          break;
        case 'slide':
          transitionCode = '.transition(.slide)';
          break;
        case 'move':
          transitionCode = `.transition(.move(edge: .${trans.edge || 'leading'}))`;
          break;
        default:
          transitionCode = '.transition(.opacity)';
      }
      interactionModifiers.push(`${indentStr}${transitionCode}`);
    }

    // Combine all modifiers in correct order
    const allModifiers = [
      ...layoutModifiers,
      ...visualModifiers,
      ...shapeModifiers,
      ...effectModifiers,
      ...interactionModifiers
    ];

    return allModifiers.join('\n');
  };

  // Individual view generator functions
  const generateHStackCode = (node, indent) => {
    const indentStr = '    '.repeat(indent);
    const { properties = {}, children = [] } = node;
    const alignment = getAlignmentCode(properties.alignment || 'center', 'HStack');
    const spacing = properties.spacing || CARD_DEFAULTS.spacing;
    const childrenCode = children.map(child => generateViewCode(child, indent + 1)).filter(Boolean).join('\n');
    const base = `${indentStr}HStack(alignment: ${alignment}, spacing: ${spacing}) {
${childrenCode || `${indentStr}    // Empty HStack`}
${indentStr}}`;
    const modifiers = generateModifiersCode(properties.modifiers, indent);
    return base + (modifiers ? '\n' + modifiers : '');
  };

  const generateVStackCode = (node, indent) => {
    const indentStr = '    '.repeat(indent);
    const { properties = {}, children = [] } = node;
    const alignment = getAlignmentCode(properties.alignment || 'center', 'VStack');
    const spacing = properties.spacing || CARD_DEFAULTS.spacing;
    const childrenCode = children.map(child => generateViewCode(child, indent + 1)).filter(Boolean).join('\n');
    const base = `${indentStr}VStack(alignment: ${alignment}, spacing: ${spacing}) {
${childrenCode || `${indentStr}    // Empty VStack`}
${indentStr}}`;
    const modifiers = generateModifiersCode(properties.modifiers, indent);
    return base + (modifiers ? '\n' + modifiers : '');
  };

  const generateZStackCode = (node, indent) => {
    const indentStr = '    '.repeat(indent);
    const { properties = {}, children = [] } = node;
    const alignment = getAlignmentCode(properties.alignment || 'center', 'ZStack');
    const childrenCode = children.map(child => generateViewCode(child, indent + 1)).filter(Boolean).join('\n');
    const base = `${indentStr}ZStack(alignment: ${alignment}) {
${childrenCode || `${indentStr}    // Empty ZStack`}
${indentStr}}`;
    const modifiers = generateModifiersCode(properties.modifiers, indent);
    return base + (modifiers ? '\n' + modifiers : '');
  };

  const generateTextCode = (node, indent) => {
    const indentStr = '    '.repeat(indent);
    const { properties = {} } = node;
    const textContent = (properties.content || 'Text').replace(/"/g, '\\"');
    const fontSize = properties.fontSize || CARD_DEFAULTS.defaultFontSize;
    const fontWeight = getFontWeightCode(properties.fontWeight || 'regular');
    const textColor = properties.color || 'primary';
    const textColorCode = getColorCode(textColor);
    const textAlignment = properties.alignment === 'leading' ? '.leading' : 
                         properties.alignment === 'trailing' ? '.trailing' : '.center';
    const accessibilityLabel = properties.accessibilityLabel || textContent;
    let base = `${indentStr}Text("${textContent}")
${indentStr}    .font(.system(size: ${fontSize}, weight: ${fontWeight}))
${indentStr}    .foregroundColor(${textColorCode})
${indentStr}    .multilineTextAlignment(${textAlignment})`;
    if (accessibilityLabel && accessibilityLabel !== textContent) {
      base += `\n${indentStr}    .accessibilityLabel("${accessibilityLabel.replace(/"/g, '\\"')}")`;
    }
    const modifiers = generateModifiersCode(properties.modifiers, indent);
    return base + (modifiers ? '\n' + modifiers : '');
  };

  const generateButtonCode = (node, indent) => {
    const indentStr = '    '.repeat(indent);
    const { properties = {} } = node;
    const buttonTitle = (properties.title || 'Button').replace(/"/g, '\\"');
    const buttonStyle = properties.style || 'bordered';
    const styleCode = buttonStyle === 'plain' ? '.plain' : 
                     buttonStyle === 'borderedProminent' ? '.borderedProminent' : '.bordered';
    const accessibilityLabel = properties.accessibilityLabel || buttonTitle;
    const accessibilityHint = properties.accessibilityHint;
    const buttonId = node.id || 'button';
    const stateVarName = `is${buttonId.charAt(0).toUpperCase() + buttonId.slice(1)}Pressed`;
    let base = `${indentStr}Button(action: {
${indentStr}    withAnimation(.easeInOut(duration: 0.2)) {
${indentStr}        ${stateVarName}.toggle()
${indentStr}    }
${indentStr}    // Add your button action here
${indentStr}}) {
${indentStr}    Text("${buttonTitle}")
${indentStr}}
${indentStr}.buttonStyle(${styleCode})`;
    if (accessibilityLabel && accessibilityLabel !== buttonTitle) {
      base += `\n${indentStr}    .accessibilityLabel("${accessibilityLabel.replace(/"/g, '\\"')}")`;
    }
    base += `\n${indentStr}    .accessibilityAddTraits(.isButton)`;
    if (accessibilityHint) {
      base += `\n${indentStr}    .accessibilityHint("${accessibilityHint.replace(/"/g, '\\"')}")`;
    }
    const modifiers = generateModifiersCode(properties.modifiers, indent);
    return base + (modifiers ? '\n' + modifiers : '');
  };

  // Recursive function to generate SwiftUI code from view tree
  const generateViewCode = (node, indent = 0) => {
    if (!node) return '';
    
    const indentStr = '    '.repeat(indent);
    const { type, properties = {}, children = [] } = node;

    switch (type) {
      case 'HStack':
        return generateHStackCode(node, indent);

      case 'VStack':
        return generateVStackCode(node, indent);

      case 'ZStack':
        return generateZStackCode(node, indent);

      case 'Text':
        return generateTextCode(node, indent);

      case 'Button':
        return generateButtonCode(node, indent);

      case 'Image':
        const imageSource = properties.source || 'star.fill';
        const imageSize = properties.size || 24;
        const aspectRatio = properties.aspectRatio || 'fit';
        const resizable = properties.resizable !== false;
        const isSystemImage = !imageSource.includes('/') && !imageSource.includes('.');
        const imageCode = isSystemImage 
          ? `Image(systemName: "${imageSource}")`
          : `Image("${imageSource}")`;
        let imageModifiers = '';
        if (resizable) {
          imageModifiers += `\n${indentStr}    .resizable()`;
          if (aspectRatio === 'fit') {
            imageModifiers += `\n${indentStr}    .aspectRatio(contentMode: .fit)`;
          } else if (aspectRatio === 'fill') {
            imageModifiers += `\n${indentStr}    .aspectRatio(contentMode: .fill)`;
          }
        }
        imageModifiers += `\n${indentStr}    .frame(width: ${imageSize}, height: ${imageSize})`;
        const imageCommonModifiers = generateModifiersCode(properties.modifiers, indent);
        return `${indentStr}${imageCode}${imageModifiers}${imageCommonModifiers ? '\n' + imageCommonModifiers : ''}`;

      case 'Spacer':
        const minLength = properties.minLength || 0;
        const spacerBase = minLength > 0 
          ? `${indentStr}Spacer(minLength: ${minLength})`
          : `${indentStr}Spacer()`;
        const spacerModifiers = generateModifiersCode(properties.modifiers, indent);
        return spacerBase + (spacerModifiers ? '\n' + spacerModifiers : '');

      case 'Divider':
        const dividerColor = properties.color || '#CCCCCC';
        const thickness = properties.thickness || 1;
        const dividerBase = `${indentStr}Divider()
${indentStr}    .background(Color(hex: "${dividerColor}"))
${indentStr}    .frame(height: ${thickness})`;
        const dividerModifiers = generateModifiersCode(properties.modifiers, indent);
        return dividerBase + (dividerModifiers ? '\n' + dividerModifiers : '');

      case 'List':
        const listChildren = children.map(child => generateViewCode(child, indent + 1)).filter(Boolean).join('\n');
        const listBase = `${indentStr}List {
${listChildren || `${indentStr}    // Empty List`}
${indentStr}}`;
        const listModifiers = generateModifiersCode(properties.modifiers, indent);
        return listBase + (listModifiers ? '\n' + listModifiers : '');

      case 'ScrollView':
        const scrollChildren = children.map(child => generateViewCode(child, indent + 1)).filter(Boolean).join('\n');
        const scrollBase = `${indentStr}ScrollView {
${scrollChildren || `${indentStr}    // Empty ScrollView`}
${indentStr}}`;
        const scrollModifiers = generateModifiersCode(properties.modifiers, indent);
        return scrollBase + (scrollModifiers ? '\n' + scrollModifiers : '');

      default:
        return `${indentStr}// Unknown view type: ${type}`;
    }
  };

  // Helper function to convert color to Swift code (supports semantic and hex colors)
  const getColorCode = (color) => {
    if (!color) return 'Color.primary';
    
    // Check if it's a semantic color
    const semanticColors = {
      'primary': 'Color.primary',
      'secondary': 'Color.secondary',
      'accentColor': 'Color.accentColor',
      'white': 'Color.white',
      'black': 'Color.black',
      'clear': 'Color.clear',
      'systemBackground': 'Color(.systemBackground)',
      'secondarySystemBackground': 'Color(.secondarySystemBackground)',
    };
    
    const lowerColor = color.toLowerCase().trim();
    if (semanticColors[lowerColor]) {
      return semanticColors[lowerColor];
    }
    
    // Default to hex color
    return `Color(hex: "${color}")`;
  };

  // Generate background code
  const getBackground = () => {
    if (cardStyle.backgroundType === 'solid') {
      const bgColor = cardStyle.backgroundColor || '#ffffff';
      return getColorCode(bgColor);
    } else if (cardStyle.backgroundType === 'gradient') {
      const colors = cardStyle.gradientColors || ['#F26F29', '#F03224'];
      return `LinearGradient(
        colors: [${getColorCode(colors[0])}, ${getColorCode(colors[1])}],
        startPoint: .topLeading,
        endPoint: .bottomTrailing
    )`;
    }
    return 'Color(.systemBackground)';
  };

  // Helper function to find all buttons in view tree
  const findButtonsInTree = (nodes) => {
    const buttons = [];
    const traverse = (node) => {
      if (node.type === 'Button') {
        buttons.push(node);
      }
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach(traverse);
      }
    };
    nodes.forEach(traverse);
    return buttons;
  };

  // Generate state variables for interactive elements
  const generateStateVariables = () => {
    const buttons = findButtonsInTree(viewTree);
    if (buttons.length === 0) return '';
    
    const stateVars = buttons.map((button, index) => {
      const buttonId = button.id || `button${index}`;
      const varName = `is${buttonId.charAt(0).toUpperCase() + buttonId.slice(1)}Pressed`;
      return `    @State private var ${varName} = false`;
    });
    
    return stateVars.join('\n') + '\n';
  };

  // Generate view tree content
  const generateContentView = () => {
    if (viewTree.length === 0) {
      return '        // Add views to see content here';
    }
    return viewTree.map(node => generateViewCode(node, 2)).join('\n\n');
  };

  // Generate CardStyle struct
  const generateCardStyle = () => {
    const padding = cardStyle.padding || CARD_DEFAULTS.padding;
    const cornerRadius = cardStyle.cornerRadius || CARD_DEFAULTS.cornerRadius;
    const shadowRadius = cardStyle.shadowRadius || CARD_DEFAULTS.shadowRadius;
    const shadowOpacity = cardStyle.shadowOpacity || CARD_DEFAULTS.shadowOpacity;
    const backgroundColor = getBackground();
    
    return `
// MARK: - Card Style Configuration
/// Configuration for card appearance and styling
struct CardStyle {
    let padding: CGFloat
    let cornerRadius: CGFloat
    let backgroundColor: Color
    let shadowRadius: CGFloat
    let shadowOpacity: Double
    
    static let \`default\` = CardStyle(
        padding: ${padding},
        cornerRadius: ${cornerRadius},
        backgroundColor: ${backgroundColor},
        shadowRadius: ${shadowRadius},
        shadowOpacity: ${shadowOpacity}
    )
}`;
  };

  // Generate CardStyleModifier
  const generateCardStyleModifier = () => {
    return `
// MARK: - Card Style Modifier
/// View modifier for applying card styles
struct CardStyleModifier: ViewModifier {
    let style: CardStyle
    
    func body(content: Content) -> some View {
        content
            .padding(style.padding)
            .background(style.backgroundColor)
            .clipShape(RoundedRectangle(cornerRadius: style.cornerRadius))
            .shadow(
                color: Color.black.opacity(style.shadowOpacity),
                radius: style.shadowRadius,
                x: 0,
                y: style.shadowRadius / 2
            )
    }
}`;
  };

  const cardCode = `
// MARK: - Card View
/// A reusable card view component with customizable styling and content.
///
/// This view provides a consistent card appearance with support for:
/// - Custom padding and corner radius
/// - Background colors and gradients
/// - Shadow effects
/// - Dark mode support
/// - Accessibility features
///
/// ## Usage
/// \`\`\`swift
/// CardView(style: .default) {
///     VStack(spacing: 12) {
///         Text("Card Title")
///             .font(.headline)
///         Text("Card Content")
///             .font(.body)
///             .foregroundColor(.secondary)
///     }
/// }
/// \`\`\`
struct CardView<Content: View>: View {
    /// The card styling configuration
    let style: CardStyle
    
    /// The card content built using a view builder
    @ViewBuilder let content: () -> Content
${generateStateVariables()}    var body: some View {
        content()
            .modifier(CardStyleModifier(style: style))
    }
}`;

  // Generate different code based on mode
  if (mode === 'fullUI') {
    // Full UI mode - generate full-screen ContentView
    const fullUIUsageExample = `
// MARK: - Usage Example
// Full Screen SwiftUI View
struct ContentView: View {
${generateStateVariables()}    var body: some View {
${generateContentView()}
    }
}`;

    const fullUIPreviewCode = `
// MARK: - Preview
#if swift(>=5.9)
#Preview {
    ContentView()
}

#Preview("Dark Mode") {
    ContentView()
        .preferredColorScheme(.dark)
}
#else
struct ContentView_Previews: PreviewProvider {
    static var previews: some View {
        ContentView()
        
        ContentView()
            .preferredColorScheme(.dark)
            .previewDisplayName("Dark Mode")
    }
}
#endif
`;

    const helperExtensionCode = `
// MARK: - Helper Extensions
/// Extension to initialize Color from hex string
///
/// Supports 3-digit (RGB), 6-digit (RGB), and 8-digit (ARGB) hex formats.
/// - Parameter hex: Hex color string (e.g., "#FF0000" or "FF0000")
extension Color {
    init(hex: String) {
        let hex = hex.trimmingCharacters(in: CharacterSet.alphanumerics.inverted)
        var int: UInt64 = 0
        Scanner(string: hex).scanHexInt64(&int)
        let a, r, g, b: UInt64
        switch hex.count {
        case 3: // RGB (12-bit)
            (a, r, g, b) = (255, (int >> 8) * 17, (int >> 4 & 0xF) * 17, (int & 0xF) * 17)
        case 6: // RGB (24-bit)
            (a, r, g, b) = (255, int >> 16, int >> 8 & 0xFF, int & 0xFF)
        case 8: // ARGB (32-bit)
            (a, r, g, b) = (int >> 24, int >> 16 & 0xFF, int >> 8 & 0xFF, int & 0xFF)
        default:
            (a, r, g, b) = (255, 0, 0, 0)
        }
        self.init(
            .sRGB,
            red: Double(r) / 255,
            green: Double(g) / 255,
            blue: Double(b) / 255,
            opacity: Double(a) / 255
        )
    }
}
`;

    return {
      blocks: [
        {
          name: 'ContentView',
          code: fullUIUsageExample,
          requiresImport: true,
          order: 1
        },
        {
          name: 'Preview',
          code: fullUIPreviewCode,
          requiresImport: false,
          order: 2,
          optional: true
        },
        {
          name: 'Color Extension',
          code: helperExtensionCode,
          requiresImport: false,
          order: 3,
          optional: true
        }
      ],
      importStatement: 'import SwiftUI'
    };
  }

  // Card mode - existing card generation code
  const usageExample = `
// MARK: - Usage Example
// Usage Example:
struct ContentView: View {
    var body: some View {
        VStack {
            CardView(style: .default) {
${generateContentView()}
            }
        }
        .padding()
    }
}`;

  const previewCode = `
// MARK: - Preview
#if swift(>=5.9)
#Preview {
    CardView(style: .default) {
${generateContentView()}
    }
    .padding()
}

#Preview("Dark Mode") {
    CardView(style: .default) {
${generateContentView()}
    }
    .padding()
    .preferredColorScheme(.dark)
}
#else
struct CardView_Previews: PreviewProvider {
    static var previews: some View {
        CardView(style: .default) {
${generateContentView()}
        }
        .padding()
        
        CardView(style: .default) {
${generateContentView()}
        }
        .padding()
        .preferredColorScheme(.dark)
        .previewDisplayName("Dark Mode")
    }
}
#endif
`;

  const helperExtensionCode = `
// MARK: - Helper Extensions
/// Extension to initialize Color from hex string
///
/// Supports 3-digit (RGB), 6-digit (RGB), and 8-digit (ARGB) hex formats.
/// - Parameter hex: Hex color string (e.g., "#FF0000" or "FF0000")
extension Color {
    init(hex: String) {
        let hex = hex.trimmingCharacters(in: CharacterSet.alphanumerics.inverted)
        var int: UInt64 = 0
        Scanner(string: hex).scanHexInt64(&int)
        let a, r, g, b: UInt64
        switch hex.count {
        case 3: // RGB (12-bit)
            (a, r, g, b) = (255, (int >> 8) * 17, (int >> 4 & 0xF) * 17, (int & 0xF) * 17)
        case 6: // RGB (24-bit)
            (a, r, g, b) = (255, int >> 16, int >> 8 & 0xFF, int & 0xFF)
        case 8: // ARGB (32-bit)
            (a, r, g, b) = (int >> 24, int >> 16 & 0xFF, int >> 8 & 0xFF, int & 0xFF)
        default:
            (a, r, g, b) = (255, 0, 0, 0)
        }
        self.init(
            .sRGB,
            red: Double(r) / 255,
            green: Double(g) / 255,
            blue: Double(b) / 255,
            opacity: Double(a) / 255
        )
    }
}
`;

  return {
    blocks: [
      {
        name: 'Usage Example',
        code: usageExample,
        requiresImport: true,
        order: 1
      },
      {
        name: 'Card Style Configuration',
        code: generateCardStyle(),
        requiresImport: false,
        order: 2
      },
      {
        name: 'Card Style Modifier',
        code: generateCardStyleModifier(),
        requiresImport: false,
        order: 3
      },
      {
        name: 'Card View',
        code: cardCode,
        requiresImport: false,
        order: 4
      },
      {
        name: 'Preview',
        code: previewCode,
        requiresImport: false,
        order: 5,
        optional: true
      },
      {
        name: 'Color Extension',
        code: helperExtensionCode,
        requiresImport: false,
        order: 6,
        optional: true
      }
    ],
    importStatement: 'import SwiftUI'
  };
};

/**
 * Generate SwiftUI Animation code
 */
export const generateAnimationCode = (config) => {
  const {
    type,
    duration,
    delay,
    curve,
    direction,
    stagger,
    repeat,
    repeatCount,
    applyTo,
    springResponse,
    springDampingFraction,
  } = config;

  // Helper function to get proper SwiftUI animation syntax
  const getAnimationType = () => {
    let animationStr;
    
    if (curve === 'spring') {
      const response = springResponse !== undefined ? springResponse : duration;
      const damping = springDampingFraction !== undefined ? springDampingFraction : 0.6;
      animationStr = `.spring(response: ${response}, dampingFraction: ${damping})`;
    } else {
      // Map curve names to SwiftUI animation syntax
      const curveMap = {
        easeInOut: '.easeInOut',
        easeIn: '.easeIn',
        easeOut: '.easeOut',
        linear: '.linear',
      };
      const curveName = curveMap[curve] || '.easeInOut';
      animationStr = `${curveName}(duration: ${duration})`;
    }

    // Add repeat modifiers if needed
    if (repeat && repeatCount === 0) {
      // Infinite repeat
      return `Animation${animationStr}.repeatForever(autoreverses: true)`;
    } else if (repeat && repeatCount > 0) {
      // Limited repeat count
      return `Animation${animationStr}.repeatCount(${repeatCount})`;
    } else {
      // No repeat
      return `Animation${animationStr}`;
    }
  };

  const getAnimationModifier = () => {
    const animationType = getAnimationType();

    switch (type) {
      case 'fadeIn':
        return `.opacity(isVisible ? 1 : 0)
            .animation(${animationType}.delay(${delay}), value: isVisible)`;

      case 'slideIn':
        const slideOffset = direction === 'up' ? 'y: isVisible ? 0 : 50'
          : direction === 'down' ? 'y: isVisible ? 0 : -50'
          : direction === 'left' ? 'x: isVisible ? 0 : 50'
          : 'x: isVisible ? 0 : -50';
        return `.offset(${slideOffset})
            .opacity(isVisible ? 1 : 0)
            .animation(${animationType}.delay(${delay}), value: isVisible)`;

      case 'bounce':
        return `.scaleEffect(isVisible ? 1 : 0.8)
            .opacity(isVisible ? 1 : 0)
            .animation(${animationType}.delay(${delay}), value: isVisible)`;

      case 'scale':
        return `.scaleEffect(isVisible ? 1 : 0)
            .opacity(isVisible ? 1 : 0)
            .animation(${animationType}.delay(${delay}), value: isVisible)`;

      case 'shimmer':
        return `.modifier(ShimmerModifier())`;

      case 'staggered':
        return `.modifier(StaggeredAnimationModifier(
            delay: ${delay},
            stagger: ${stagger},
            direction: .${direction}
        ))`;

      default:
        return `.opacity(isVisible ? 1 : 0)
            .animation(${animationType}.delay(${delay}), value: isVisible)`;
    }
  };

  const getShimmerModifier = () => {
    if (type !== 'shimmer') return '';
    return `
struct ShimmerModifier: ViewModifier {
    @State private var phase: CGFloat = 0
    
    func body(content: Content) -> some View {
        content
            .overlay(
                LinearGradient(
                    gradient: Gradient(colors: [
                        Color.white.opacity(0),
                        Color.white.opacity(0.3),
                        Color.white.opacity(0)
                    ]),
                    startPoint: .leading,
                    endPoint: .trailing
                )
                .offset(x: phase)
                .animation(
                    Animation.linear(duration: ${duration})
                        .repeatForever(autoreverses: false),
                    value: phase
                )
            )
            .onAppear {
                phase = 200
            }
    }
}`;
  };

  const getStaggeredModifier = () => {
    if (type !== 'staggered') return '';
    return `
struct StaggeredAnimationModifier: ViewModifier {
    let baseDelay: Double
    let stagger: Double
    let index: Int
    let direction: StaggerDirection
    @State private var isVisible = false
    
    enum StaggerDirection {
        case up, down, left, right
    }
    
    func body(content: Content) -> some View {
        content
            .offset(
                x: direction == .left ? (isVisible ? 0 : 50) : direction == .right ? (isVisible ? 0 : -50) : 0,
                y: direction == .up ? (isVisible ? 0 : 50) : direction == .down ? (isVisible ? 0 : -50) : 0
            )
            .opacity(isVisible ? 1 : 0)
            .onAppear {
                let itemDelay = baseDelay + Double(index) * stagger
                DispatchQueue.main.asyncAfter(deadline: .now() + itemDelay) {
                    withAnimation(.easeOut(duration: 0.5)) {
                        isVisible = true
                    }
                }
            }
    }
}`;
  };

  const getViewExample = () => {
    if (applyTo === 'list' || type === 'staggered') {
      return `
struct AnimatedListView: View {
    @State private var items = ["Item 1", "Item 2", "Item 3", "Item 4", "Item 5"]
    @State private var isVisible = false
    
    var body: some View {
        List {
            ForEach(Array(items.enumerated()), id: \\.offset) { index, item in
                Text(item)
                    .padding()
                    ${type === 'staggered' ? `.modifier(StaggeredAnimationModifier(
                        baseDelay: ${delay},
                        stagger: ${stagger},
                        index: index,
                        direction: .${direction}
                    ))` : getAnimationModifier().replace('isVisible', `isVisible && index == 0`)}
            }
        }
        .onAppear {
            isVisible = true
        }
    }
}`;
    }

    if (applyTo === 'grid') {
      return `
struct AnimatedGridView: View {
    @State private var items = Array(1...6)
    @State private var isVisible = false
    
    let columns = [GridItem(.flexible()), GridItem(.flexible())]
    
    var body: some View {
        ScrollView {
            LazyVGrid(columns: columns, spacing: 16) {
                ForEach(Array(items.enumerated()), id: \\.offset) { index, item in
                    Text("Item \\(item)")
                        .frame(height: 100)
                        .frame(maxWidth: .infinity)
                        .background(Color.blue.opacity(0.2))
                        .cornerRadius(12)
                        ${getAnimationModifier()}
                }
            }
            .padding()
        }
        .onAppear {
            isVisible = true
        }
    }
}`;
    }

    return `
struct AnimatedView: View {
    @State private var isVisible = false
    
    var body: some View {
        VStack {
            Text("Animated Content")
                .font(.title)
                .padding()
                ${getAnimationModifier()}
        }
        .onAppear {
            isVisible = true
        }
    }
}`;
  };

  const usageExample = getViewExample();
  const helperModifiers = getShimmerModifier() + '\n' + getStaggeredModifier();
  
  // Generate reusable animation extension based on type
  const getAnimationExtension = () => {
    if (type === 'shimmer' || type === 'staggered') {
      // These types use custom modifiers, no extension needed
      return '';
    }
    
    let extensionParams = '';
    let animationCall = '';
    
    if (curve === 'spring') {
      const response = springResponse !== undefined ? springResponse : duration;
      const damping = springDampingFraction !== undefined ? springDampingFraction : 0.6;
      extensionParams = `response: Double = ${response}, dampingFraction: Double = ${damping}, delay: Double = ${delay}`;
      animationCall = `Animation.spring(response: response, dampingFraction: dampingFraction)`;
    } else {
      extensionParams = `duration: Double = ${duration}, delay: Double = ${delay}`;
      const curveMap = {
        easeInOut: '.easeInOut',
        easeIn: '.easeIn',
        easeOut: '.easeOut',
        linear: '.linear',
      };
      const curveName = curveMap[curve] || '.easeInOut';
      animationCall = `Animation${curveName}(duration: duration)`;
    }
    
    // Generate modifier code for extension
    // Create a simplified version that works with @State binding
    const animationVar = 'animation';
    let modifierBody = '';
    
    // Build modifier based on animation type
    switch (type) {
      case 'fadeIn':
        modifierBody = `.opacity(1)\n            .animation(${animationVar}.delay(delay), value: UUID())`;
        break;
      case 'slideIn':
        const slideOffsetExt = direction === 'up' ? 'y: 0'
          : direction === 'down' ? 'y: 0'
          : direction === 'left' ? 'x: 0'
          : 'x: 0';
        modifierBody = `.offset(${slideOffsetExt})\n            .opacity(1)\n            .animation(${animationVar}.delay(delay), value: UUID())`;
        break;
      case 'bounce':
        modifierBody = `.scaleEffect(1)\n            .opacity(1)\n            .animation(${animationVar}.delay(delay), value: UUID())`;
        break;
      case 'scale':
        modifierBody = `.scaleEffect(1)\n            .opacity(1)\n            .animation(${animationVar}.delay(delay), value: UUID())`;
        break;
      default:
        modifierBody = `.opacity(1)\n            .animation(${animationVar}.delay(delay), value: UUID())`;
    }
    
    return `
// MARK: - Reusable Animation Extension
// Usage: YourView().animate${type.charAt(0).toUpperCase() + type.slice(1)}(duration: 0.5, delay: 0)
extension View {
    func animate${type.charAt(0).toUpperCase() + type.slice(1)}(
        ${extensionParams}
    ) -> some View {
        let ${animationVar} = ${animationCall};
        self${modifierBody}
    }
}`;
  };
  
  return `
import SwiftUI

// MARK: - Usage Example
${usageExample}

${getAnimationExtension()}

// MARK: - Helper Modifiers
${helperModifiers.trim()}
`;
};

/**
 * Generate SwiftUI Alert code
 */
export const generateAlertCode = (config) => {
  // Extract all config properties with defaults
  const type = config.type || 'success';
  const title = config.title || 'Success';
  const message = config.message || 'Operation completed successfully';
  const buttonCount = config.buttonCount || 1;
  const primaryButtonText = config.primaryButtonText || 'OK';
  const secondaryButtonText = config.secondaryButtonText || 'Cancel';
  const tertiaryButtonText = config.tertiaryButtonText || 'Delete';
  const cornerRadius = config.cornerRadius || 12;
  const blurBackground = config.blurBackground !== false;
  const blurIntensity = config.blurIntensity || 10;
  const animationType = config.animationType || 'fade';
  const animationDuration = config.animationDuration || 0.3;
  const backgroundColor = config.backgroundColor || '#ffffff';
  const titleColor = config.titleColor || '#000000';
  const messageColor = config.messageColor || '#666666';
  const customTypeColor = config.customTypeColor;
  const customIcon = config.customIcon;
  const showIcon = config.showIcon !== false;
  const dismissible = config.dismissible !== false;
  const autoDismiss = config.autoDismiss === true;
  const autoDismissDelay = config.autoDismissDelay || 3;
  const primaryButtonStyle = config.primaryButtonStyle || 'borderedProminent';
  const secondaryButtonStyle = config.secondaryButtonStyle || 'bordered';
  const tertiaryButtonStyle = config.tertiaryButtonStyle || 'bordered';
  const primaryButtonColor = config.primaryButtonColor;
  const shadowEnabled = config.shadowEnabled !== false;
  const shadowRadius = config.shadowRadius || 20;
  const shadowOpacity = config.shadowOpacity ?? 0.1;
  const padding = config.padding || 24;

  // Helper function to get effective color (custom or type default)
  const getEffectiveColor = () => {
    if (type === 'custom' && customTypeColor) {
      return customTypeColor;
    }
    const typeColors = {
      success: '#10b981',
      error: '#ef4444',
      warning: '#f59e0b',
      info: '#3b82f6',
    };
    return typeColors[type] || '#6b7280';
  };

  // Helper function to get effective icon (custom or type default)
  const getEffectiveIcon = () => {
    if (customIcon) {
      return customIcon;
    }
    const typeIcons = {
      success: '✓',
      error: '✕',
      warning: '⚠',
      info: 'ℹ',
    };
    return typeIcons[type] || '•';
  };

  // Helper function to map button style to SwiftUI style
  const getButtonStyle = (style) => {
    const styleMap = {
      borderedProminent: '.borderedProminent',
      bordered: '.bordered',
      plain: '.plain',
      borderless: '.borderless',
    };
    return styleMap[style] || '.borderedProminent';
  };

  // Get effective button color
  const effectiveButtonColor = primaryButtonColor || getEffectiveColor();
  const effectiveIcon = getEffectiveIcon();

  // Generate buttons with dynamic styles
  const getButtons = () => {
    const buttons = [];
    if (buttonCount >= 1) {
      const buttonStyle = getButtonStyle(primaryButtonStyle);
      buttons.push(`Button("${primaryButtonText}") {
                action()
                isPresented = false
            }
            .buttonStyle(${buttonStyle})
            .tint(Color(hex: "${effectiveButtonColor}"))`);
    }
    if (buttonCount >= 2) {
      const buttonStyle = getButtonStyle(secondaryButtonStyle);
      buttons.push(`Button("${secondaryButtonText}") {
                secondaryAction?()
                isPresented = false
            }
            .buttonStyle(${buttonStyle})`);
    }
    if (buttonCount >= 3) {
      const buttonStyle = getButtonStyle(tertiaryButtonStyle);
      buttons.push(`Button("${tertiaryButtonText}", role: .destructive) {
                tertiaryAction?()
                isPresented = false
            }
            .buttonStyle(${buttonStyle})`);
    }
    return buttons.join('\n                        ');
  };

  // Get animation modifier with duration support
  const getAnimationModifier = () => {
    if (animationType === 'none') return '';
    
    const duration = animationDuration;
    let transition = '';
    let animation = '';
    
    switch (animationType) {
      case 'fade':
        transition = '.transition(.opacity)';
        animation = `.animation(.easeOut(duration: ${duration}), value: isPresented)`;
        break;
      case 'slide':
        transition = '.transition(.move(edge: .top).combined(with: .opacity))';
        animation = `.animation(.easeOut(duration: ${duration}), value: isPresented)`;
        break;
      case 'scale':
        transition = '.transition(.scale.combined(with: .opacity))';
        animation = `.animation(.easeOut(duration: ${duration}), value: isPresented)`;
        break;
      case 'bounce':
        transition = '.transition(.scale.combined(with: .opacity))';
        animation = `.animation(.spring(response: ${duration}, dampingFraction: 0.6), value: isPresented)`;
        break;
      default:
        transition = '.transition(.opacity)';
        animation = `.animation(.easeOut(duration: ${duration}), value: isPresented)`;
    }
    
    return `${transition}\n                ${animation}`;
  };

  // Get shadow modifier
  const getShadowModifier = () => {
    if (!shadowEnabled) return '';
    return `.shadow(color: Color.black.opacity(${shadowOpacity}), radius: ${shadowRadius}, x: 0, y: 10)`;
  };

  const usageExample = `
// MARK: - Usage Example
struct ContentView: View {
    @State private var showAlert = false
    
    var body: some View {
        VStack {
            Button("Show Alert") {
                showAlert = true
            }
        }
        .customAlert(
            isPresented: $showAlert,
            title: "${title}",
            message: "${message}",
            action: {
                print("Primary action")
            },
            secondaryAction: ${buttonCount >= 2 ? `{
                print("Secondary action")
            }` : 'nil'},
            tertiaryAction: ${buttonCount >= 3 ? `{
                print("Tertiary action")
            }` : 'nil'},
            autoDismiss: ${autoDismiss},
            autoDismissDelay: ${autoDismissDelay}
        )
    }
}
`;

  const alertComponentCode = `// MARK: - Main Component
struct CustomAlert: View {
    @Binding var isPresented: Bool
    let title: String
    let message: String
    var action: () -> Void = {}
    var secondaryAction: (() -> Void)? = nil
    var tertiaryAction: (() -> Void)? = nil
    var autoDismiss: Bool = false
    var autoDismissDelay: Double = 3.0
    var dismissible: Bool = true
    
    @State private var dismissTimer: Timer?
    
    var body: some View {
        ZStack {
            ${blurBackground ? `Color.black.opacity(0.3)
                .ignoresSafeArea()
                .blur(radius: ${blurIntensity})
                ${dismissible ? `.onTapGesture {
                    if dismissible {
                        isPresented = false
                    }
                }` : ''}` : 'Color.clear'}
            
            if isPresented {
                VStack(spacing: 0) {
                    // Icon and Title
                    HStack(spacing: 12) {
                        ${showIcon ? `Circle()
                            .fill(Color(hex: "${getEffectiveColor()}").opacity(0.2))
                            .frame(width: 40, height: 40)
                            .overlay(
                                Text("${effectiveIcon}")
                                    .font(.system(size: 20, weight: .bold))
                                    .foregroundColor(Color(hex: "${getEffectiveColor()}"))
                            )` : ''}
                        
                        Text(title)
                            .font(.system(size: 20, weight: .bold))
                            .foregroundColor(Color(hex: "${titleColor}"))
                    }
                    .frame(maxWidth: .infinity, alignment: .leading)
                    .padding(.horizontal, ${padding})
                    .padding(.top, ${padding})
                    
                    // Message
                    Text(message)
                        .font(.system(size: 14))
                        .foregroundColor(Color(hex: "${messageColor}"))
                        .frame(maxWidth: .infinity, alignment: .leading)
                        .padding(.horizontal, ${padding})
                        .padding(.top, 12)
                    
                    // Buttons
                    HStack(spacing: 8) {
                        ${getButtons()}
                    }
                    .padding(.horizontal, ${padding})
                    .padding(.top, 20)
                    .padding(.bottom, ${padding})
                }
                .frame(maxWidth: 400)
                .background(Color(hex: "${backgroundColor}"))
                .cornerRadius(${cornerRadius})
                ${getShadowModifier()}
                ${getAnimationModifier()}
                .onAppear {
                    if autoDismiss {
                        dismissTimer = Timer.scheduledTimer(withTimeInterval: autoDismissDelay, repeats: false) { _ in
                            isPresented = false
                        }
                    }
                }
                .onDisappear {
                    dismissTimer?.invalidate()
                }
            }
        }
    }
}`;

  const modifierCode = `// MARK: - ViewModifier
struct CustomAlertModifier: ViewModifier {
    @Binding var isPresented: Bool
    let title: String
    let message: String
    var action: () -> Void = {}
    var secondaryAction: (() -> Void)? = nil
    var tertiaryAction: (() -> Void)? = nil
    var autoDismiss: Bool = false
    var autoDismissDelay: Double = 3.0
    var dismissible: Bool = true
    
    func body(content: Content) -> some View {
        ZStack {
            content
            CustomAlert(
                isPresented: $isPresented,
                title: title,
                message: message,
                action: action,
                secondaryAction: secondaryAction,
                tertiaryAction: tertiaryAction,
                autoDismiss: autoDismiss,
                autoDismissDelay: autoDismissDelay,
                dismissible: dismissible
            )
        }
    }
}

// MARK: - View Extension
extension View {
    func customAlert(
        isPresented: Binding<Bool>,
        title: String,
        message: String,
        action: @escaping () -> Void = {},
        secondaryAction: (() -> Void)? = nil,
        tertiaryAction: (() -> Void)? = nil,
        autoDismiss: Bool = false,
        autoDismissDelay: Double = 3.0,
        dismissible: Bool = true
    ) -> some View {
        self.modifier(CustomAlertModifier(
            isPresented: isPresented,
            title: title,
            message: message,
            action: action,
            secondaryAction: secondaryAction,
            tertiaryAction: tertiaryAction,
            autoDismiss: autoDismiss,
            autoDismissDelay: autoDismissDelay,
            dismissible: dismissible
        ))
    }
}`;

  const helperExtensionCode = `// MARK: - Helper Extensions
extension Color {
    init(hex: String) {
        let hex = hex.trimmingCharacters(in: CharacterSet.alphanumerics.inverted)
        var int: UInt64 = 0
        Scanner(string: hex).scanHexInt64(&int)
        let a, r, g, b: UInt64
        switch hex.count {
        case 3: // RGB (12-bit)
            (a, r, g, b) = (255, (int >> 8) * 17, (int >> 4 & 0xF) * 17, (int & 0xF) * 17)
        case 6: // RGB (24-bit)
            (a, r, g, b) = (255, int >> 16, int >> 8 & 0xFF, int & 0xFF)
        case 8: // ARGB (32-bit)
            (a, r, g, b) = (int >> 24, int >> 16 & 0xFF, int >> 8 & 0xFF, int & 0xFF)
        default:
            (a, r, g, b) = (255, 0, 0, 0)
        }
        self.init(
            .sRGB,
            red: Double(r) / 255,
            green: Double(g) / 255,
            blue: Double(b) / 255,
            opacity: Double(a) / 255
        )
    }
}`;

  return {
    blocks: [
      {
        name: 'Usage Example',
        code: usageExample,
        requiresImport: true,
        order: 1
      },
      {
        name: 'Custom Alert Component',
        code: alertComponentCode,
        requiresImport: false,
        order: 2
      },
      {
        name: 'Alert Modifier',
        code: modifierCode,
        requiresImport: false,
        order: 3
      },
      {
        name: 'Color Extension',
        code: helperExtensionCode,
        requiresImport: false,
        order: 4,
        optional: true
      }
    ],
    importStatement: 'import SwiftUI'
  };
};

/**
 * Generate SwiftUI Unified Media Picker code
 * Comprehensive plug-and-play media picker system with document, photo, video, and cropping support
 */
export const generatePhotoPickerCode = (config) => {
  const {
    selectionMode = 'single',
    cropShape = 'square',
    cropEnabled = false,
    maxSelectionCount = 5,
    allowsEditing = false,
    filterType = 'images',
    enableDocumentPicker = false,
    enableCamera = true,
    enableGallery = true,
    allowVideos = false,
  } = config;

  const allowMultipleSelection = selectionMode === 'multiple';
  const allowsVideos = filterType === 'videos' || filterType === 'all' || allowVideos;

  // Generate MediaPickerConfig
  const configCode = `// MARK: - Media Picker Configuration
struct MediaPickerConfig {
    // Feature toggles
    var enableDocumentPicker: Bool = ${enableDocumentPicker}
    var enablePhotoPicker: Bool = true
    var enableCamera: Bool = ${enableCamera}
    var enableGallery: Bool = ${enableGallery}
    var allowVideos: Bool = ${allowsVideos}
    var allowMultipleSelection: Bool = ${allowMultipleSelection}
    var enableCropping: Bool = ${cropEnabled}
    
    // Limits
    var maxSelectionCount: Int? = ${allowMultipleSelection ? maxSelectionCount : 'nil'}
    
    // Cropping options
    var cropAspectRatio: CropAspectRatio? = ${cropEnabled ? `.${cropShape}` : 'nil'}
}

// MARK: - Crop Aspect Ratio
enum CropAspectRatio {
    case freeform
    case square
    case circle
    case portrait
    case landscape
    case wide
    case tall
    
    var ratio: CGSize? {
        switch self {
        case .freeform: return nil
        case .square, .circle: return CGSize(width: 1, height: 1)
        case .portrait: return CGSize(width: 3, height: 4)
        case .landscape: return CGSize(width: 4, height: 3)
        case .wide: return CGSize(width: 16, height: 9)
        case .tall: return CGSize(width: 9, height: 16)
        }
    }
}`;

  // Generate MediaPickerResult
  const resultCode = `// MARK: - Media Picker Result
enum MediaPickerResult {
    case documents([URL])
    case images([UIImage])
    case videos([URL])
    case mixed([MediaItem])
}

struct MediaItem {
    let image: UIImage?
    let videoURL: URL?
    let fileURL: URL?
    
    var type: MediaType {
        if image != nil { return .image }
        if videoURL != nil { return .video }
        return .unknown
    }
}

enum MediaType {
    case image, video, unknown
}

extension MediaPickerResult {
    var images: [UIImage] {
        switch self {
        case .images(let imgs): return imgs
        case .mixed(let items): return items.compactMap { $0.image }
        default: return []
        }
    }
    
    var videoURLs: [URL] {
        switch self {
        case .videos(let urls): return urls
        case .mixed(let items): return items.compactMap { $0.videoURL }
        default: return []
        }
    }
    
    var firstImage: UIImage? { images.first }
    var firstVideoURL: URL? { videoURLs.first }
    var hasImages: Bool { !images.isEmpty }
    var hasVideos: Bool { !videoURLs.isEmpty }
}`;

  // Generate Document Picker
  const documentPickerCode = enableDocumentPicker ? `// MARK: - Document Picker
struct DocumentPicker: UIViewControllerRepresentable {
    var contentTypes: [UTType] = [.item]
    var allowsMultipleSelection: Bool = false
    var asCopy: Bool = true
    var documentDidPick: (([URL]) -> Void)?
    var onCancel: (() -> Void)?
    
    func makeCoordinator() -> Coordinator {
        Coordinator(self)
    }
    
    func makeUIViewController(context: Context) -> UIDocumentPickerViewController {
        let picker = UIDocumentPickerViewController(forOpeningContentTypes: contentTypes, asCopy: asCopy)
        picker.delegate = context.coordinator
        picker.allowsMultipleSelection = allowsMultipleSelection
        return picker
    }
    
    func updateUIViewController(_ uiViewController: UIDocumentPickerViewController, context: Context) {}
    
    class Coordinator: NSObject, UIDocumentPickerDelegate {
        let parent: DocumentPicker
        init(_ parent: DocumentPicker) {
            self.parent = parent
        }
        
        func documentPicker(_ controller: UIDocumentPickerViewController, didPickDocumentsAt urls: [URL]) {
            for url in urls {
                _ = url.startAccessingSecurityScopedResource()
            }
            parent.documentDidPick?(urls)
        }
        
        func documentPickerWasCancelled(_ controller: UIDocumentPickerViewController) {
            parent.onCancel?()
        }
    }
}` : '';

  // Generate Camera Picker
  const cameraPickerCode = enableCamera ? `// MARK: - Camera Picker
struct CameraPicker: UIViewControllerRepresentable {
    var allowsVideo: Bool = ${allowsVideos}
    var allowsEditing: Bool = ${allowsEditing}
    var onImagePicked: ([UIImage], [URL]?) -> Void
    var onVideoPicked: ((URL) -> Void)?
    var onCancel: (() -> Void)?
    @Environment(\\.presentationMode) private var presentationMode
    
    func makeCoordinator() -> Coordinator {
        Coordinator(parent: self)
    }
    
    func makeUIViewController(context: Context) -> UIImagePickerController {
        let picker = UIImagePickerController()
        picker.delegate = context.coordinator
        picker.sourceType = .camera
        picker.mediaTypes = allowsVideo ? ["public.image", "public.movie"] : ["public.image"]
        picker.allowsEditing = allowsEditing
        return picker
    }
    
    func updateUIViewController(_ uiViewController: UIImagePickerController, context: Context) {}
    
    class Coordinator: NSObject, UINavigationControllerDelegate, UIImagePickerControllerDelegate {
        let parent: CameraPicker
        
        init(parent: CameraPicker) {
            self.parent = parent
        }
        
        func imagePickerController(_ picker: UIImagePickerController, didFinishPickingMediaWithInfo info: [UIImagePickerController.InfoKey: Any]) {
            if let videoURL = info[.mediaURL] as? URL {
                parent.onVideoPicked?(videoURL)
            } else if let image = info[.editedImage] as? UIImage ?? info[.originalImage] as? UIImage {
                if let url = saveImage(image) {
                    parent.onImagePicked([image], [url])
                } else {
                    parent.onImagePicked([image], nil)
                }
            }
            parent.presentationMode.wrappedValue.dismiss()
        }
        
        func imagePickerControllerDidCancel(_ picker: UIImagePickerController) {
            parent.onCancel?()
            parent.presentationMode.wrappedValue.dismiss()
        }
        
        private func saveImage(_ image: UIImage) -> URL? {
            guard let data = image.jpegData(compressionQuality: 0.8) else { return nil }
            let url = FileManager.default.temporaryDirectory.appendingPathComponent("IMG_\\(Date().timeIntervalSince1970).jpg")
            try? data.write(to: url)
            return url
        }
    }
}` : '';

  // Generate Gallery Picker
  const galleryPickerCode = enableGallery ? `// MARK: - Gallery Picker
struct ImagePicker: UIViewControllerRepresentable {
    var allowsMultipleSelection: Bool = ${allowMultipleSelection}
    var maxSelectionCount: Int? = ${allowMultipleSelection ? maxSelectionCount : 'nil'}
    var allowsVideos: Bool = ${allowsVideos}
    var onImagePicked: ([UIImage], [URL]?) -> Void
    var onVideoPicked: (([URL]) -> Void)?
    var onMediaPicked: (([UIImage], [URL], [URL]) -> Void)?
    @Environment(\\.presentationMode) private var presentationMode
    
    func makeCoordinator() -> Coordinator {
        Coordinator(parent: self)
    }
    
    func makeUIViewController(context: Context) -> PHPickerViewController {
        var config = PHPickerConfiguration(photoLibrary: .shared())
        config.filter = allowsVideos ? .any(of: [.images, .videos]) : .images
        config.selectionLimit = allowsMultipleSelection ? (maxSelectionCount ?? 0) : 1
        config.selection = .ordered
        let picker = PHPickerViewController(configuration: config)
        picker.delegate = context.coordinator
        return picker
    }
    
    func updateUIViewController(_ uiViewController: PHPickerViewController, context: Context) {}
    
    class Coordinator: NSObject, PHPickerViewControllerDelegate {
        let parent: ImagePicker
        
        init(parent: ImagePicker) {
            self.parent = parent
        }
        
        func picker(_ picker: PHPickerViewController, didFinishPicking results: [PHPickerResult]) {
            guard !results.isEmpty else {
                parent.presentationMode.wrappedValue.dismiss()
                return
            }
            
            var images: [UIImage] = []
            var imageURLs: [URL] = []
            var videoURLs: [URL] = []
            let group = DispatchGroup()
            
            for result in results {
                group.enter()
                if result.itemProvider.canLoadObject(ofClass: UIImage.self) {
                    result.itemProvider.loadObject(ofClass: UIImage.self) { image, _ in
                        if let img = image as? UIImage {
                            images.append(img)
                        }
                        group.leave()
                    }
                } else if result.itemProvider.hasItemConformingToTypeIdentifier(UTType.movie.identifier) {
                    result.itemProvider.loadFileRepresentation(forTypeIdentifier: UTType.movie.identifier) { url, _ in
                        if let url = url {
                            videoURLs.append(url)
                        }
                        group.leave()
                    }
                } else {
                    group.leave()
                }
            }
            
            group.notify(queue: .main) {
                if let onMediaPicked = self.parent.onMediaPicked {
                    onMediaPicked(images, imageURLs, videoURLs)
                } else if !videoURLs.isEmpty, let onVideoPicked = self.parent.onVideoPicked {
                    onVideoPicked(videoURLs)
                } else {
                    self.parent.onImagePicked(images, imageURLs.isEmpty ? nil : imageURLs)
                }
                self.parent.presentationMode.wrappedValue.dismiss()
            }
        }
    }
}` : '';

  // Generate Image Cropper (if cropping enabled)
  const cropperCode = cropEnabled ? `// MARK: - Image Cropper
struct ImageCropper: View {
    let image: UIImage
    let aspectRatio: CropAspectRatio?
    let onCropped: (UIImage) -> Void
    @Environment(\\.presentationMode) var presentationMode
    
    @State private var scale: CGFloat = 1.0
    @State private var lastScale: CGFloat = 1.0
    @State private var offset: CGSize = .zero
    @State private var lastOffset: CGSize = .zero
    @State private var rotation: Angle = .zero
    @State private var lastRotation: Angle = .zero
    @State private var imageFrame: CGRect = .zero
    @State private var cropFrame: CGRect = .zero
    @State private var containerSize: CGSize = CGSize(width: 375, height: 667)
    
    private var lockedAspectRatio: CGSize? {
        aspectRatio?.ratio
    }
    
    var body: some View {
        ZStack {
            Color.black.ignoresSafeArea()
            
            VStack(spacing: 0) {
                // Custom Header
                HStack {
                    Text("Crop Image")
                        .foregroundColor(.white)
                        .font(.headline)
                        .frame(maxWidth: .infinity)
                    
                    Button(action: {
                        presentationMode.wrappedValue.dismiss()
                    }) {
                        Image(systemName: "xmark")
                            .foregroundColor(.white)
                            .font(.system(size: 16, weight: .semibold))
                            .padding(8)
                    }
                }
                .padding(.horizontal)
                .padding(.vertical, 12)
                .background(Color.black.opacity(0.8))
                
                // Content Area - Ultra Simplified
                GeometryReader { geometry in
                    ZStack {
                        // Image - Display immediately, no complex constraints
                        Image(uiImage: image)
                            .resizable()
                            .scaledToFit()
                            .frame(maxWidth: geometry.size.width, maxHeight: geometry.size.height)
                            .scaleEffect(scale)
                            .offset(offset)
                            .rotationEffect(rotation)
                            .background(
                                GeometryReader { imgGeo in
                                    Color.clear
                                        .onAppear {
                                            imageFrame = imgGeo.frame(in: .global)
                                            containerSize = geometry.size
                                            // Initialize crop frame after image renders
                                            DispatchQueue.main.asyncAfter(deadline: .now() + 0.15) {
                                                let cropSize = calculateInitialCropSize(containerSize: geometry.size)
                                                cropFrame = CGRect(
                                                    x: (geometry.size.width - cropSize.width) / 2,
                                                    y: (geometry.size.height - cropSize.height) / 2,
                                                    width: cropSize.width,
                                                    height: cropSize.height
                                                )
                                            }
                                        }
                                        .onChange(of: scale) { _ in
                                            imageFrame = imgGeo.frame(in: .global)
                                        }
                                        .onChange(of: offset) { _ in
                                            imageFrame = imgGeo.frame(in: .global)
                                        }
                                }
                            )
                            .gesture(
                                SimultaneousGesture(
                                    MagnificationGesture()
                                        .onChanged { value in
                                            scale = max(0.5, min(3.0, lastScale * value))
                                        }
                                        .onEnded { _ in
                                            lastScale = scale
                                        },
                                    DragGesture()
                                        .onChanged { value in
                                            offset = CGSize(
                                                width: lastOffset.width + value.translation.width,
                                                height: lastOffset.height + value.translation.height
                                            )
                                        }
                                        .onEnded { _ in
                                            lastOffset = offset
                                        }
                                )
                            )
                            .gesture(
                                RotationGesture()
                                    .onChanged { value in
                                        rotation = lastRotation + value
                                    }
                                    .onEnded { _ in
                                        lastRotation = rotation
                                    }
                            )
                        
                        // Crop overlay
                        if cropFrame.width > 0 && cropFrame.height > 0 {
                            CropOverlayView(
                                cropFrame: $cropFrame,
                                aspectRatio: lockedAspectRatio,
                                containerSize: geometry.size
                            )
                        }
                    }
                    .onChange(of: geometry.size.width) { newWidth in
                        if newWidth > 0 && geometry.size.height > 0 {
                            containerSize = geometry.size
                            let cropSize = calculateInitialCropSize(containerSize: geometry.size)
                            cropFrame = CGRect(
                                x: (geometry.size.width - cropSize.width) / 2,
                                y: (geometry.size.height - cropSize.height) / 2,
                                width: cropSize.width,
                                height: cropSize.height
                            )
                        }
                    }
                    .onChange(of: geometry.size.height) { newHeight in
                        if geometry.size.width > 0 && newHeight > 0 {
                            containerSize = geometry.size
                            let cropSize = calculateInitialCropSize(containerSize: geometry.size)
                            cropFrame = CGRect(
                                x: (geometry.size.width - cropSize.width) / 2,
                                y: (geometry.size.height - cropSize.height) / 2,
                                width: cropSize.width,
                                height: cropSize.height
                            )
                        }
                    }
                }
                
                // Custom Footer Buttons
                HStack(spacing: 20) {
                    Button("Cancel") {
                        presentationMode.wrappedValue.dismiss()
                    }
                    .foregroundColor(.white)
                    .padding()
                    
                    Spacer()
                    
                    Button("Reset") {
                        withAnimation {
                            scale = 1.0
                            lastScale = 1.0
                            offset = .zero
                            lastOffset = .zero
                            rotation = .zero
                            lastRotation = .zero
                        }
                    }
                    .foregroundColor(.white)
                    .padding()
                    
                    Button("Done") {
                        performCrop()
                    }
                    .foregroundColor(.white)
                    .fontWeight(.semibold)
                    .padding()
                }
                .padding(.horizontal)
                .padding(.vertical, 12)
                .background(Color.black.opacity(0.8))
            }
        }
    }
    
    private func calculateInitialCropSize(containerSize: CGSize) -> CGSize {
        // Safety check for valid container size
        guard containerSize.width > 0 && containerSize.height > 0 else {
            return CGSize(width: 300, height: 300)
        }
        
        if let ratio = lockedAspectRatio {
            let ratioValue = ratio.width / ratio.height
            let maxSize = max(100, min(containerSize.width, containerSize.height) * 0.8)
            
            if ratioValue > 1 {
                return CGSize(width: maxSize, height: max(100, maxSize / ratioValue))
            } else {
                return CGSize(width: max(100, maxSize * ratioValue), height: maxSize)
            }
        } else {
            let size = max(100, min(containerSize.width, containerSize.height) * 0.8)
            return CGSize(width: size, height: size)
        }
    }
    
    private func performCrop() {
        let croppedImage = cropImage(image: image, to: cropFrame, imageFrame: imageFrame, scale: scale, rotation: rotation)
        onCropped(croppedImage)
        presentationMode.wrappedValue.dismiss()
    }
    
    private func cropImage(image: UIImage, to cropRect: CGRect, imageFrame: CGRect, scale: CGFloat, rotation: Angle) -> UIImage {
        let imageSize = image.size
        
        // Safety check for valid image size
        guard imageSize.width > 0 && imageSize.height > 0 else {
            return image
        }
        
        // If imageFrame is invalid, use fallback calculation
        guard imageFrame.width > 0 && imageFrame.height > 0 else {
            // Fallback: crop from center with default size
            let cropSize = min(imageSize.width, imageSize.height) * 0.8
            let cropRect = CGRect(
                x: (imageSize.width - cropSize) / 2,
                y: (imageSize.height - cropSize) / 2,
                width: cropSize,
                height: cropSize
            )
            guard let cgImage = image.cgImage,
                  let croppedCGImage = cgImage.cropping(to: cropRect) else {
                return image
            }
            return UIImage(cgImage: croppedCGImage, scale: image.scale, orientation: image.imageOrientation)
        }
        
        let imageViewSize = imageFrame.size
        
        // Safety check for valid imageViewSize
        guard imageViewSize.width > 0 && imageViewSize.height > 0 else {
            return image
        }
        
        // Calculate scale factor from image view to actual image
        let scaleFactor = imageSize.width / imageViewSize.width
        
        // Adjust crop rect for scale and offset
        let adjustedCropRect = CGRect(
            x: (cropRect.origin.x - imageFrame.origin.x) * scaleFactor / scale,
            y: (cropRect.origin.y - imageFrame.origin.y) * scaleFactor / scale,
            width: cropRect.width * scaleFactor / scale,
            height: cropRect.height * scaleFactor / scale
        )
        
        // Ensure crop rect is within image bounds
        let clampedRect = CGRect(
            x: max(0, min(adjustedCropRect.origin.x, imageSize.width)),
            y: max(0, min(adjustedCropRect.origin.y, imageSize.height)),
            width: min(adjustedCropRect.width, imageSize.width - max(0, adjustedCropRect.origin.x)),
            height: min(adjustedCropRect.height, imageSize.height - max(0, adjustedCropRect.origin.y))
        )
        
        // Ensure minimum crop size
        guard clampedRect.width >= 10 && clampedRect.height >= 10 else {
            return image
        }
        
        guard let cgImage = image.cgImage,
              let croppedCGImage = cgImage.cropping(to: clampedRect) else {
            return image
        }
        
        var croppedImage = UIImage(cgImage: croppedCGImage, scale: image.scale, orientation: image.imageOrientation)
        
        // Apply rotation if needed
        if rotation != .zero {
            let radians = rotation.radians
            let rotatedImage = rotateImage(croppedImage, by: radians)
            croppedImage = rotatedImage ?? croppedImage
        }
        
        return croppedImage
    }
    
    private func rotateImage(_ image: UIImage, by radians: Double) -> UIImage? {
        let size = image.size
        UIGraphicsBeginImageContextWithOptions(size, false, image.scale)
        defer { UIGraphicsEndImageContext() }
        
        guard let context = UIGraphicsGetCurrentContext() else { return nil }
        
        context.translateBy(x: size.width / 2, y: size.height / 2)
        context.rotate(by: CGFloat(radians))
        context.translateBy(x: -size.width / 2, y: -size.height / 2)
        
        image.draw(at: .zero)
        return UIGraphicsGetImageFromCurrentImageContext()
    }
}

extension Angle {
    var radians: Double {
        return Double(self.radians)
    }
}

struct CropOverlayView: View {
    @Binding var cropFrame: CGRect
    let aspectRatio: CGSize?
    let containerSize: CGSize
    
    var body: some View {
        ZStack {
            // Dark overlay with transparent crop area
            Rectangle()
                .fill(Color.black.opacity(0.6))
                .mask(
                    Rectangle()
                        .fill(Color.black)
                        .overlay(
                            Rectangle()
                                .frame(width: cropFrame.width, height: cropFrame.height)
                                .position(x: cropFrame.midX, y: cropFrame.midY)
                                .blendMode(.destinationOut)
                        )
                )
            
            // Crop frame border
            RoundedRectangle(cornerRadius: 8)
                .stroke(Color.white, lineWidth: 2)
                .frame(width: cropFrame.width, height: cropFrame.height)
                .position(x: cropFrame.midX, y: cropFrame.midY)
            
            // Corner handles
            ForEach(0..<4) { index in
                Circle()
                    .fill(Color.white)
                    .frame(width: 24, height: 24)
                    .overlay(
                        Circle()
                            .stroke(Color.blue, lineWidth: 2)
                            .frame(width: 20, height: 20)
                    )
                    .position(cornerPosition(for: index))
                    .gesture(
                        DragGesture()
                            .onChanged { value in
                                adjustCropFrame(for: index, translation: value.translation)
                            }
                    )
            }
        }
    }
    
    private func cornerPosition(for index: Int) -> CGPoint {
        switch index {
        case 0: return CGPoint(x: cropFrame.minX, y: cropFrame.minY)
        case 1: return CGPoint(x: cropFrame.maxX, y: cropFrame.minY)
        case 2: return CGPoint(x: cropFrame.maxX, y: cropFrame.maxY)
        case 3: return CGPoint(x: cropFrame.minX, y: cropFrame.maxY)
        default: return .zero
        }
    }
    
    private func adjustCropFrame(for cornerIndex: Int, translation: CGSize) {
        var newFrame = cropFrame
        
        switch cornerIndex {
        case 0: // Top-left
            newFrame.origin.x += translation.width
            newFrame.origin.y += translation.height
            newFrame.size.width -= translation.width
            newFrame.size.height -= translation.height
        case 1: // Top-right
            newFrame.origin.y += translation.height
            newFrame.size.width += translation.width
            newFrame.size.height -= translation.height
        case 2: // Bottom-right
            newFrame.size.width += translation.width
            newFrame.size.height += translation.height
        case 3: // Bottom-left
            newFrame.origin.x += translation.width
            newFrame.size.width -= translation.width
            newFrame.size.height += translation.height
        default: break
        }
        
        // Maintain aspect ratio if locked
        if let ratio = aspectRatio {
            let ratioValue = ratio.width / ratio.height
            let newRatio = newFrame.width / newFrame.height
            
            if abs(newRatio - ratioValue) > 0.01 {
                if newFrame.width > newFrame.height {
                    newFrame.size.height = newFrame.width / ratioValue
                } else {
                    newFrame.size.width = newFrame.height * ratioValue
                }
            }
        }
        
        // Ensure minimum size
        if newFrame.width >= 50 && newFrame.height >= 50 {
            // Keep within bounds
            if newFrame.minX >= 0 && newFrame.minY >= 0 &&
               newFrame.maxX <= containerSize.width && newFrame.maxY <= containerSize.height {
                cropFrame = newFrame
            }
        }
    }
}` : '';

  // Generate Unified Media Picker
  const unifiedPickerCode = `// MARK: - Unified Media Picker Helper
struct UnifiedMediaPickerHelper {
    let config: MediaPickerConfig
    let onMediaPicked: (MediaPickerResult) -> Void
    let onCancel: (() -> Void)?
    
    func shouldShowSourceSelection() -> Bool {
        var count = 0
        ${enableDocumentPicker ? 'if config.enableDocumentPicker { count += 1 }' : ''}
        ${enableCamera ? 'if config.enableCamera && config.enablePhotoPicker { count += 1 }' : ''}
        ${enableGallery ? 'if config.enableGallery && config.enablePhotoPicker { count += 1 }' : ''}
        return count > 1
    }
    
    func getDirectPickerType() -> PickerType? {
        ${enableDocumentPicker ? `if config.enableDocumentPicker {
            return .document
        }` : ''}
        ${enableCamera ? `if config.enableCamera && config.enablePhotoPicker {
            return .camera
        }` : ''}
        ${enableGallery ? `if config.enableGallery && config.enablePhotoPicker {
            return .gallery
        }` : ''}
        return nil
    }
}

enum PickerType {
    case document
    case camera
    case gallery
}`;

  // Generate Usage Example
  const usageExample = `// MARK: - Usage Example
struct ContentView: View {
    @State private var showSourceSelection = false
    @State private var showDocumentPicker = false
    @State private var showCameraPicker = false
    @State private var showGalleryPicker = false
    ${cropEnabled ? `@State private var showCropper = false
    @State private var imageToCrop: UIImage?` : ''}
    @State private var selectedImage: UIImage?
    @State private var selectedImages: [UIImage] = []
    
    private let config = MediaPickerConfig()
    
    var body: some View {
        VStack(spacing: 20) {
            ${allowMultipleSelection ? `if !selectedImages.isEmpty {
                ScrollView(.horizontal) {
                    HStack {
                        ForEach(selectedImages.indices, id: \\.self) { index in
                            Image(uiImage: selectedImages[index])
                                .resizable()
                                .scaledToFill()
                                .frame(width: 150, height: 150)
                                .clipShape(RoundedRectangle(cornerRadius: 10))
                        }
                    }
                }
            }` : `if let image = selectedImage {
                Image(uiImage: image)
                    .resizable()
                    .scaledToFit()
                    .frame(maxWidth: 300, maxHeight: 300)
            }`}
            
            Button("Select Media") {
                let helper = UnifiedMediaPickerHelper(config: config, onMediaPicked: { _ in }, onCancel: nil)
                if helper.shouldShowSourceSelection() {
                    showSourceSelection = true
                } else if let pickerType = helper.getDirectPickerType() {
                    switch pickerType {
                    case .document: showDocumentPicker = true
                    case .camera: showCameraPicker = true
                    case .gallery: showGalleryPicker = true
                    }
                }
            }
        }
        .confirmationDialog("Select Source", isPresented: $showSourceSelection, titleVisibility: .visible) {
            ${enableDocumentPicker ? `Button("Files") {
                showDocumentPicker = true
            }` : ''}
            ${enableCamera ? `Button("Camera") {
                showCameraPicker = true
            }` : ''}
            ${enableGallery ? `Button("Photo Gallery") {
                showGalleryPicker = true
            }` : ''}
            Button("Cancel", role: .cancel) {}
        }
        ${enableDocumentPicker ? `.sheet(isPresented: $showDocumentPicker) {
            DocumentPicker(
                allowsMultipleSelection: config.allowMultipleSelection,
                documentDidPick: { urls in
                    // Handle document selection
                    showDocumentPicker = false
                },
                onCancel: {
                    showDocumentPicker = false
                }
            )
        }` : ''}
        .fullScreenCover(isPresented: $showCameraPicker) {
            CameraPicker(
                allowsVideo: config.allowVideos,
                onImagePicked: { images, urls in
                    handleImagePicked(images: images, urls: urls, fromCamera: true)
                },
                onVideoPicked: { url in
                    // Handle video selection
                    showCameraPicker = false
                },
                onCancel: {
                    showCameraPicker = false
                }
            )
        }
        .sheet(isPresented: $showGalleryPicker) {
            ImagePicker(
                allowsMultipleSelection: config.allowMultipleSelection,
                maxSelectionCount: config.maxSelectionCount,
                allowsVideos: config.allowVideos,
                onImagePicked: { images, urls in
                    handleImagePicked(images: images, urls: urls, fromCamera: false)
                },
                onVideoPicked: { urls in
                    // Handle video selection
                    showGalleryPicker = false
                }
            )
        }
        ${cropEnabled ? `.sheet(isPresented: $showCropper) {
            if let image = imageToCrop {
                ImageCropper(
                    image: image,
                    aspectRatio: config.cropAspectRatio,
                    onCropped: { croppedImage in
                        ${allowMultipleSelection ? `selectedImages.append(croppedImage)` : `selectedImage = croppedImage`}
                        showCropper = false
                    }
                )
            }
        }` : ''}
    }
    
    private func handleImagePicked(images: [UIImage], urls: [URL]?, fromCamera: Bool) {
        ${cropEnabled ? `if config.enableCropping, let firstImage = images.first {
            imageToCrop = firstImage
            showCropper = true
            if fromCamera {
                showCameraPicker = false
            } else {
                showGalleryPicker = false
            }
        } else {
            ${allowMultipleSelection ? `selectedImages.append(contentsOf: images)` : `selectedImage = images.first`}
            showCameraPicker = false
            showGalleryPicker = false
        }` : `${allowMultipleSelection ? `selectedImages.append(contentsOf: images)` : `selectedImage = images.first`}
        showCameraPicker = false
        showGalleryPicker = false`}
    }
}

// Note: Add to Info.plist:
// <key>NSPhotoLibraryUsageDescription</key>
// <string>We need access to your photo library</string>
${enableCamera ? '// <key>NSCameraUsageDescription</key>\n// <string>We need access to your camera</string>' : ''}
${allowsVideos ? '// <key>NSPhotoLibraryAddUsageDescription</key>\n// <string>We need access to save videos</string>' : ''}`;

  const blocks = [
    {
      name: 'Usage Example',
      code: usageExample,
      requiresImport: true,
      order: 1
    },
    {
      name: 'Configuration & Result Types',
      code: `${configCode}\n\n${resultCode}`,
      requiresImport: false,
      order: 2
    },
    {
      name: 'Unified Media Picker',
      code: unifiedPickerCode,
      requiresImport: false,
      order: 3
    }
  ];

  // Add component blocks conditionally
  if (enableDocumentPicker) {
    blocks.push({
      name: 'Document Picker',
      code: documentPickerCode,
      requiresImport: false,
      order: 4,
      optional: true
    });
  }

  if (enableCamera) {
    blocks.push({
      name: 'Camera Picker',
      code: cameraPickerCode,
      requiresImport: false,
      order: 5,
      optional: true
    });
  }

  if (enableGallery) {
    blocks.push({
      name: 'Gallery Picker',
      code: galleryPickerCode,
      requiresImport: false,
      order: 6,
      optional: true
    });
  }

  if (cropEnabled) {
    blocks.push({
      name: 'Image Cropper',
      code: cropperCode,
      requiresImport: false,
      order: 7,
      optional: true
    });
  }

  const imports = ['import SwiftUI', 'import PhotosUI', 'import UniformTypeIdentifiers'];
  if (enableCamera || enableGallery) imports.push('import UIKit');

  return {
    blocks: blocks.filter(block => block.code),
    importStatement: [...new Set(imports)].join('\n')
  };
};

/**
 * Generate Swift Codable models from JSON with safe decoding and type mismatch error handling
 */
export const generateJsonToModelCode = (config) => {
  const { jsonInput, modelName, useMacros } = config;
  // eslint-disable-next-line no-unused-vars
  const useOptional = config.useOptional; // Always true now, kept for API compatibility

  if (!jsonInput || !jsonInput.trim()) {
    return '// Please enter JSON to generate Codable models';
  }

  let parsed;
  try {
    parsed = JSON.parse(jsonInput);
  } catch (e) {
    return `// Invalid JSON: ${e.message}\n// Please fix the JSON and try again.`;
  }

  const toSwiftType = (value, isOptional = true) => {
    if (value === null) {
      return 'String?';
    }
    if (typeof value === 'string') {
      return isOptional ? 'String?' : 'String';
    }
    if (typeof value === 'number') {
      return Number.isInteger(value) 
        ? (isOptional ? 'Int?' : 'Int')
        : (isOptional ? 'Double?' : 'Double');
    }
    if (typeof value === 'boolean') {
      return isOptional ? 'Bool?' : 'Bool';
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '[String]?';
      }
      const itemType = toSwiftType(value[0], false);
      return `[${itemType}]?`;
    }
    if (typeof value === 'object') {
      return 'Any?';
    }
    return 'Any?';
  };

  const capitalize = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const toCamelCase = (str) => {
    return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
  };

  // Generate the generic extensions
  const generateExtensions = () => {
    return `
// MARK: - Safe Decoding Extensions

extension KeyedDecodingContainer {
    func safeDecode<T: Decodable>(_ type: T.Type, forKey key: K) throws -> T? {
        do {
            return try decodeIfPresent(type, forKey: key)
        } catch {
            throw DecodingError.typeMismatch(
                T.self,
                DecodingError.Context(
                    codingPath: codingPath + [key],
                    debugDescription: "❌ Type mismatch for key '\\(key.stringValue)'. Expected: \\(T.self). Error: \\(error)"
                )
            )
        }
    }
}

extension KeyedDecodingContainer {
    func decodeString(forKey key: KeyedDecodingContainer<K>.Key) throws -> String? {
        // Try decoding as String
        if let stringValue = try? decode(String.self, forKey: key) {
            return stringValue
        }
        // Try decoding as Int
        if let intValue = try? decode(Int.self, forKey: key) {
            return String(intValue)
        }
        // Try decoding as Double
        if let doubleValue = try? decode(Double.self, forKey: key) {
            return String(doubleValue)
        }
        // Try decoding as Float
        if let floatValue = try? decode(Float.self, forKey: key) {
            return String(floatValue)
        }
        // Try decoding as Bool
        if let boolValue = try? decode(Bool.self, forKey: key) {
            return String(boolValue)
        }
        // Return nil if key not found
        return nil
    }
    
    func decodeInt(forKey key: KeyedDecodingContainer<K>.Key) throws -> Int? {
        // Try decoding as Int
        if let intValue = try? decode(Int.self, forKey: key) {
            return intValue
        }
        // Try decoding as String
        if let stringValue = try? decode(String.self, forKey: key), let converted = Int(stringValue) {
            return converted
        }
        // Try decoding as Double
        if let doubleValue = try? decode(Double.self, forKey: key) {
            return Int(doubleValue)
        }
        return nil
    }
    
    func decodeDouble(forKey key: KeyedDecodingContainer<K>.Key) throws -> Double? {
        // Try decoding as Double
        if let doubleValue = try? decode(Double.self, forKey: key) {
            return doubleValue
        }
        // Try decoding as String
        if let stringValue = try? decode(String.self, forKey: key), let converted = Double(stringValue) {
            return converted
        }
        // Try decoding as Int
        if let intValue = try? decode(Int.self, forKey: key) {
            return Double(intValue)
        }
        return nil
    }
    
    func decodeBool(forKey key: KeyedDecodingContainer<K>.Key) throws -> Bool? {
        // Try decoding as Bool
        if let boolValue = try? decode(Bool.self, forKey: key) {
            return boolValue
        }
        // Try decoding as String
        if let stringValue = try? decode(String.self, forKey: key) {
            let lowercased = stringValue.lowercased()
            if lowercased == "true" || lowercased == "1" {
                return true
            } else if lowercased == "false" || lowercased == "0" {
                return false
            }
        }
        // Try decoding as Int
        if let intValue = try? decode(Int.self, forKey: key) {
            return intValue != 0
        }
        return nil
    }
}

extension DecodingError {
    var prettyDescription: String {
        switch self {
        case .typeMismatch(let type, let context):
            return "❌ Type mismatch: expected \\(type) at path \\(context.codingPath.prettyPath).\\nReason: \\(context.debugDescription)"
        case .valueNotFound(let type, let context):
            return "❌ Value not found: \\(type) at path \\(context.codingPath.prettyPath).\\nReason: \\(context.debugDescription)"
        case .keyNotFound(let key, let context):
            return "❌ Key not found: \\(key.stringValue) at path \\(context.codingPath.prettyPath).\\nReason: \\(context.debugDescription)"
        case .dataCorrupted(let context):
            return "❌ Data corrupted at path \\(context.codingPath.prettyPath).\\nReason: \\(context.debugDescription)"
        @unknown default:
            return "❌ Unknown decoding error: \\(self)"
        }
    }
}

extension Array where Element == CodingKey {
    var prettyPath: String {
        self.map { $0.stringValue }.joined(separator: " ➝ ")
    }
}
`;
  };

  const generateSafeDecodingInit = (obj, structName, path = '') => {
    const entries = Object.entries(obj);
    let initCode = `    init(from decoder: Decoder) throws {\n`;
    initCode += `        let container = try decoder.container(keyedBy: CodingKeys.self)\n\n`;
    
    entries.forEach(([key, value]) => {
      const swiftKey = toCamelCase(key);
      const needsNested = typeof value === 'object' && value !== null && !Array.isArray(value);
      const isArrayOfObjects = Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null;
      
      if (needsNested) {
        const nestedName = `${structName}${capitalize(swiftKey)}`;
        initCode += `        self.${swiftKey} = try container.safeDecode(${nestedName}.self, forKey: .${swiftKey})\n`;
      } else if (isArrayOfObjects) {
        const nestedName = `${structName}${capitalize(swiftKey)}Item`;
        initCode += `        self.${swiftKey} = try container.safeDecode([${nestedName}].self, forKey: .${swiftKey})\n`;
      } else {
        const swiftType = toSwiftType(value, true);
        const baseType = swiftType.replace('?', '');
        
        // Use appropriate extension method based on type
        if (baseType === 'String') {
          initCode += `        self.${swiftKey} = try container.decodeString(forKey: .${swiftKey})\n`;
        } else if (baseType === 'Int') {
          initCode += `        self.${swiftKey} = try container.decodeInt(forKey: .${swiftKey})\n`;
        } else if (baseType === 'Double') {
          initCode += `        self.${swiftKey} = try container.decodeDouble(forKey: .${swiftKey})\n`;
        } else if (baseType === 'Bool') {
          initCode += `        self.${swiftKey} = try container.decodeBool(forKey: .${swiftKey})\n`;
        } else {
          // For arrays and other types, use safeDecode
          initCode += `        self.${swiftKey} = try container.safeDecode(${baseType}.self, forKey: .${swiftKey})\n`;
        }
      }
    });
    
    initCode += `    }\n`;
    return initCode;
  };

  const generateStruct = (obj, name, depth = 0, path = '') => {
    const indent = '    '.repeat(depth);
    const structName = capitalize(toCamelCase(name));
    
    let code = `${indent}${useMacros ? '@Codable' : ''}\n${indent}struct ${structName}: Codable {\n`;
    
    const entries = Object.entries(obj);
    
    // Generate properties (all optional)
    entries.forEach(([key, value]) => {
      const swiftKey = toCamelCase(key);
      const needsNested = typeof value === 'object' && value !== null && !Array.isArray(value);
      
      if (needsNested) {
        const nestedName = `${structName}${capitalize(swiftKey)}`;
        code += `${indent}    let ${swiftKey}: ${nestedName}?\n`;
      } else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        const nestedName = `${structName}${capitalize(swiftKey)}Item`;
        code += `${indent}    let ${swiftKey}: [${nestedName}]?\n`;
      } else {
        const swiftType = toSwiftType(value, true);
        code += `${indent}    let ${swiftKey}: ${swiftType}\n`;
      }
    });
    
    // Always generate CodingKeys enum
    code += `\n${indent}    enum CodingKeys: String, CodingKey {\n`;
    entries.forEach(([key]) => {
      const swiftKey = toCamelCase(key);
      if (key !== swiftKey) {
        code += `${indent}        case ${swiftKey} = "${key}"\n`;
      } else {
        code += `${indent}        case ${swiftKey}\n`;
      }
    });
    code += `${indent}    }\n`;
    
    // Generate custom init with safe decoding
    if (!useMacros) {
      code += '\n' + generateSafeDecodingInit(obj, structName, path);
    }
    
    code += `${indent}}\n`;
    
    // Generate nested structs
    entries.forEach(([key, value]) => {
      const swiftKey = toCamelCase(key);
      const currentPath = path ? `${path}.${key}` : key;
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const nestedName = `${structName}${capitalize(swiftKey)}`;
        code += '\n' + generateStruct(value, nestedName, depth, currentPath);
      } else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        const nestedName = `${structName}${capitalize(swiftKey)}Item`;
        code += '\n' + generateStruct(value[0], nestedName, depth, currentPath);
      }
    });
    
    return code;
  };

  const mainStructName = capitalize(toCamelCase(modelName || 'Model'));
  const structCode = generateStruct(parsed, mainStructName);

  const usageExample = `
// MARK: - Usage Example
// Replace jsonString with your actual JSON data
let jsonString = """
{
    // Your JSON here
}
"""

if let jsonData = jsonString.data(using: .utf8) {
    let decoder = JSONDecoder()
    do {
        let model = try decoder.decode(${mainStructName}.self, from: jsonData)
        print("✅ Decoded successfully: \\(model)")
    } catch let error as DecodingError {
        print(error.prettyDescription)
    } catch {
        print("❌ Error decoding: \\(error)")
    }
}
`;

  return {
    blocks: [
      {
        name: 'Usage Example',
        code: usageExample,
        requiresImport: true,
        order: 1
      },
      {
        name: 'Model Struct',
        code: `// MARK: - Main Component\n${structCode}`,
        requiresImport: false,
        order: 2
      },
      {
        name: 'Safe Decoding Extensions',
        code: `// MARK: - Helper Extensions\n${generateExtensions()}`,
        requiresImport: false,
        order: 3,
        optional: true
      }
    ],
    importStatement: 'import Foundation'
  };
};

/**
 * Generate NetworkAdapter-based Swift networking layer
 * Unified design with configurable async/await, Combine, and completion APIs
 */
export const generateApiCode = (config) => {
  const {
    baseURL = '',
    useAsyncAwait = true,
    timeout = 30,
    enableRetry = false,
    enableLogging = false,
    includeCombineWrapper = true,
    pattern = 'singleton',
  } = config;

  const safeBaseURL = baseURL || 'https://api.example.com';
  const includeAsync = useAsyncAwait !== false;
  const includeCombine = includeCombineWrapper !== false;
  const includeCompletion = true; // shared implementation used by async & Combine
  const includeSingletonShared = pattern === 'singleton';

  // Generate HTTPMethod enum
  const generateHTTPMethodEnum = () => {
    return `// MARK: - HTTP Method Enum

enum HTTPMethod: String {
    case get = "GET"
    case post = "POST"
    case put = "PUT"
    case patch = "PATCH"
    case delete = "DELETE"
    
    var rawValue: String {
        switch self {
        case .get: return "GET"
        case .post: return "POST"
        case .put: return "PUT"
        case .patch: return "PATCH"
        case .delete: return "DELETE"
        }
    }
}`;
  };

  // MARK: - NetworkConfig
  const generateNetworkConfig = () => {
    return `// MARK: - Network Configuration

struct NetworkConfig {
    var defaultHeaders: [String: String]
    var timeout: TimeInterval
    var allowsLogging: Bool
    var decoder: JSONDecoder
    var encoder: JSONEncoder

    static let \`default\` = NetworkConfig(
        defaultHeaders: ["Content-Type": "application/json"],
        timeout: ${timeout},
        allowsLogging: ${enableLogging ? 'true' : 'false'},
        decoder: JSONDecoder(),
        encoder: JSONEncoder()
    )
}`;
  };

  // MARK: - NetworkError
  const generateNetworkError = () => {
    return `// MARK: - Network Error

enum NetworkError: Error {
    case invalidURL
    case noData
    case decodingFailed(Error)
    case serverError(Int)
    case underlying(Error)
}`;
  };

  // MARK: - APIEndpoint
  const generateAPIEndpointProtocol = () => {
    return `// MARK: - API Endpoint

protocol APIEndpoint {
    var baseURL: URL { get }
    var path: String { get }
    var method: HTTPMethod { get }
    var headers: [String: String]? { get }
    var queryItems: [URLQueryItem]? { get }
    var body: Data? { get }
}`;
  };

  // MARK: - NetworkAdapter core and variants
  const generateNetworkAdapter = () => {
    let code = `// MARK: - Network Adapter

final class NetworkAdapter {
`;

    if (includeSingletonShared) {
      code += `
    // Singleton
    static let shared = NetworkAdapter()
`;
    }

    code += `

    // Properties
    private let session: URLSession
    private let config: NetworkConfig

    // Init (Injectable)
    init(
        session: URLSession = .shared,
        config: NetworkConfig = .default
    ) {
        self.session = session
        self.config = config
    }

    // Request Builder
    private func buildRequest(from endpoint: APIEndpoint) throws -> URLRequest {
        var components = URLComponents(
            url: endpoint.baseURL.appendingPathComponent(endpoint.path),
            resolvingAgainstBaseURL: false
        )

        components?.queryItems = endpoint.queryItems

        guard let url = components?.url else {
            throw NetworkError.invalidURL
        }

        var request = URLRequest(url: url)
        request.httpMethod = endpoint.method.rawValue
        request.timeoutInterval = config.timeout
        request.httpBody = endpoint.body

        config.defaultHeaders.forEach {
            request.setValue($0.value, forHTTPHeaderField: $0.key)
        }

        endpoint.headers?.forEach {
            request.setValue($0.value, forHTTPHeaderField: $0.key)
        }

        return request
    }`;

    if (includeCompletion) {
      code += `

    // MARK: - Completion-based API
    func request<T: Decodable>(
        endpoint: APIEndpoint,
        responseType: T.Type,
        completion: @escaping (Result<T, NetworkError>) -> Void
    ) {
        do {
            let request = try buildRequest(from: endpoint)

            if config.allowsLogging {
                print("➡️ Request:", request)
            }

            session.dataTask(with: request) { data, response, error in

                if let error = error {
                    completion(.failure(.underlying(error)))
                    return
                }

                guard let httpResponse = response as? HTTPURLResponse else {
                    completion(.failure(.noData))
                    return
                }

                guard (200...299).contains(httpResponse.statusCode) else {
                    completion(.failure(.serverError(httpResponse.statusCode)))
                    return
                }

                guard let data = data else {
                    completion(.failure(.noData))
                    return
                }

                do {
                    let decoded = try self.config.decoder.decode(T.self, from: data)
                    completion(.success(decoded))
                } catch {
                    completion(.failure(.decodingFailed(error)))
                }
            }
            .resume()

        } catch {
            completion(.failure(.underlying(error)))
        }
    }`;
    }

    if (includeAsync) {
      code += `

    // MARK: - Async / Await API
    func request<T: Decodable>(
        endpoint: APIEndpoint,
        responseType: T.Type
    ) async throws -> T {

        let request = try buildRequest(from: endpoint)

        if config.allowsLogging {
            print("➡️ Request:", request)
        }

        let (data, response) = try await session.data(for: request)

        guard let httpResponse = response as? HTTPURLResponse,
              (200...299).contains(httpResponse.statusCode) else {
            throw NetworkError.serverError(
                (response as? HTTPURLResponse)?.statusCode ?? -1
            )
        }

        do {
            return try config.decoder.decode(T.self, from: data)
        } catch {
            throw NetworkError.decodingFailed(error)
        }
    }`;
    }

    if (includeCombine) {
      code += `
}

// MARK: - Combine Support
extension NetworkAdapter {

    func request<T: Decodable>(
        endpoint: APIEndpoint,
        responseType: T.Type
    ) -> AnyPublisher<T, NetworkError> {

        Future { promise in
            self.request(endpoint: endpoint, responseType: responseType) { result in
                promise(result)
            }
        }
        .eraseToAnyPublisher()
    }
}`;
    } else {
      code += `
}`;
    }

    return code;
  };

  // MARK: - AnyEndpoint (generic endpoint type)
  const generateAnyEndpoint = () => {
    return `// MARK: - Generic Endpoint Type

struct AnyEndpoint: APIEndpoint {
    let baseURL: URL
    let path: String
    let method: HTTPMethod
    let headers: [String: String]?
    let queryItems: [URLQueryItem]?
    let body: Data?

    init(
        baseURL: URL,
        path: String,
        method: HTTPMethod,
        headers: [String: String]? = nil,
        queryItems: [URLQueryItem]? = nil,
        body: Data? = nil
    ) {
        self.baseURL = baseURL
        self.path = path
        self.method = method
        self.headers = headers
        self.queryItems = queryItems
        self.body = body
    }
}`;
  };

  // MARK: - Generic async helpers (pattern-agnostic)
  const generateGenericHelpers = () => {
    return `// MARK: - Generic Request Helpers

@discardableResult
func apiRequest<T: Decodable>(
    _ method: HTTPMethod,
    path: String,
    baseURL: URL,
    queryItems: [URLQueryItem]? = nil,
    headers: [String: String]? = nil,
    body: Data? = nil,
    adapter: NetworkAdapter = NetworkAdapter(config: .default),
    responseType: T.Type = T.self
) async throws -> T {
    let endpoint = AnyEndpoint(
        baseURL: baseURL,
        path: path,
        method: method,
        headers: headers,
        queryItems: queryItems,
        body: body
    )
    return try await adapter.request(endpoint: endpoint, responseType: T.self)
}

@discardableResult
func apiGet<T: Decodable>(
    _ path: String,
    baseURL: URL,
    queryItems: [URLQueryItem]? = nil,
    headers: [String: String]? = nil,
    adapter: NetworkAdapter = NetworkAdapter(config: .default),
    responseType: T.Type = T.self
) async throws -> T {
    try await apiRequest(.get, path: path, baseURL: baseURL, queryItems: queryItems, headers: headers, adapter: adapter)
}

@discardableResult
func apiPost<T: Decodable>(
    _ path: String,
    baseURL: URL,
    body: Data? = nil,
    headers: [String: String]? = nil,
    adapter: NetworkAdapter = NetworkAdapter(config: .default),
    responseType: T.Type = T.self
) async throws -> T {
    try await apiRequest(.post, path: path, baseURL: baseURL, headers: headers, body: body, adapter: adapter)
}

@discardableResult
func apiPut<T: Decodable>(
    _ path: String,
    baseURL: URL,
    body: Data? = nil,
    headers: [String: String]? = nil,
    adapter: NetworkAdapter = NetworkAdapter(config: .default),
    responseType: T.Type = T.self
) async throws -> T {
    try await apiRequest(.put, path: path, baseURL: baseURL, headers: headers, body: body, adapter: adapter)
}

@discardableResult
func apiPatch<T: Decodable>(
    _ path: String,
    baseURL: URL,
    body: Data? = nil,
    headers: [String: String]? = nil,
    adapter: NetworkAdapter = NetworkAdapter(config: .default),
    responseType: T.Type = T.self
) async throws -> T {
    try await apiRequest(.patch, path: path, baseURL: baseURL, headers: headers, body: body, adapter: adapter)
}

@discardableResult
func apiDelete<T: Decodable>(
    _ path: String,
    baseURL: URL,
    queryItems: [URLQueryItem]? = nil,
    headers: [String: String]? = nil,
    adapter: NetworkAdapter = NetworkAdapter(config: .default),
    responseType: T.Type = T.self
) async throws -> T {
    try await apiRequest(.delete, path: path, baseURL: baseURL, queryItems: queryItems, headers: headers, adapter: adapter)
}`;
  };

  // MARK: - Examples
  const generateExamples = () => {
    const base = safeBaseURL;
    let code = `// MARK: - Example Endpoint

enum UserAPI: APIEndpoint {

    case getUsers
    case createUser(name: String)

    var baseURL: URL {
        URL(string: "${base}")!
    }

    var path: String {
        switch self {
        case .getUsers: return "/users"
        case .createUser: return "/users"
        }
    }

    var method: HTTPMethod {
        switch self {
        case .getUsers: return .get
        case .createUser: return .post
        }
    }

    var headers: [String: String]? {
        ["Authorization": "Bearer token"]
    }

    var queryItems: [URLQueryItem]? {
        nil
    }

    var body: Data? {
        switch self {
        case .createUser(let name):
            return try? JSONEncoder().encode(["name": name])
        default:
            return nil
        }
    }
}

// MARK: - Usage Examples
// Replace \`User\` and \`[User]\` with your real models.
`;

    // Completion example
    if (includeCompletion) {
      code += `
// Completion-based
NetworkAdapter.shared.request(
    endpoint: UserAPI.getUsers,
    responseType: [User].self
) { result in
    print(result)
}
`;
    }

    // Async/await example
    if (includeAsync) {
      code += `// Async / Await
Task {
    do {
        let users = try await NetworkAdapter.shared.request(
            endpoint: UserAPI.getUsers,
            responseType: [User].self
        )
        print(users)
    } catch {
        print(error)
    }
}
`;
    }

    // Combine example
    if (includeCombine) {
      code += `// Combine
var cancellables = Set<AnyCancellable>()

NetworkAdapter.shared
    .request(endpoint: UserAPI.getUsers, responseType: [User].self)
    .sink(receiveCompletion: { completion in
        print(completion)
    }, receiveValue: { users in
        print(users)
    })
    .store(in: &cancellables)
`;
    }

    if (enableRetry) {
      code += `
// NOTE: Retry with exponential backoff is not implemented yet.
// You can wrap the async/await or Combine APIs above in your own retry strategy.`;
    }

    // Minimal SwiftUI demo (compilable) showing how to use the generated API
    let swiftUIDemo = '';

    if (includeAsync) {
      if (pattern === 'singleton') {
        swiftUIDemo = `
#if canImport(SwiftUI)
import SwiftUI

struct APINetworkDemoView: View {
    @State private var resultText = "Loading..."

    var body: some View {
        Text(resultText)
            .padding()
            .task {
                await load()
            }
    }

    private func load() async {
        do {
            let users: [User] = try await APIService.shared.get("/users")
            resultText = "Loaded users: \\(users.count)"
        } catch {
            resultText = "Error: \\(error)"
        }
    }
}
#endif
`;
      } else if (pattern === 'generic') {
        swiftUIDemo = `
#if canImport(SwiftUI)
import SwiftUI

struct APINetworkDemoView: View {
    @State private var resultText = "Loading..."
    private let service = UserService(baseURL: URL(string: "${base}")!)

    var body: some View {
        Text(resultText)
            .padding()
            .task {
                await load()
            }
    }

    private func load() async {
        do {
            let users = try await service.getUsers()
            resultText = "Loaded users: \\(users.count)"
        } catch {
            resultText = "Error: \\(error)"
        }
    }
}
#endif
`;
      } else if (pattern === 'repository') {
        swiftUIDemo = `
#if canImport(SwiftUI)
import SwiftUI

struct APINetworkDemoView: View {
    @State private var resultText = "Loading..."
    private let repository = UserRepository(adapter: .shared, baseURL: URL(string: "${base}")!)

    var body: some View {
        Text(resultText)
            .padding()
            .task {
                await load()
            }
    }

    private func load() async {
        do {
            let users = try await repository.getUsers()
            resultText = "Loaded users: \\(users.count)"
        } catch {
            resultText = "Error: \\(error)"
        }
    }
}
#endif
`;
      } else if (pattern === 'functional') {
        swiftUIDemo = `
#if canImport(SwiftUI)
import SwiftUI

struct APINetworkDemoView: View {
    @State private var resultText = "Loading..."

    var body: some View {
        Text(resultText)
            .padding()
            .task {
                await load()
            }
    }

    private func load() async {
        do {
            let users: [User] = try await apiGet(
                "/users",
                baseURL: URL(string: "${base}")!
            )
            resultText = "Loaded users: \\(users.count)"
        } catch {
            resultText = "Error: \\(error)"
        }
    }
}
#endif
`;
      } else if (pattern === 'interceptor') {
        swiftUIDemo = `
#if canImport(SwiftUI)
import SwiftUI

struct APINetworkDemoView: View {
    @State private var resultText = "Loading..."
    private let client = APIClient(adapter: .shared, baseURL: URL(string: "${base}")!)

    var body: some View {
        Text(resultText)
            .padding()
            .task {
                await load()
            }
    }

    private func load() async {
        do {
            let users: [User] = try await client.get("/users")
            resultText = "Loaded users: \\(users.count)"
        } catch {
            resultText = "Error: \\(error)"
        }
    }
}
#endif
`;
      }
    }

    return code + swiftUIDemo;
  };

  // MARK: - Pattern-specific wrappers
  const generatePatternWrapper = () => {
    const baseUrlLiteral = `URL(string: "${safeBaseURL}")!`;

    switch (pattern) {
      case 'generic':
        return `// MARK: - Generic Base API Service

class BaseAPIService {
    let adapter: NetworkAdapter
    let baseURL: URL

    init(adapter: NetworkAdapter = NetworkAdapter(config: .default), baseURL: URL = ${baseUrlLiteral}) {
        self.adapter = adapter
        self.baseURL = baseURL
    }

    // Common verb helpers
    func get<T: Decodable>(_ path: String,
                           queryItems: [URLQueryItem]? = nil,
                           headers: [String: String]? = nil) async throws -> T {
        try await apiGet(path, baseURL: baseURL, queryItems: queryItems, headers: headers, adapter: adapter)
    }

    func post<T: Decodable>(_ path: String,
                            body: Data? = nil,
                            headers: [String: String]? = nil) async throws -> T {
        try await apiPost(path, baseURL: baseURL, body: body, headers: headers, adapter: adapter)
    }

    func put<T: Decodable>(_ path: String,
                           body: Data? = nil,
                           headers: [String: String]? = nil) async throws -> T {
        try await apiPut(path, baseURL: baseURL, body: body, headers: headers, adapter: adapter)
    }

    func patch<T: Decodable>(_ path: String,
                             body: Data? = nil,
                             headers: [String: String]? = nil) async throws -> T {
        try await apiPatch(path, baseURL: baseURL, body: body, headers: headers, adapter: adapter)
    }

    func delete<T: Decodable>(_ path: String,
                              queryItems: [URLQueryItem]? = nil,
                              headers: [String: String]? = nil) async throws -> T {
        try await apiDelete(path, baseURL: baseURL, queryItems: queryItems, headers: headers, adapter: adapter)
    }
}

// MARK: - Example concrete service

final class UserService: BaseAPIService {
    func getUsers() async throws -> [User] {
        try await get("/users")
    }

    func createUser(name: String) async throws -> User {
        let body = try JSONEncoder().encode(["name": name])
        return try await post("/users", body: body)
    }
}`;

      case 'repository':
        return `// MARK: - Repository Pattern Example

protocol UserRepositoryProtocol {
    func getUsers() async throws -> [User]
    func getUser(id: String) async throws -> User
    func createUser(name: String) async throws -> User
    func deleteUser(id: String) async throws
}

final class UserRepository: UserRepositoryProtocol {
    private let adapter: NetworkAdapter
    private let baseURL: URL

    init(adapter: NetworkAdapter = NetworkAdapter(config: .default), baseURL: URL = ${baseUrlLiteral}) {
        self.adapter = adapter
        self.baseURL = baseURL
    }

    func getUsers() async throws -> [User] {
        try await apiGet("/users", baseURL: baseURL, adapter: adapter)
    }

    func getUser(id: String) async throws -> User {
        try await apiGet("/users/\\(id)", baseURL: baseURL, adapter: adapter)
    }

    func createUser(name: String) async throws -> User {
        let body = try JSONEncoder().encode(["name": name])
        return try await apiPost("/users", baseURL: baseURL, body: body, adapter: adapter)
    }

    func deleteUser(id: String) async throws {
        let _: EmptyResponse = try await apiDelete("/users/\\(id)", baseURL: baseURL, adapter: adapter)
    }
}

struct EmptyResponse: Decodable {}`;

      case 'functional':
        return `// MARK: - Functional API Style
// You can call the generic helpers directly, for example:
//
// let users: [User] = try await apiGet("/users", baseURL: URL(string: "${safeBaseURL}")!)
//
// or wrap them in your own namespaced API if desired:
//
// enum API {
//     static func getUsers() async throws -> [User] {
//         try await apiGet("/users", baseURL: URL(string: "${safeBaseURL}")!)
//     }
// }`;

      case 'interceptor':
        return `// MARK: - Interceptor-based API Client

protocol RequestInterceptor {
    func intercept(_ request: URLRequest) throws -> URLRequest
}

protocol ResponseInterceptor {
    func intercept(_ response: URLResponse, data: Data) throws
}

final class APIClient {
    private let adapter: NetworkAdapter
    private let baseURL: URL
    private var requestInterceptors: [RequestInterceptor] = []
    private var responseInterceptors: [ResponseInterceptor] = []

    init(adapter: NetworkAdapter = NetworkAdapter(config: .default), baseURL: URL = ${baseUrlLiteral}) {
        self.adapter = adapter
        self.baseURL = baseURL
    }

    func addRequestInterceptor(_ interceptor: RequestInterceptor) {
        requestInterceptors.append(interceptor)
    }

    func addResponseInterceptor(_ interceptor: ResponseInterceptor) {
        responseInterceptors.append(interceptor)
    }

    // Example GET helper with interceptor hooks
    func get<T: Decodable>(_ path: String,
                           queryItems: [URLQueryItem]? = nil,
                           headers: [String: String]? = nil) async throws -> T {
        let endpoint = AnyEndpoint(
            baseURL: baseURL,
            path: path,
            method: .get,
            headers: headers,
            queryItems: queryItems,
            body: nil
        )

        var request = try URLRequest(url: endpoint.baseURL.appendingPathComponent(endpoint.path))
        request.httpMethod = endpoint.method.rawValue

        // Apply request interceptors
        for interceptor in requestInterceptors {
            request = try interceptor.intercept(request)
        }

        // Delegate to NetworkAdapter for execution
        let result: T = try await apiRequest(.get, path: path, baseURL: baseURL, queryItems: queryItems, headers: headers, adapter: adapter)

        // Response interceptors would be applied inside NetworkAdapter or via a custom hook
        // This scaffold shows where to add them.

        return result
    }
}`;

      case 'singleton':
      default:
        return `// MARK: - Singleton API Service

final class APIService {
    static let shared = APIService(adapter: .shared, baseURL: ${baseUrlLiteral})

    let adapter: NetworkAdapter
    let baseURL: URL

    init(adapter: NetworkAdapter = .shared, baseURL: URL = ${baseUrlLiteral}) {
        self.adapter = adapter
        self.baseURL = baseURL
    }

    func get<T: Decodable>(_ path: String,
                           queryItems: [URLQueryItem]? = nil,
                           headers: [String: String]? = nil) async throws -> T {
        try await apiGet(path, baseURL: baseURL, queryItems: queryItems, headers: headers, adapter: adapter)
    }

    func post<T: Decodable>(_ path: String,
                            body: Data? = nil,
                            headers: [String: String]? = nil) async throws -> T {
        try await apiPost(path, baseURL: baseURL, body: body, headers: headers, adapter: adapter)
    }

    func put<T: Decodable>(_ path: String,
                           body: Data? = nil,
                           headers: [String: String]? = nil) async throws -> T {
        try await apiPut(path, baseURL: baseURL, body: body, headers: headers, adapter: adapter)
    }

    func patch<T: Decodable>(_ path: String,
                             body: Data? = nil,
                             headers: [String: String]? = nil) async throws -> T {
        try await apiPatch(path, baseURL: baseURL, body: body, headers: headers, adapter: adapter)
    }

    func delete<T: Decodable>(_ path: String,
                              queryItems: [URLQueryItem]? = nil,
                              headers: [String: String]? = nil) async throws -> T {
        try await apiDelete(path, baseURL: baseURL, queryItems: queryItems, headers: headers, adapter: adapter)
    }
}`;
    }
  };
  // Generate example Codable models
  const generateExampleModels = () => {
    return `// MARK: - Example Codable Models

// Replace these with your actual API response models

struct User: Codable {
    let id: String
    let name: String
    let email: String
    let createdAt: String?
}

struct Product: Codable {
    let id: String
    let name: String
    let price: Double
    let description: String?
}

struct AuthResponse: Codable {
    let token: String
    let refreshToken: String?
    let user: User
    let expiresIn: Int?
}

struct APIResponse<T: Codable>: Codable {
    let data: T?
    let message: String?
    let success: Bool
}

struct ErrorResponse: Codable {
    let error: String
    let code: Int?
    let message: String?
}`;
  };


  // Generate validation helpers
  // eslint-disable-next-line no-unused-vars
  const generateValidationHelpers = () => {
    return `// MARK: - Validation Helpers

struct URLValidator {
    static func isValid(_ urlString: String) -> Bool {
        guard let url = URL(string: urlString),
              let scheme = url.scheme,
              ["http", "https"].contains(scheme) else {
            return false
        }
        return true
    }
}

struct HTTPMethodValidator {
    static func isValid(_ method: String) -> Bool {
        let validMethods = ["GET", "POST", "PUT", "PATCH", "DELETE"]
        return validMethods.contains(method.uppercased())
    }
}

struct QueryParamEncoder {
    static func encode(_ params: [String: String]) -> String {
        guard !params.isEmpty else { return "" }
        return params.map { key, value in
            let encodedKey = key.addingPercentEncoding(withAllowedCharacters: .urlQueryAllowed) ?? key
            let encodedValue = value.addingPercentEncoding(withAllowedCharacters: .urlQueryAllowed) ?? value
            return "\\(encodedKey)=\\(encodedValue)"
        }.joined(separator: "&")
    }
}

struct FormDataEncoder {
    static func encode(_ params: [String: String]) -> Data? {
        let formString = params.map { key, value in
            let encodedKey = key.addingPercentEncoding(withAllowedCharacters: .urlQueryAllowed) ?? key
            let encodedValue = value.addingPercentEncoding(withAllowedCharacters: .urlQueryAllowed) ?? value
            return "\\(encodedKey)=\\(encodedValue)"
        }.joined(separator: "&")
        return formString.data(using: .utf8)
    }
}

struct MultipartFormDataBuilder {
    private var body = Data()
    private let boundary: String
    
    init(boundary: String? = nil) {
        self.boundary = boundary ?? "Boundary-\\(UUID().uuidString)"
    }
    
    mutating func addField(name: String, value: String) {
        body.append("--\\(boundary)\\r\\n".data(using: .utf8)!)
        body.append("Content-Disposition: form-data; name=\\"\\(name)\\"\\r\\n".data(using: .utf8)!)
        body.append("\\r\\n".data(using: .utf8)!)
        body.append(value.data(using: .utf8)!)
        body.append("\\r\\n".data(using: .utf8)!)
    }
    
    mutating func addFile(name: String, filename: String, data: Data, mimeType: String = "application/octet-stream") {
        body.append("--\\(boundary)\\r\\n".data(using: .utf8)!)
        body.append("Content-Disposition: form-data; name=\\"\\(name)\\"; filename=\\"\\(filename)\\"\\r\\n".data(using: .utf8)!)
        body.append("Content-Type: \\(mimeType)\\r\\n".data(using: .utf8)!)
        body.append("\\r\\n".data(using: .utf8)!)
        body.append(data)
        body.append("\\r\\n".data(using: .utf8)!)
    }
    
    func build() -> (data: Data, boundary: String) {
        var finalBody = body
        finalBody.append("--\\(boundary)--\\r\\n".data(using: .utf8)!)
        return (finalBody, boundary)
    }
}`;
  };

  // Generate enhanced error handling
  // eslint-disable-next-line no-unused-vars
  const generateErrorHandling = () => {
    return `// MARK: - Error Handling

enum APIError: Error, LocalizedError {
    case invalidURL
    case invalidResponse
    case networkError(Error)
    case httpError(Int, String?)
    case decodingError(Error)
    case timeout
    case cancelled
    case invalidMethod
    case invalidBody
    
    var errorDescription: String? {
        switch self {
        case .invalidURL:
            return "Invalid URL format"
        case .invalidResponse:
            return "Invalid HTTP response"
        case .networkError(let error):
            return "Network error: \\(error.localizedDescription)"
        case .httpError(let code, let message):
            return "HTTP \\(code): \\(message ?? "Unknown error")"
        case .decodingError(let error):
            return "Decoding error: \\(error.localizedDescription)"
        case .timeout:
            return "Request timeout"
        case .cancelled:
            return "Request cancelled"
        case .invalidMethod:
            return "Invalid HTTP method"
        case .invalidBody:
            return "Invalid request body"
        }
    }
    
    var statusCode: Int? {
        if case .httpError(let code, _) = self {
            return code
        }
        return nil
    }
}`;
  };

  // Pattern 1: Singleton - Fully Dynamic
  const generateSingletonPattern = () => {
    if (useAsyncAwait) {
      return `// MARK: - Core API Service
// MARK: - Singleton API Client (Dynamic)

class APIService {
    static let shared = APIService()
    private let baseURL: String
    private var defaultHeaders: [String: String] = [:]
    ${enableLogging ? 'private let enableLogging = true' : ''}
    
    init(baseURL: String = "${safeBaseURL}") {
        self.baseURL = baseURL
        self.defaultHeaders = ["Content-Type": "application/json"]
    }
    
    func setDefaultHeaders(_ headers: [String: String]) {
        self.defaultHeaders = headers
    }
    
    // MARK: - Dynamic Request Method
    func request<T: Codable>(
        endpoint: String,
        method: HTTPMethod = .get,
        headers: [String: String]? = nil,
        queryParams: [String: String]? = nil,
        body: (any Codable)? = nil
    ) async throws -> T {
        var urlString = baseURL + endpoint
        
        // Add query parameters
        if let params = queryParams, !params.isEmpty {
            let queryString = QueryParamEncoder.encode(params)
            urlString += (urlString.contains("?") ? "&" : "?") + queryString
        }
        
        guard URLValidator.isValid(urlString), let url = URL(string: urlString) else {
            throw APIError.invalidURL
        }
        
        var request = URLRequest(url: url)
        request.httpMethod = method.rawValue
        request.timeoutInterval = ${timeout}
        
        // Apply default headers
        for (key, value) in defaultHeaders {
            request.setValue(value, forHTTPHeaderField: key)
        }
        
        // Apply custom headers (override defaults)
        if let headers = headers {
            for (key, value) in headers {
                request.setValue(value, forHTTPHeaderField: key)
            }
        }
        
        // Encode body if provided
        if let body = body {
            do {
                request.httpBody = try JSONEncoder().encode(body)
            } catch {
                throw APIError.invalidBody
            }
        }
        
        ${enableLogging ? `print("[API] \\(method.rawValue) \\(urlString)")` : ''}
        
        do {
            let (data, response) = try await URLSession.shared.data(for: request)
            
            guard let httpResponse = response as? HTTPURLResponse else {
                throw APIError.invalidResponse
            }
            
            ${enableLogging ? `print("[API] Response: \\(httpResponse.statusCode)")` : ''}
            
            guard (200...299).contains(httpResponse.statusCode) else {
                let errorMessage = String(data: data, encoding: .utf8)
                throw APIError.httpError(httpResponse.statusCode, errorMessage)
            }
            
            do {
                let decoder = JSONDecoder()
                return try decoder.decode(T.self, from: data)
            } catch {
                throw APIError.decodingError(error)
            }
        } catch let error as APIError {
            throw error
        } catch {
            throw APIError.networkError(error)
        }
    }
    
    // Convenience method for raw Data response
    func requestData(
        endpoint: String,
        method: HTTPMethod = .get,
        headers: [String: String]? = nil,
        queryParams: [String: String]? = nil,
        body: Data? = nil
    ) async throws -> Data {
        var urlString = baseURL + endpoint
        
        if let params = queryParams, !params.isEmpty {
            let queryString = QueryParamEncoder.encode(params)
            urlString += (urlString.contains("?") ? "&" : "?") + queryString
        }
        
        guard URLValidator.isValid(urlString), let url = URL(string: urlString) else {
            throw APIError.invalidURL
        }
        
        var request = URLRequest(url: url)
        request.httpMethod = method.rawValue
        request.timeoutInterval = ${timeout}
        
        for (key, value) in defaultHeaders {
            request.setValue(value, forHTTPHeaderField: key)
        }
        
        if let headers = headers {
            for (key, value) in headers {
                request.setValue(value, forHTTPHeaderField: key)
            }
        }
        
        if let body = body {
            request.httpBody = body
        }
        
        let (data, response) = try await URLSession.shared.data(for: request)
        
        guard let httpResponse = response as? HTTPURLResponse,
              (200...299).contains(httpResponse.statusCode) else {
            let errorMessage = String(data: data, encoding: .utf8)
            throw APIError.httpError((response as? HTTPURLResponse)?.statusCode ?? 0, errorMessage)
        }
        
        return data
    }
}`;
    } else {
      return `// MARK: - Core API Service
// MARK: - Singleton API Client (Dynamic)

import Combine

class APIService {
    static let shared = APIService()
    private let baseURL: String
    private var defaultHeaders: [String: String] = [:]
    ${enableLogging ? 'private let enableLogging = true' : ''}
    
    init(baseURL: String = "${safeBaseURL}") {
        self.baseURL = baseURL
        self.defaultHeaders = ["Content-Type": "application/json"]
    }
    
    func setDefaultHeaders(_ headers: [String: String]) {
        self.defaultHeaders = headers
    }
    
    func request<T: Codable>(
        endpoint: String,
        method: HTTPMethod = .get,
        headers: [String: String]? = nil,
        queryParams: [String: String]? = nil,
        body: (any Codable)? = nil
    ) -> AnyPublisher<T, APIError> {
        var urlString = baseURL + endpoint
        
        if let params = queryParams, !params.isEmpty {
            let queryString = QueryParamEncoder.encode(params)
            urlString += (urlString.contains("?") ? "&" : "?") + queryString
        }
        
        guard URLValidator.isValid(urlString), let url = URL(string: urlString) else {
            return Fail(error: APIError.invalidURL).eraseToAnyPublisher()
        }
        
        var request = URLRequest(url: url)
        request.httpMethod = method.rawValue
        request.timeoutInterval = ${timeout}
        
        for (key, value) in defaultHeaders {
            request.setValue(value, forHTTPHeaderField: key)
        }
        
        if let headers = headers {
            for (key, value) in headers {
                request.setValue(value, forHTTPHeaderField: key)
            }
        }
        
        if let body = body {
            do {
                request.httpBody = try JSONEncoder().encode(body)
            } catch {
                return Fail(error: APIError.invalidBody).eraseToAnyPublisher()
            }
        }
        
        return URLSession.shared.dataTaskPublisher(for: request)
            .tryMap { data, response -> Data in
                guard let httpResponse = response as? HTTPURLResponse else {
                    throw APIError.invalidResponse
                }
                
                guard (200...299).contains(httpResponse.statusCode) else {
                    let errorMessage = String(data: data, encoding: .utf8)
                    throw APIError.httpError(httpResponse.statusCode, errorMessage)
                }
                
                return data
            }
            .decode(type: T.self, decoder: JSONDecoder())
            .mapError { error -> APIError in
                if let apiError = error as? APIError {
                    return apiError
                }
                if error is DecodingError {
                    return APIError.decodingError(error)
                }
                return APIError.networkError(error)
            }
            .eraseToAnyPublisher()
    }
}`;
    }
  };

  // Pattern 2: Generic Base Class - Fully Dynamic
  const generateGenericPattern = () => {
    return `// MARK: - Core API Service
// MARK: - Generic Base API Service (Dynamic)

class BaseAPIService {
    protected let baseURL: String
    protected var defaultHeaders: [String: String]
    ${enableLogging ? 'protected let enableLogging: Bool' : ''}
    
    init(baseURL: String, defaultHeaders: [String: String] = [:]) {
        self.baseURL = baseURL
        self.defaultHeaders = defaultHeaders.isEmpty ? ["Content-Type": "application/json"] : defaultHeaders
        ${enableLogging ? 'self.enableLogging = true' : ''}
    }
    
    // MARK: - Dynamic Request Method
    func request<T: Codable>(
        endpoint: String,
        method: HTTPMethod = .get,
        headers: [String: String]? = nil,
        queryParams: [String: String]? = nil,
        body: (any Codable)? = nil
    ) async throws -> T {
        var urlString = baseURL + endpoint
        
        if let params = queryParams, !params.isEmpty {
            let queryString = QueryParamEncoder.encode(params)
            urlString += (urlString.contains("?") ? "&" : "?") + queryString
        }
        
        guard URLValidator.isValid(urlString), let url = URL(string: urlString) else {
            throw APIError.invalidURL
        }
        
        var request = URLRequest(url: url)
        request.httpMethod = method.rawValue
        request.timeoutInterval = ${timeout}
        
        // Apply default headers
        for (key, value) in defaultHeaders {
            request.setValue(value, forHTTPHeaderField: key)
        }
        
        // Apply custom headers
        if let headers = headers {
            for (key, value) in headers {
                request.setValue(value, forHTTPHeaderField: key)
            }
        }
        
        // Encode body
        if let body = body {
            do {
                request.httpBody = try JSONEncoder().encode(body)
            } catch {
                throw APIError.invalidBody
            }
        }
        
        ${enableLogging ? `print("[API] \\(method.rawValue) \\(urlString)")` : ''}
        
        let (data, response) = try await URLSession.shared.data(for: request)
        
        guard let httpResponse = response as? HTTPURLResponse else {
            throw APIError.invalidResponse
        }
        
        ${enableLogging ? `print("[API] Response: \\(httpResponse.statusCode)")` : ''}
        
        guard (200...299).contains(httpResponse.statusCode) else {
            let errorMessage = String(data: data, encoding: .utf8)
            throw APIError.httpError(httpResponse.statusCode, errorMessage)
        }
        
        do {
            let decoder = JSONDecoder()
            return try decoder.decode(T.self, from: data)
        } catch {
            throw APIError.decodingError(error)
        }
    }
}

// MARK: - Feature-Specific Services (Examples)

class AuthService: BaseAPIService {
    init(baseURL: String = "${safeBaseURL}") {
        super.init(baseURL: baseURL, defaultHeaders: ["Content-Type": "application/json"])
    }
    
    // Convenience methods that use the dynamic request method
    func login(email: String, password: String) async throws -> AuthResponse {
        struct LoginRequest: Codable {
            let email: String
            let password: String
        }
        
        let requestBody = LoginRequest(email: email, password: password)
        return try await request<AuthResponse>(
            endpoint: "/auth/login",
            method: .post,
            body: requestBody
        )
    }
    
    func refreshToken(refreshToken: String) async throws -> AuthResponse {
        struct RefreshRequest: Codable {
            let refreshToken: String
        }
        
        return try await request<AuthResponse>(
            endpoint: "/auth/refresh",
            method: .post,
            body: RefreshRequest(refreshToken: refreshToken)
        )
    }
}

class UserService: BaseAPIService {
    init(baseURL: String = "${safeBaseURL}") {
        super.init(baseURL: baseURL)
    }
    
    func getUser(id: String) async throws -> User {
        return try await request<User>(
            endpoint: "/users/\\(id)",
            method: .get
        )
    }
    
    func getUsers(queryParams: [String: String]? = nil) async throws -> [User] {
        return try await request<[User]>(
            endpoint: "/users",
            method: .get,
            queryParams: queryParams
        )
    }
    
    func createUser(_ user: User) async throws -> User {
        return try await request<User>(
            endpoint: "/users",
            method: .post,
            body: user
        )
    }
    
    func updateUser(id: String, user: User) async throws -> User {
        return try await request<User>(
            endpoint: "/users/\\(id)",
            method: .put,
            body: user
        )
    }
    
    func deleteUser(id: String) async throws -> Void {
        struct EmptyResponse: Codable {}
        _ = try await request<EmptyResponse>(
            endpoint: "/users/\\(id)",
            method: .delete
        )
    }
}`;
  };

  // Pattern 3: Repository Pattern
  const generateRepositoryPattern = () => {
    return `// MARK: - Core API Service
// MARK: - Repository Pattern

protocol APIRepository {
    func request<T: Codable>(
        endpoint: String,
        method: HTTPMethod,
        headers: [String: String]? = nil,
        queryParams: [String: String]? = nil,
        body: Data? = nil
    ) async throws -> T
}

class DefaultAPIRepository: APIRepository {
    private let baseURL: String
    private let session: URLSession
    private var defaultHeaders: [String: String] = [:]
    ${enableLogging ? 'private let enableLogging = true' : ''}
    
    init(baseURL: String, session: URLSession = .shared) {
        self.baseURL = baseURL
        self.session = session
        self.defaultHeaders = ["Content-Type": "application/json"]
    }
    
    func request<T: Codable>(
        endpoint: String,
        method: HTTPMethod = .get,
        headers: [String: String]? = nil,
        queryParams: [String: String]? = nil,
        body: Data? = nil
    ) async throws -> T {
        var urlString = baseURL + endpoint
        
        // Add query parameters
        if let params = queryParams, !params.isEmpty {
            let queryString = QueryParamEncoder.encode(params)
            urlString += (urlString.contains("?") ? "&" : "?") + queryString
        }
        
        guard URLValidator.isValid(urlString), let url = URL(string: urlString) else {
            throw APIError.invalidURL
        }
        
        var request = URLRequest(url: url)
        request.httpMethod = method.rawValue
        request.timeoutInterval = ${timeout}
        
        // Apply default headers
        for (key, value) in defaultHeaders {
            request.setValue(value, forHTTPHeaderField: key)
        }
        
        // Apply custom headers (override defaults)
        if let headers = headers {
            for (key, value) in headers {
                request.setValue(value, forHTTPHeaderField: key)
            }
        }
        
        if let body = body {
            request.httpBody = body
        }
        
        ${enableLogging ? `print("[API] \\(method.rawValue) \\(urlString)")` : ''}
        
        let (data, response) = try await session.data(for: request)
        
        guard let httpResponse = response as? HTTPURLResponse else {
            throw APIError.invalidResponse
        }
        
        ${enableLogging ? `print("[API] Response: \\(httpResponse.statusCode)")` : ''}
        
        guard (200...299).contains(httpResponse.statusCode) else {
            let errorMessage = String(data: data, encoding: .utf8)
            throw APIError.httpError(httpResponse.statusCode, errorMessage)
        }
        
        do {
            return try JSONDecoder().decode(T.self, from: data)
        } catch {
            throw APIError.decodingError(error)
        }
    }
}

// MARK: - Feature Repositories

class UserRepository {
    private let apiRepository: APIRepository
    
    init(apiRepository: APIRepository) {
        self.apiRepository = apiRepository
    }
    
    // Convenience methods that use the dynamic request method
    func getUser(id: String) async throws -> User {
        return try await apiRepository.request<User>(
            endpoint: "/users/\\(id)",
            method: .get
        )
    }
    
    func getUsers(queryParams: [String: String]? = nil) async throws -> [User] {
        return try await apiRepository.request<[User]>(
            endpoint: "/users",
            method: .get,
            queryParams: queryParams
        )
    }
    
    func createUser(_ user: User) async throws -> User {
        let body = try JSONEncoder().encode(user)
        return try await apiRepository.request<User>(
            endpoint: "/users",
            method: .post,
            body: body
        )
    }
    
    func updateUser(id: String, user: User) async throws -> User {
        let body = try JSONEncoder().encode(user)
        return try await apiRepository.request<User>(
            endpoint: "/users/\\(id)",
            method: .put,
            body: body
        )
    }
    
    func deleteUser(id: String) async throws -> Void {
        struct EmptyResponse: Codable {}
        _ = try await apiRepository.request<EmptyResponse>(
            endpoint: "/users/\\(id)",
            method: .delete
        )
    }
}

// Example usage
let apiRepo = DefaultAPIRepository(baseURL: "${safeBaseURL}")
let userRepo = UserRepository(apiRepository: apiRepo)`;
  };

  // Pattern 4: Functional Helpers
  const generateFunctionalPattern = () => {
    return `// MARK: - Core API Service
// MARK: - Functional API Helpers

struct APIHelpers {
    static func request(
        baseURL: String,
        endpoint: String,
        method: HTTPMethod = .get,
        headers: [String: String] = [:],
        queryParams: [String: String]? = nil,
        body: Data? = nil,
        timeout: TimeInterval = ${timeout}
    ) async throws -> Data {
        var urlString = baseURL + endpoint
        
        // Add query parameters
        if let params = queryParams, !params.isEmpty {
            let queryString = QueryParamEncoder.encode(params)
            urlString += (urlString.contains("?") ? "&" : "?") + queryString
        }
        
        guard URLValidator.isValid(urlString), let url = URL(string: urlString) else {
            throw APIError.invalidURL
        }
        
        var request = URLRequest(url: url)
        request.httpMethod = method.rawValue
        request.timeoutInterval = timeout
        
        // Apply headers
        for (key, value) in headers {
            request.setValue(value, forHTTPHeaderField: key)
        }
        
        if let body = body {
            request.httpBody = body
        }
        
        ${enableLogging ? `print("[API] \\(method.rawValue) \\(urlString)")` : ''}
        
        let (data, response) = try await URLSession.shared.data(for: request)
        
        guard let httpResponse = response as? HTTPURLResponse else {
            throw APIError.invalidResponse
        }
        
        ${enableLogging ? `print("[API] Response: \\(httpResponse.statusCode)")` : ''}
        
        guard (200...299).contains(httpResponse.statusCode) else {
            let errorMessage = String(data: data, encoding: .utf8)
            throw APIError.httpError(httpResponse.statusCode, errorMessage)
        }
        
        return data
    }
    
    static func requestJSON<T: Codable>(
        baseURL: String,
        endpoint: String,
        method: HTTPMethod = .get,
        headers: [String: String] = [:],
        queryParams: [String: String]? = nil,
        body: Data? = nil
    ) async throws -> T {
        let data = try await request(
            baseURL: baseURL,
            endpoint: endpoint,
            method: method,
            headers: headers,
            queryParams: queryParams,
            body: body
        )
        
        do {
            return try JSONDecoder().decode(T.self, from: data)
        } catch {
            throw APIError.decodingError(error)
        }
    }
    
    // Convenience method for Codable body
    static func requestJSON<T: Codable, U: Codable>(
        baseURL: String,
        endpoint: String,
        method: HTTPMethod = .get,
        headers: [String: String] = [:],
        queryParams: [String: String]? = nil,
        body: T? = nil
    ) async throws -> U {
        var bodyData: Data? = nil
        if let body = body {
            bodyData = try JSONEncoder().encode(body)
        }
        
        return try await requestJSON<U>(
            baseURL: baseURL,
            endpoint: endpoint,
            method: method,
            headers: headers,
            queryParams: queryParams,
            body: bodyData
        )
    }
}`;
  };

  // Pattern 5: Interceptor-Based
  const generateInterceptorPattern = () => {
    const retryCode = enableRetry ? `
    // Retry logic with exponential backoff
    private func retryRequest<T>(
        _ request: URLRequest,
        maxRetries: Int = 3,
        currentRetry: Int = 0
    ) async throws -> (Data, URLResponse) {
        do {
            return try await URLSession.shared.data(for: request)
        } catch {
            if currentRetry < maxRetries {
                let delay = pow(2.0, Double(currentRetry))
                try await Task.sleep(nanoseconds: UInt64(delay * 1_000_000_000))
                return try await retryRequest(request, maxRetries: maxRetries, currentRetry: currentRetry + 1)
            }
            throw error
        }
    }` : '';

    return `// MARK: - Core API Service
// MARK: - Interceptor-Based API Service

protocol RequestInterceptor {
    func intercept(_ request: inout URLRequest) throws
}

protocol ResponseInterceptor {
    func intercept(_ response: URLResponse, data: Data) throws
}

class APIService {
    private let baseURL: String
    private var requestInterceptors: [RequestInterceptor] = []
    private var responseInterceptors: [ResponseInterceptor] = []
    ${enableLogging ? 'private let enableLogging = true' : ''}
    
    init(baseURL: String) {
        self.baseURL = baseURL
    }
    
    func addRequestInterceptor(_ interceptor: RequestInterceptor) {
        requestInterceptors.append(interceptor)
    }
    
    func addResponseInterceptor(_ interceptor: ResponseInterceptor) {
        responseInterceptors.append(interceptor)
    }
    
    // MARK: - Dynamic Request Method
    func request<T: Codable>(
        endpoint: String,
        method: HTTPMethod = .get,
        headers: [String: String]? = nil,
        queryParams: [String: String]? = nil,
        body: (any Codable)? = nil
    ) async throws -> T {
        var urlString = baseURL + endpoint
        
        // Add query parameters
        if let params = queryParams, !params.isEmpty {
            let queryString = QueryParamEncoder.encode(params)
            urlString += (urlString.contains("?") ? "&" : "?") + queryString
        }
        
        guard URLValidator.isValid(urlString), let url = URL(string: urlString) else {
            throw APIError.invalidURL
        }
        
        var request = URLRequest(url: url)
        request.httpMethod = method.rawValue
        request.timeoutInterval = ${timeout}
        
        // Apply default headers
        request.setValue("application/json", forHTTPHeaderField: "Content-Type")
        
        // Apply custom headers
        if let headers = headers {
            for (key, value) in headers {
                request.setValue(value, forHTTPHeaderField: key)
            }
        }
        
        // Encode body if provided
        if let body = body {
            do {
                request.httpBody = try JSONEncoder().encode(body)
            } catch {
                throw APIError.invalidBody
            }
        }
        
        // Apply request interceptors
        for interceptor in requestInterceptors {
            try interceptor.intercept(&request)
        }
        
        ${enableLogging ? `print("[API] \\(method.rawValue) \\(urlString)")` : ''}
        
        ${enableRetry ? `let (data, response) = try await retryRequest(request)` : `let (data, response) = try await URLSession.shared.data(for: request)`}
        
        // Apply response interceptors
        for interceptor in responseInterceptors {
            try interceptor.intercept(response, data: data)
        }
        
        guard let httpResponse = response as? HTTPURLResponse else {
            throw APIError.invalidResponse
        }
        
        ${enableLogging ? `print("[API] Response: \\(httpResponse.statusCode)")` : ''}
        
        guard (200...299).contains(httpResponse.statusCode) else {
            let errorMessage = String(data: data, encoding: .utf8)
            throw APIError.httpError(httpResponse.statusCode, errorMessage)
        }
        
        do {
            let decoder = JSONDecoder()
            return try decoder.decode(T.self, from: data)
        } catch {
            throw APIError.decodingError(error)
        }
    }
    
    // Convenience method for raw Data response
    func requestData(
        endpoint: String,
        method: HTTPMethod = .get,
        headers: [String: String]? = nil,
        queryParams: [String: String]? = nil,
        body: Data? = nil
    ) async throws -> Data {
        var urlString = baseURL + endpoint
        
        if let params = queryParams, !params.isEmpty {
            let queryString = QueryParamEncoder.encode(params)
            urlString += (urlString.contains("?") ? "&" : "?") + queryString
        }
        
        guard URLValidator.isValid(urlString), let url = URL(string: urlString) else {
            throw APIError.invalidURL
        }
        
        var request = URLRequest(url: url)
        request.httpMethod = method.rawValue
        request.timeoutInterval = ${timeout}
        
        if let headers = headers {
            for (key, value) in headers {
                request.setValue(value, forHTTPHeaderField: key)
            }
        }
        
        if let body = body {
            request.httpBody = body
        }
        
        // Apply request interceptors
        for interceptor in requestInterceptors {
            try interceptor.intercept(&request)
        }
        
        ${enableRetry ? `let (data, response) = try await retryRequest(request)` : `let (data, response) = try await URLSession.shared.data(for: request)`}
        
        // Apply response interceptors
        for interceptor in responseInterceptors {
            try interceptor.intercept(response, data: data)
        }
        
        guard let httpResponse = response as? HTTPURLResponse else {
            throw APIError.invalidResponse
        }
        
        guard (200...299).contains(httpResponse.statusCode) else {
            let errorMessage = String(data: data, encoding: .utf8)
            throw APIError.httpError(httpResponse.statusCode, errorMessage)
        }
        
        return data
    }${retryCode}
}

// MARK: - Example Interceptors

class AuthInterceptor: RequestInterceptor {
    private let token: String
    
    init(token: String) {
        self.token = token
    }
    
    func intercept(_ request: inout URLRequest) throws {
        request.setValue("Bearer \\(token)", forHTTPHeaderField: "Authorization")
    }
}

class LoggingInterceptor: ResponseInterceptor {
    func intercept(_ response: URLResponse, data: Data) throws {
        if let httpResponse = response as? HTTPURLResponse {
            print("[API] Response: \\(httpResponse.statusCode)")
        }
    }
}

class TokenRefreshInterceptor: ResponseInterceptor {
    func intercept(_ response: URLResponse, data: Data) throws {
        if let httpResponse = response as? HTTPURLResponse,
           httpResponse.statusCode == 401 {
            // Trigger token refresh logic here
            throw APIError.httpError(401, "Unauthorized - token refresh needed")
        }
    }
}

// Usage example
let apiService = APIService(baseURL: "${safeBaseURL}")
apiService.addRequestInterceptor(AuthInterceptor(token: "your-token"))
apiService.addResponseInterceptor(LoggingInterceptor())
apiService.addResponseInterceptor(TokenRefreshInterceptor())`;
  };

  // Select pattern code generator
  // eslint-disable-next-line no-unused-vars
  const generatePatternCode = () => {
    switch (pattern) {
      case 'singleton':
        return generateSingletonPattern();
      case 'generic':
        return generateGenericPattern();
      case 'repository':
        return generateRepositoryPattern();
      case 'functional':
        return generateFunctionalPattern();
      case 'interceptor':
        return generateInterceptorPattern();
      default:
        return generateSingletonPattern();
    }
  };
  
  // Generate comprehensive usage examples
  // eslint-disable-next-line no-unused-vars
  const generateUsageExample = () => {
    // Generic placeholders for query parameters, headers, and body.
    // These can be wired to actual configuration/state in the future if needed.
    const queryParamsDict = {};
    const headersDict = {};
    const bodyCode = '';

    const hasQueryParams = Object.keys(queryParamsDict).length > 0;
    const hasHeaders = Object.keys(headersDict).length > 0;
    
    if (useAsyncAwait) {
      let exampleCode = '';
      
      if (pattern === 'singleton') {
        exampleCode = `// Example 1: GET request with query parameters
do {
    let users: [User] = try await APIService.shared.request(
        endpoint: "/users",
        method: .get,
        queryParams: ${hasQueryParams ? JSON.stringify(queryParamsDict) : '{ "page": "1", "limit": "10" }'}
    )
    print("Fetched \\(users.count) users")
} catch {
    print("Error: \\(error)")
}

// Example 2: POST request with JSON body
do {
    ${bodyCode ? bodyCode + '\n    ' : 'struct CreateUserRequest: Codable {\n        let name: String\n        let email: String\n    }\n    let requestBody = CreateUserRequest(name: "John Doe", email: "john@example.com")\n    '}let user: User = try await APIService.shared.request(
        endpoint: "/users",
        method: .post,
        body: ${bodyCode ? 'requestBody' : 'requestBody'}
    )
    print("Created user: \\(user.name)")
} catch {
    print("Error: \\(error)")
}

// Example 3: GET request with custom headers
do {
    let product: Product = try await APIService.shared.request(
        endpoint: "/products/123",
        method: .get,
        headers: ${hasHeaders ? JSON.stringify(headersDict) : '{ "Authorization": "Bearer token123" }'}
    )
    print("Product: \\(product.name)")
} catch {
    print("Error: \\(error)")
}

// Example 4: PUT request to update resource
do {
    struct UpdateUserRequest: Codable {
        let name: String
        let email: String
    }
    let updateBody = UpdateUserRequest(name: "Jane Doe", email: "jane@example.com")
    let updatedUser: User = try await APIService.shared.request(
        endpoint: "/users/123",
        method: .put,
        body: updateBody
    )
    print("Updated user: \\(updatedUser.name)")
} catch {
    print("Error: \\(error)")
}

// Example 5: DELETE request
do {
    struct EmptyResponse: Codable {}
    _ = try await APIService.shared.request<EmptyResponse>(
        endpoint: "/users/123",
        method: .delete
    )
    print("User deleted successfully")
} catch {
    print("Error: \\(error)")
}`;
      } else if (pattern === 'generic') {
        exampleCode = `// Example: Using feature-specific services
let authService = AuthService(baseURL: "${safeBaseURL}")
let userService = UserService(baseURL: "${safeBaseURL}")

// Login
do {
    let authResponse: AuthResponse = try await authService.request(
        endpoint: "/auth/login",
        method: .post,
        body: LoginRequest(email: "user@example.com", password: "password")
    )
    print("Logged in: \\(authResponse.token)")
} catch {
    print("Error: \\(error)")
}

// Get user
do {
    let user: User = try await userService.request(
        endpoint: "/users/123",
        method: .get
    )
    print("User: \\(user.name)")
} catch {
    print("Error: \\(error)")
}`;
      } else if (pattern === 'repository') {
        exampleCode = `// Example: Using repositories
let apiRepo = DefaultAPIRepository(baseURL: "${safeBaseURL}")
let userRepo = UserRepository(apiRepository: apiRepo)

// Get user
do {
    let user: User = try await userRepo.getUser(id: "123")
    print("User: \\(user.name)")
} catch {
    print("Error: \\(error)")
}

// Create user
do {
    let newUser = User(id: "456", name: "John Doe", email: "john@example.com", createdAt: nil)
    let createdUser: User = try await userRepo.createUser(newUser)
    print("Created: \\(createdUser.name)")
} catch {
    print("Error: \\(error)")
}`;
      } else if (pattern === 'functional') {
        exampleCode = `// Example 1: GET request returning Codable
do {
    let users: [User] = try await APIHelpers.requestJSON(
        baseURL: "${safeBaseURL}",
        endpoint: "/users",
        method: .get,
        queryParams: ${hasQueryParams ? JSON.stringify(queryParamsDict) : '{ "page": "1" }'}
    )
    print("Fetched \\(users.count) users")
} catch {
    print("Error: \\(error)")
}

// Example 2: POST request with Codable body
do {
    struct CreateUserRequest: Codable {
        let name: String
        let email: String
    }
    let requestBody = CreateUserRequest(name: "John", email: "john@example.com")
    let user: User = try await APIHelpers.requestJSON(
        baseURL: "${safeBaseURL}",
        endpoint: "/users",
        method: .post,
        body: requestBody
    )
    print("Created: \\(user.name)")
} catch {
    print("Error: \\(error)")
}`;
      } else if (pattern === 'interceptor') {
        exampleCode = `// Example: Using interceptor-based service
let apiService = APIService(baseURL: "${safeBaseURL}")

// Add interceptors
apiService.addRequestInterceptor(AuthInterceptor(token: "your-token"))
apiService.addResponseInterceptor(LoggingInterceptor())
apiService.addResponseInterceptor(TokenRefreshInterceptor())

// Make requests
do {
    let user: User = try await apiService.request(
        endpoint: "/users/123",
        method: .get
    )
    print("User: \\(user.name)")
} catch {
    print("Error: \\(error)")
}

do {
    let products: [Product] = try await apiService.request(
        endpoint: "/products",
        method: .get,
        queryParams: ["category": "electronics"]
    )
    print("Products: \\(products.count)")
} catch {
    print("Error: \\(error)")
}`;
      }
      
      return `// MARK: - Usage Examples

import SwiftUI

${exampleCode}

// MARK: - SwiftUI Integration Example

struct ContentView: View {
    @State private var users: [User] = []
    @State private var error: String?
    @State private var isLoading = false
    
    var body: some View {
        VStack(spacing: 20) {
            if isLoading {
                ProgressView()
            } else if let error = error {
                Text("Error: \\(error)")
                    .foregroundColor(.red)
            } else {
                List(users) { user in
                    VStack(alignment: .leading) {
                        Text(user.name)
                            .font(.headline)
                        Text(user.email)
                            .font(.subheadline)
                            .foregroundColor(.gray)
                    }
                }
            }
            
            Button("Load Users") {
                Task {
                    isLoading = true
                    do {
                        ${pattern === 'singleton' ? 
                          `let fetchedUsers: [User] = try await APIService.shared.request(endpoint: "/users", method: .get)` :
                          pattern === 'generic' ?
                          `let userService = UserService(baseURL: "${safeBaseURL}")
                        let fetchedUsers: [User] = try await userService.request(endpoint: "/users", method: .get)` :
                          pattern === 'repository' ?
                          `let apiRepo = DefaultAPIRepository(baseURL: "${safeBaseURL}")
                        let userRepo = UserRepository(apiRepository: apiRepo)
                        let fetchedUsers: [User] = try await userRepo.getUsers()` :
                          pattern === 'functional' ?
                          `let fetchedUsers: [User] = try await APIHelpers.requestJSON(baseURL: "${safeBaseURL}", endpoint: "/users", method: .get)` :
                          `let apiService = APIService(baseURL: "${safeBaseURL}")
                        let fetchedUsers: [User] = try await apiService.request(endpoint: "/users", method: .get)`}
                        await MainActor.run {
                            users = fetchedUsers
                            error = nil
                            isLoading = false
                        }
                    } catch {
                        await MainActor.run {
                            error = error.localizedDescription
                            isLoading = false
                        }
                    }
                }
            }
        }
        .padding()
    }
}`;
    } else {
      // Combine version
      return `// MARK: - Usage Examples (Combine)

import Combine
import SwiftUI

class ViewModel: ObservableObject {
    @Published var users: [User] = []
    @Published var error: String?
    private var cancellables = Set<AnyCancellable>()
    
    func loadUsers() {
        ${pattern === 'singleton' ? 
          `APIService.shared.request<[User]>(endpoint: "/users", method: .get)` :
          pattern === 'generic' ?
          `UserService(baseURL: "${safeBaseURL}").request<[User]>(endpoint: "/users", method: .get)` :
          pattern === 'repository' ?
          `UserRepository(apiRepository: DefaultAPIRepository(baseURL: "${safeBaseURL}")).getUsers()` :
          pattern === 'functional' ?
          `APIHelpers.requestJSON<[User]>(baseURL: "${safeBaseURL}", endpoint: "/users", method: .get)` :
          `APIService(baseURL: "${safeBaseURL}").request<[User]>(endpoint: "/users", method: .get)`}
            .receive(on: DispatchQueue.main)
            .sink(
                receiveCompletion: { [weak self] completion in
                    if case .failure(let err) = completion {
                        self?.error = err.localizedDescription
                    }
                },
                receiveValue: { [weak self] users in
                    self?.users = users
                }
            )
            .store(in: &cancellables)
    }
}`;
    }
  };

  // Assemble final code
  const importLines = ['import Foundation'];
  if (includeCombine) {
    importLines.push('import Combine');
  }

  const parts = [
    '// Generated by SwiftToolsKit API Generator',
    '// NetworkAdapter-based networking layer',
    importLines.join('\n'),
    '',
    generateHTTPMethodEnum(),
    '',
    generateNetworkConfig(),
    '',
    generateNetworkError(),
    '',
    generateAPIEndpointProtocol(),
    '',
    generateNetworkAdapter(),
    '',
    generateAnyEndpoint(),
    '',
    generateGenericHelpers(),
    '',
    generatePatternWrapper(),
    '',
    generateExampleModels(),
    '',
    generateExamples(),
  ];

  return parts.join('\n');
};
/**
 * Generate SwiftUI code from Storyboard XML using Python backend
 * Returns a Promise that resolves to an array of file objects: [{ name: string, content: string, sceneId: string }]
 */
export const generateStoryboardCodeAsync = async (config) => {
  const { storyboardContent } = config;

  if (!storyboardContent || !storyboardContent.trim()) {
    return [];
  }

  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(storyboardContent, 'text/xml');
    
    // Check for parsing errors
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      return [{ 
        name: 'Error.swift', 
        content: `// Error parsing storyboard XML\n// ${parserError.textContent}\n// Please ensure the XML is valid.`,
        sceneId: 'error'
      }];
    }

    // Check if backend is available
    const backendAvailable = await checkBackendAvailability();
    
    if (!backendAvailable) {
      // Backend not available, use basic conversion
      console.warn('Python backend not available, using basic conversion');
      return generateStoryboardCode(config);
    }
    
    // Try to find scenes
    const scenes = xmlDoc.querySelectorAll('scene');
    const files = [];

    if (scenes.length > 0) {
      // Process each scene with Python backend
      for (let i = 0; i < scenes.length; i++) {
        const scene = scenes[i];
        const sceneId = scene.getAttribute('sceneID') || `scene-${i}`;
        
        // Extract scene XML
        const sceneXml = new XMLSerializer().serializeToString(scene);
        
        try {
          // Parse storyboard with backend
          const parsedData = await parseStoryboardWithBackend(storyboardContent);
          
          // Analyze constraints
          const constraintAnalysis = await analyzeConstraintsWithBackend(storyboardContent);
          
          // Extract styles
          const styles = await extractStylesWithBackend(storyboardContent);
          
          // Prepare parsed data for generation
          const enhancedParsedData = {
            constraints: constraintAnalysis.constraints,
            layout: constraintAnalysis.hierarchy,
            styles: styles,
            parsedData: parsedData
          };
          
          // Generate SwiftUI code using Python backend
          const generatedCode = await generateSwiftUIWithBackend(
            enhancedParsedData,
            sceneXml,
            sceneId
          );
          
          // Extract struct name from generated code or use default
          const structMatch = generatedCode.match(/struct\s+(\w+)\s*:/);
          const structName = structMatch ? structMatch[1] : `Scene${i + 1}View`;
          
          files.push({
            name: `${structName}.swift`,
            content: generatedCode.trim(),
            sceneId: sceneId
          });
        } catch (backendError) {
          // Fallback to basic conversion for this scene
          console.warn(`Backend generation failed for scene ${sceneId}, using fallback:`, backendError);
          const fallbackFile = generateViewCodeBasic(scene, sceneId, xmlDoc);
          files.push(fallbackFile);
        }
      }
    } else {
      // No scenes found, process entire document
      const fullXml = new XMLSerializer().serializeToString(xmlDoc.documentElement);
      
      try {
        // Parse and generate using backend
        const parsedData = await parseStoryboardWithBackend(storyboardContent);
        const constraintAnalysis = await analyzeConstraintsWithBackend(storyboardContent);
        const styles = await extractStylesWithBackend(storyboardContent);
        
        const enhancedParsedData = {
          constraints: constraintAnalysis.constraints,
          layout: constraintAnalysis.hierarchy,
          styles: styles,
          parsedData: parsedData
        };
        
        const generatedCode = await generateSwiftUIWithBackend(
          enhancedParsedData,
          fullXml,
          'main-scene'
        );
        
        const structMatch = generatedCode.match(/struct\s+(\w+)\s*:/);
        const structName = structMatch ? structMatch[1] : 'ConvertedView';
        
        files.push({
          name: `${structName}.swift`,
          content: generatedCode.trim(),
          sceneId: 'main-scene'
        });
      } catch (backendError) {
        // Fallback to basic conversion
        console.warn('Backend generation failed, using fallback:', backendError);
        const fallbackFile = generateViewCodeBasic(xmlDoc.documentElement, 'main-scene', xmlDoc);
        files.push(fallbackFile);
      }
    }

    return files.length > 0 ? files : [{
      name: 'ConvertedView.swift',
      content: '// Please upload or paste a storyboard XML file to generate SwiftUI code',
      sceneId: 'default'
    }];
  } catch (e) {
    // Fallback to basic conversion on any error
    console.error('Error in backend generation, falling back to basic conversion:', e);
    return generateStoryboardCode(config);
  }
};

/**
 * Basic fallback function for generating SwiftUI code (original implementation)
 */
const generateViewCodeBasic = (containerElement, sceneId, xmlDoc) => {
  let swiftCode = 'import SwiftUI\n\n';
  
  const isScene = containerElement.tagName === 'scene' || containerElement.querySelector('viewController');
  const viewController = isScene 
    ? containerElement.querySelector('viewController')
    : containerElement.querySelector('viewController');
  const customClass = viewController?.getAttribute('customClass');
  const storyboardIdentifier = viewController?.getAttribute('storyboardIdentifier');
  const sceneIdAttr = containerElement.getAttribute?.('sceneID') || sceneId;
  
  let structName = 'ConvertedView';
  if (customClass) {
    structName = customClass.replace(/ViewController$/, '').replace(/Controller$/, '') + 'View';
  } else if (storyboardIdentifier) {
    structName = storyboardIdentifier.replace(/ViewController$/, '').replace(/Controller$/, '') + 'View';
  } else {
    const sceneName = sceneIdAttr.split('-').pop() || 'View';
    structName = sceneName.charAt(0).toUpperCase() + sceneName.slice(1) + 'View';
  }

  swiftCode += `struct ${structName}: View {\n`;
  swiftCode += '    var body: some View {\n';
  swiftCode += '        VStack(spacing: 16) {\n';

  let container = containerElement;
  if (isScene) {
    const sceneView = containerElement.querySelector('viewController view') || containerElement.querySelector('view');
    container = sceneView || containerElement;
  }

  const buttons = container === xmlDoc.documentElement 
    ? xmlDoc.querySelectorAll('button')
    : container.querySelectorAll('button');
  buttons.forEach((button) => {
    const title = button.getAttribute('title') || button.textContent?.trim() || 'Button';
    const cleanTitle = title.replace(/\s+/g, ' ').trim();
    swiftCode += `            Button("${cleanTitle}") {\n`;
    swiftCode += `                // Button action\n`;
    swiftCode += `            }\n`;
  });

  const labels = container === xmlDoc.documentElement 
    ? xmlDoc.querySelectorAll('label')
    : container.querySelectorAll('label');
  labels.forEach((label) => {
    const text = label.textContent?.trim() || label.getAttribute('text') || 'Label';
    const cleanText = text.replace(/\s+/g, ' ').trim();
    swiftCode += `            Text("${cleanText}")\n`;
  });

  const imageViews = container === xmlDoc.documentElement 
    ? xmlDoc.querySelectorAll('imageView')
    : container.querySelectorAll('imageView');
  imageViews.forEach((imageView) => {
    const imageName = imageView.getAttribute('image') || imageView.getAttribute('imageName') || 'placeholder';
    swiftCode += `            Image("${imageName}")\n`;
    swiftCode += `                .resizable()\n`;
    swiftCode += `                .scaledToFit()\n`;
  });

  const textFields = container === xmlDoc.documentElement 
    ? xmlDoc.querySelectorAll('textField')
    : container.querySelectorAll('textField');
  textFields.forEach((textField) => {
    const placeholder = textField.getAttribute('placeholder') || 'Enter text';
    swiftCode += `            TextField("${placeholder}", text: .constant(""))\n`;
  });

  const textViews = container === xmlDoc.documentElement 
    ? xmlDoc.querySelectorAll('textView')
    : container.querySelectorAll('textView');
  textViews.forEach((textView) => {
    const text = textView.textContent?.trim() || textView.getAttribute('text') || '';
    if (text) {
      swiftCode += `            Text("${text.replace(/\s+/g, ' ').trim()}")\n`;
    } else {
      swiftCode += `            TextEditor(text: .constant(""))\n`;
    }
  });

  swiftCode += '        }\n';
  swiftCode += '        .padding()\n';
  swiftCode += '    }\n';
  swiftCode += '}\n';

  swiftCode += '\n// Note: This is a basic conversion. You may need to:\n';
  swiftCode += '// - Adjust layout constraints\n';
  swiftCode += '// - Add proper state management\n';
  swiftCode += '// - Implement actions and bindings\n';
  swiftCode += '// - Style components appropriately\n';

  return {
    name: `${structName}.swift`,
    content: swiftCode,
    sceneId: sceneIdAttr
  };
};


/**
 * Generate SwiftUI code from Storyboard XML (synchronous fallback)
 * Returns an array of file objects: [{ name: string, content: string, sceneId: string }]
 */
export const generateStoryboardCode = (config) => {
  const { storyboardContent } = config;

  if (!storyboardContent || !storyboardContent.trim()) {
    return [];
  }

  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(storyboardContent, 'text/xml');
    
    // Check for parsing errors
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      return [{ 
        name: 'Error.swift', 
        content: `// Error parsing storyboard XML\n// ${parserError.textContent}\n// Please ensure the XML is valid.`,
        sceneId: 'error'
      }];
    }

    // Helper function to generate SwiftUI code for a set of elements
    const generateViewCode = (containerElement, sceneId) => {
      let swiftCode = 'import SwiftUI\n\n';
      
      // Extract view controller class name or generate one
      // Check if containerElement is a scene element or the document
      const isScene = containerElement.tagName === 'scene' || containerElement.querySelector('viewController');
      const viewController = isScene 
        ? containerElement.querySelector('viewController')
        : containerElement.querySelector('viewController');
      const customClass = viewController?.getAttribute('customClass');
      const storyboardIdentifier = viewController?.getAttribute('storyboardIdentifier');
      const sceneIdAttr = containerElement.getAttribute?.('sceneID') || sceneId;
      
      // Generate struct name
      let structName = 'ConvertedView';
      if (customClass) {
        // Remove "ViewController" suffix if present, add "View" suffix
        structName = customClass.replace(/ViewController$/, '').replace(/Controller$/, '') + 'View';
      } else if (storyboardIdentifier) {
        structName = storyboardIdentifier.replace(/ViewController$/, '').replace(/Controller$/, '') + 'View';
      } else {
        // Use scene ID to generate name
        const sceneName = sceneIdAttr.split('-').pop() || 'View';
        structName = sceneName.charAt(0).toUpperCase() + sceneName.slice(1) + 'View';
      }

      swiftCode += `struct ${structName}: View {\n`;
      swiftCode += '    var body: some View {\n';
      swiftCode += '        VStack(spacing: 16) {\n';

      // Find all elements within this scene's view hierarchy
      // If it's a scene, look for viewController > view, otherwise search the container directly
      let container = containerElement;
      if (isScene) {
        const sceneView = containerElement.querySelector('viewController view') || containerElement.querySelector('view');
        container = sceneView || containerElement;
      }

      // Convert buttons - search in container or entire document if container is document
      const buttons = container === xmlDoc.documentElement 
        ? xmlDoc.querySelectorAll('button')
        : container.querySelectorAll('button');
      buttons.forEach((button) => {
        const title = button.getAttribute('title') || button.textContent?.trim() || 'Button';
        // Clean up title - remove extra whitespace
        const cleanTitle = title.replace(/\s+/g, ' ').trim();
        swiftCode += `            Button("${cleanTitle}") {\n`;
        swiftCode += `                // Button action\n`;
        swiftCode += `            }\n`;
      });

      // Convert labels
      const labels = container === xmlDoc.documentElement 
        ? xmlDoc.querySelectorAll('label')
        : container.querySelectorAll('label');
      labels.forEach((label) => {
        const text = label.textContent?.trim() || label.getAttribute('text') || 'Label';
        const cleanText = text.replace(/\s+/g, ' ').trim();
        swiftCode += `            Text("${cleanText}")\n`;
      });

      // Convert image views
      const imageViews = container === xmlDoc.documentElement 
        ? xmlDoc.querySelectorAll('imageView')
        : container.querySelectorAll('imageView');
      imageViews.forEach((imageView) => {
        const imageName = imageView.getAttribute('image') || imageView.getAttribute('imageName') || 'placeholder';
        swiftCode += `            Image("${imageName}")\n`;
        swiftCode += `                .resizable()\n`;
        swiftCode += `                .scaledToFit()\n`;
      });

      // Convert text fields
      const textFields = container === xmlDoc.documentElement 
        ? xmlDoc.querySelectorAll('textField')
        : container.querySelectorAll('textField');
      textFields.forEach((textField) => {
        const placeholder = textField.getAttribute('placeholder') || 'Enter text';
        swiftCode += `            TextField("${placeholder}", text: .constant(""))\n`;
      });

      // Convert text views
      const textViews = container === xmlDoc.documentElement 
        ? xmlDoc.querySelectorAll('textView')
        : container.querySelectorAll('textView');
      textViews.forEach((textView) => {
        const text = textView.textContent?.trim() || textView.getAttribute('text') || '';
        if (text) {
          swiftCode += `            Text("${text.replace(/\s+/g, ' ').trim()}")\n`;
        } else {
          swiftCode += `            TextEditor(text: .constant(""))\n`;
        }
      });

      swiftCode += '        }\n';
      swiftCode += '        .padding()\n';
      swiftCode += '    }\n';
      swiftCode += '}\n';

      swiftCode += '\n// Note: This is a basic conversion. You may need to:\n';
      swiftCode += '// - Adjust layout constraints\n';
      swiftCode += '// - Add proper state management\n';
      swiftCode += '// - Implement actions and bindings\n';
      swiftCode += '// - Style components appropriately\n';

      return {
        name: `${structName}.swift`,
        content: swiftCode,
        sceneId: sceneIdAttr
      };
    };

    // Try to find scenes
    const scenes = xmlDoc.querySelectorAll('scene');
    const files = [];

    if (scenes.length > 0) {
      // Process each scene
      scenes.forEach((scene, index) => {
        const sceneId = scene.getAttribute('sceneID') || `scene-${index}`;
        const file = generateViewCode(scene, sceneId);
        files.push(file);
      });
    } else {
      // Fallback: treat entire document as one scene
      // Try to find all top-level elements
      const allButtons = xmlDoc.querySelectorAll('button');
      const allLabels = xmlDoc.querySelectorAll('label');
      const allImageViews = xmlDoc.querySelectorAll('imageView');
      const allTextFields = xmlDoc.querySelectorAll('textField');
      const allTextViews = xmlDoc.querySelectorAll('textView');

      if (allButtons.length > 0 || allLabels.length > 0 || allImageViews.length > 0 || 
          allTextFields.length > 0 || allTextViews.length > 0) {
        // Pass the document element directly - generateViewCode will search the entire document
        const file = generateViewCode(xmlDoc.documentElement, 'main-scene');
        files.push(file);
      } else {
        // No elements found, return empty file
        files.push({
          name: 'ConvertedView.swift',
          content: '// No UI elements found in storyboard\nimport SwiftUI\n\nstruct ConvertedView: View {\n    var body: some View {\n        Text("No content")\n    }\n}',
          sceneId: 'main-scene'
        });
      }
    }

    return files.length > 0 ? files : [{
      name: 'ConvertedView.swift',
      content: '// Please upload or paste a storyboard XML file to generate SwiftUI code',
      sceneId: 'default'
    }];
  } catch (e) {
    return [{ 
      name: 'Error.swift', 
      content: `// Error parsing storyboard: ${e.message}\n// Please ensure the XML is valid.`,
      sceneId: 'error'
    }];
  }
};

/**
 * Generate API code from Postman collection
 * Enhanced version with comprehensive parsing and design pattern support
 */
export const generatePostmanCode = (config) => {
  const {
    collectionFile,
    pattern = 'singleton',
    enableLogging = false,
  } = config;

  if (!collectionFile) {
    return '// Please upload a Postman collection JSON file to generate API code';
  }

  try {
    const collection = collectionFile;
    const items = collection.item || [];
    const variables = collection.variable || [];
    const auth = collection.auth || {};
    
    // Extract base URL from variables or use default
    const baseURLVar = variables.find(v => 
      ['base_url', 'baseUrl', 'baseURL', 'BASE_URL'].includes(v.key)
    );
    const baseURL = baseURLVar?.value || 'https://api.example.com';
    
    // Extract auth info
    const collectionAuth = {
      type: auth.type || 'noauth',
      bearerToken: auth.bearer?.[0]?.value || null,
      apiKey: auth.apikey?.find(item => item.key === 'value')?.value || null,
      apiKeyHeader: auth.apikey?.find(item => item.key === 'key')?.value || 'api-key',
    };

    // Helper to replace Postman variables
    const replaceVariables = (str) => {
      if (!str || typeof str !== 'string') return str;
      return str.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
        const varObj = variables.find(v => v.key === varName);
        return varObj?.value || match;
      });
    };

    // Parse authentication from request
    const parseAuth = (requestAuth, collectionAuth) => {
      if (!requestAuth || requestAuth.type === 'noauth') {
        return collectionAuth.type !== 'noauth' ? collectionAuth : null;
      }
      
      const authType = requestAuth.type || 'noauth';
      if (authType === 'bearer') {
        const token = requestAuth.bearer?.[0]?.value || collectionAuth.bearerToken;
        return { type: 'bearer', token: replaceVariables(token) };
      } else if (authType === 'apikey') {
        const key = requestAuth.apikey?.find(item => item.key === 'value')?.value || collectionAuth.apiKey;
        const header = requestAuth.apikey?.find(item => item.key === 'key')?.value || collectionAuth.apiKeyHeader;
        return { type: 'apikey', key: replaceVariables(key), header: replaceVariables(header) };
      } else if (authType === 'basic') {
        const username = requestAuth.basic?.[0]?.value || '';
        const password = requestAuth.basic?.[1]?.value || '';
        return { type: 'basic', username: replaceVariables(username), password: replaceVariables(password) };
      }
      return null;
    };

    // Parse URL and extract endpoint path
    const parseURL = (urlObj, baseURL) => {
      let fullURL = '';
      let path = '';
      let queryParams = [];
      
      if (typeof urlObj === 'string') {
        fullURL = replaceVariables(urlObj);
        const urlParts = fullURL.split('?');
        path = urlParts[0];
        if (urlParts[1]) {
          queryParams = urlParts[1].split('&').map(q => {
            const [key, value] = q.split('=');
            return { key: decodeURIComponent(key || ''), value: decodeURIComponent(value || '') };
          });
        }
      } else if (urlObj) {
        if (urlObj.raw) {
          fullURL = replaceVariables(urlObj.raw);
          const urlParts = fullURL.split('?');
          path = urlParts[0];
          if (urlParts[1]) {
            queryParams = urlParts[1].split('&').map(q => {
              const [key, value] = q.split('=');
              return { key: decodeURIComponent(key || ''), value: decodeURIComponent(value || '') };
            });
          }
        } else if (urlObj.path) {
          path = '/' + urlObj.path.map(p => replaceVariables(p)).join('/');
        }
        
        // Also check query array
        if (urlObj.query && Array.isArray(urlObj.query)) {
          queryParams = urlObj.query.map(q => ({
            key: replaceVariables(q.key || ''),
            value: replaceVariables(q.value || ''),
            disabled: q.disabled || false
          })).filter(q => !q.disabled && q.key);
        }
      }
      
      // Extract endpoint path (remove base URL)
      let endpoint = path;
      if (path.startsWith(baseURL)) {
        endpoint = path.replace(baseURL, '');
      } else if (path.startsWith('http://') || path.startsWith('https://')) {
        // Full URL provided, extract path
        try {
          const url = new URL(path);
          endpoint = url.pathname;
        } catch (e) {
          endpoint = path;
        }
      }
      
      if (!endpoint.startsWith('/')) {
        endpoint = '/' + endpoint;
      }
      
      return { endpoint, queryParams, fullURL: path };
    };

    // Parse request body
    const parseBody = (body) => {
      if (!body) return { type: 'none', content: null };
      
      const mode = body.mode || 'raw';
      
      if (mode === 'raw') {
        const raw = body.raw || '';
        let contentType = 'application/json';
        if (body.options && body.options.raw && body.options.raw.language) {
          const lang = body.options.raw.language;
          if (lang === 'json') contentType = 'application/json';
          else if (lang === 'xml') contentType = 'application/xml';
          else if (lang === 'text') contentType = 'text/plain';
        }
        return { type: 'raw', content: replaceVariables(raw), contentType };
      } else if (mode === 'formdata') {
        const formdata = body.formdata || [];
        const hasFile = formdata.some(item => item.type === 'file');
        return { 
          type: hasFile ? 'multipart' : 'formdata', 
          content: formdata.map(item => ({
            key: item.key || '',
            value: item.value || '',
            type: item.type || 'text',
            src: item.src || null,
            disabled: item.disabled || false
          })).filter(item => !item.disabled)
        };
      } else if (mode === 'urlencoded') {
        const urlencoded = body.urlencoded || [];
        return { 
          type: 'urlencoded', 
          content: urlencoded.map(item => ({
            key: item.key || '',
            value: replaceVariables(item.value || ''),
            disabled: item.disabled || false
          })).filter(item => !item.disabled)
        };
      } else if (mode === 'file') {
        return { type: 'file', content: body.file?.src || null };
      }
      
      return { type: 'none', content: null };
    };

    // Extract endpoints recursively
    let endpoints = [];
    
    const extractEndpoints = (items, folderPath = '') => {
      items.forEach(item => {
        if (item.request) {
          const method = (item.request.method || 'GET').toUpperCase();
          const urlInfo = parseURL(item.request.url, baseURL);
          const authInfo = parseAuth(item.request.auth, collectionAuth);
          const bodyInfo = parseBody(item.request.body);
          const headers = (item.request.header || []).map(h => ({
            key: h.key || '',
            value: replaceVariables(h.value || ''),
            disabled: h.disabled || false
          })).filter(h => !h.disabled && h.key);
          
          // Generate function name from endpoint name
          const name = item.name || `${method} ${urlInfo.endpoint}`;
          const functionName = name
            .replace(/[^a-zA-Z0-9\s]/g, ' ')
            .split(/\s+/)
            .map((word, idx) => idx === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join('')
            .replace(/^[0-9]/, '_$&');
          
          endpoints.push({
            name,
            functionName,
            method,
            endpoint: urlInfo.endpoint,
            fullURL: urlInfo.fullURL,
            headers,
            queryParams: urlInfo.queryParams,
            body: bodyInfo,
            auth: authInfo,
            description: item.request.description || '',
          });
        }
        if (item.item) {
          const newPath = folderPath ? `${folderPath}/${item.name || ''}` : (item.name || '');
          extractEndpoints(item.item, newPath);
        }
      });
    };
    
    extractEndpoints(items);

    if (endpoints.length === 0) {
      return '// No endpoints found in Postman collection. Please ensure the collection contains at least one request.';
    }

    // Generate HttpMethods enum (matching reference pattern)
    const generateHttpMethodsEnum = () => {
      return `enum HttpMethods: String {
    case get = "GET"
    case post = "POST"
    case put = "PUT"
    case patch = "PATCH"
    case delete = "DELETE"
}`;
    };

    // Generate ContentType enum (matching reference pattern)
    const generateContentTypeEnum = () => {
      return `//MARK: - CONTENT TYPE
enum ContentType {
    static let applicationXWWFormUrlencoded = "application/x-www-form-urlencoded"
    static let applicationJson = "application/json"
    static let multipartFormData = "multipart/form-data"
}`;
    };


    // Generate APIEndpoints struct (matching reference pattern)
    const generateAPIEndpoints = () => {
      const normalizedBaseURL = baseURL.endsWith('/') ? baseURL : baseURL + '/';
      
      let code = `struct APIEndpoints{\n    \n    static let baseURL = "${normalizedBaseURL}"\n`;
      
      // Generate endpoint properties
      endpoints.forEach(ep => {
        const endpointPath = ep.endpoint.startsWith('/') ? ep.endpoint : '/' + ep.endpoint;
        code += `    static let ${ep.functionName} = baseURL + "${endpointPath.replace(/^\//, '')}"\n`;
      });
      
      code += `}`;
      
      // Generate APIEndpointsUpload if there are file upload endpoints
      const uploadEndpoints = endpoints.filter(ep => ep.body.type === 'multipart' || ep.body.type === 'file');
      if (uploadEndpoints.length > 0) {
        code += `\n\nstruct APIEndpointsUpload{\n`;
        uploadEndpoints.forEach(ep => {
          const endpointPath = ep.endpoint.startsWith('/') ? ep.endpoint : '/' + ep.endpoint;
          code += `    static let ${ep.functionName} = "${normalizedBaseURL}${endpointPath.replace(/^\//, '')}"\n`;
        });
        code += `}`;
      }
      
      return code;
    };

    // Extract parameters from endpoints for enum associated values
    const extractParameters = (endpoint) => {
      const params = [];
      
      // Add query parameters as associated values
      endpoint.queryParams.forEach(qp => {
        if (qp.key && !params.find(p => p.name === qp.key)) {
          params.push({ name: qp.key, type: 'String' });
        }
      });
      
      // Add body parameters
      if (endpoint.body.type === 'raw' && endpoint.body.content) {
        // For raw body, we'll use a generic parameter
        params.push({ name: 'body', type: 'String' });
      } else if (endpoint.body.type === 'formdata' || endpoint.body.type === 'urlencoded') {
        endpoint.body.content.forEach(item => {
          if (item.key && !params.find(p => p.name === item.key)) {
            params.push({ name: item.key, type: 'String' });
          }
        });
      } else if (endpoint.body.type === 'multipart' || endpoint.body.type === 'file') {
        params.push({ name: 'files', type: '[UploadRequestFileDM]' });
      }
      
      return params;
    };

    // Generate APIManager enum (matching reference pattern)
    const generateAPIManager = () => {
      let code = `enum APIManager {\n`;
      
      // Generate enum cases with associated values
      endpoints.forEach(ep => {
        const params = extractParameters(ep);
        const caseName = ep.functionName;
        
        if (params.length === 0) {
          code += `    case ${caseName}\n`;
        } else {
          const paramString = params.map(p => `${p.name}: ${p.type}`).join(', ');
          code += `    case ${caseName}(${paramString})\n`;
        }
      });
      
      // Generate path computed property
      code += `    \n    // Path configuration\n    var path: String {\n        switch self {\n`;
      endpoints.forEach(ep => {
        code += `        case .${ep.functionName}${extractParameters(ep).length > 0 ? '(_)' : ''}: return APIEndpoints.${ep.functionName}\n`;
      });
      code += `        }\n    }\n`;
      
      // Generate method computed property
      code += `    \n    // HTTP method configuration\n    var method: String {\n        switch self {\n`;
      endpoints.forEach(ep => {
        code += `        case .${ep.functionName}${extractParameters(ep).length > 0 ? '(_)' : ''}: return "${ep.method}"\n`;
      });
      code += `        }\n    }\n`;
      
      // Generate bodyParameters computed property
      code += `    \n    // Body parameters for POST/PUT (as [String: Any] for JSON)\n    var bodyParameters: [String: Any]? {\n        switch self {\n`;
      endpoints.forEach(ep => {
        const params = extractParameters(ep);
        if (['POST', 'PUT', 'PATCH'].includes(ep.method) && ep.body.type !== 'none') {
          if (ep.body.type === 'raw') {
            code += `        case .${ep.functionName}(let body): return ["body": body]\n`;
          } else if (ep.body.type === 'formdata' || ep.body.type === 'urlencoded') {
            const bodyParams = ep.body.content.map(item => item.key).filter(Boolean);
            if (bodyParams.length > 0) {
              const paramBindings = bodyParams.map(key => `let ${key}`).join(', ');
              const dictEntries = bodyParams.map(key => `"${key}": ${key}`).join(', ');
              code += `        case .${ep.functionName}(${paramBindings}): return [${dictEntries}].removingNilValues()\n`;
            } else {
              code += `        case .${ep.functionName}: return nil\n`;
            }
          } else {
            code += `        case .${ep.functionName}: return nil\n`;
          }
        } else {
          code += `        case .${ep.functionName}${params.length > 0 ? '(_)' : ''}: return nil\n`;
        }
      });
      code += `        }\n    }\n`;
      
      // Generate queryParameters computed property
      code += `    \n    // Query parameters for GET requests (as [String: Any] for URL query items)\n    var queryParameters: [String: Any]? {\n        switch self {\n`;
      endpoints.forEach(ep => {
        const params = extractParameters(ep);
        const queryParams = ep.queryParams.filter(qp => qp.key);
        if (queryParams.length > 0) {
          const paramBindings = queryParams.map(qp => `let ${qp.key}`).join(', ');
          const dictEntries = queryParams.map(qp => `"${qp.key}": ${qp.key}`).join(', ');
          code += `        case .${ep.functionName}(${paramBindings}${ep.body.type !== 'none' && ['POST', 'PUT', 'PATCH'].includes(ep.method) ? ', _' : ''}): return [${dictEntries}].removingNilValues()\n`;
        } else {
          code += `        case .${ep.functionName}${params.length > 0 ? '(_)' : ''}: return nil\n`;
        }
      });
      code += `        }\n    }\n`;
      
      // Generate formBody computed property
      code += `    \n    // formBody\n    var formBody: [UploadRequestFileDM]? {\n        switch self {\n`;
      endpoints.forEach(ep => {
        if (ep.body.type === 'multipart' || ep.body.type === 'file') {
          code += `        case .${ep.functionName}(let files): return files\n`;
        } else {
          code += `        case .${ep.functionName}${extractParameters(ep).length > 0 ? '(_)' : ''}: return nil\n`;
        }
      });
      code += `        }\n    }\n`;
      
      // Generate headers computed property
      code += `    \n    // Headers for the API request\n    var headers: [String: String]? {\n        switch self {\n`;
      endpoints.forEach(ep => {
        const needsAuth = ep.auth && ep.auth.type === 'bearer';
        const hasHeaders = ep.headers.length > 0;
        
        if (needsAuth || hasHeaders) {
          let headerEntries = [];
          if (needsAuth) {
            headerEntries.push(`"Authorization": AppStorageManager.shared.userToken`);
          }
          ep.headers.forEach(h => {
            headerEntries.push(`"${h.key}": "${h.value}"`);
          });
          code += `        case .${ep.functionName}${extractParameters(ep).length > 0 ? '(_)' : ''}: return [${headerEntries.join(', ')}]\n`;
        } else {
          code += `        case .${ep.functionName}${extractParameters(ep).length > 0 ? '(_)' : ''}: return nil\n`;
        }
      });
      code += `        }\n    }\n`;
      
      // Generate request method
      code += `    \n    // Async request method for Decodable response\n    func request<T: Decodable>() async throws -> Result<T, APIError> {\n        await APIClient.shared.request(endpoint: self)\n    }\n`;
      
      // Generate upload method
      code += `    \n    // Async request method for Decodable response\n    @MainActor\n    func upload<T: Decodable>(completion: @escaping (Result<T, APIError>) -> Void)  {\n        APIClient.shared.upload(endpoint: self, completion: completion)\n    }\n`;
      
      code += `}\n`;
      
      return code;
    };

    // Generate APIClient class (matching reference pattern)
    const generateAPIClient = () => {
      return `@MainActor
class APIClient {
    static let shared = APIClient()
    
    private let session: URLSession
    
    private init() {
        let config = URLSessionConfiguration.default
        config.urlCache = nil // disable entirely
        config.requestCachePolicy = .reloadIgnoringLocalCacheData
        self.session = URLSession(configuration: config)
    }
    
    // Generic async request method returning Result
    func request<T: Decodable>(endpoint: APIManager) async -> Result<T, APIError> {
        guard URL(string: endpoint.path) != nil else {
            return .failure(.serverError(message: "Invalid URL"))
        }
        
        var urlComponents = URLComponents(string: endpoint.path)
       
        // Append query parameters if they exist
        if let queryParams = endpoint.queryParameters, !queryParams.isEmpty {
            urlComponents?.queryItems = queryParams.map { key, value in
                URLQueryItem(name: key, value: "\\(value)")
            }
        }
        
        guard let url = urlComponents?.url else {
            return .failure(.serverError(message: "Invalid URL"))
        }
        
        var request = URLRequest(url: url)
        request.httpMethod = endpoint.method
        ${enableLogging ? `debugPrint("API headers------>>>>>>>", endpoint.headers)` : ''}
        ${enableLogging ? `debugPrint("\\n\\n")` : ''}
        ${enableLogging ? `debugPrint("API Request------>>>>>>>", endpoint.bodyParameters)` : ''}
        ${enableLogging ? `debugPrint("\\n\\n")` : ''}
        ${enableLogging ? `debugPrint("API URL------>>>>>>>", url.absoluteString)` : ''}
        ${enableLogging ? `debugPrint("\\n\\n")` : ''}
        
        if let bodyParams = endpoint.bodyParameters, let jsonBody = try? JSONSerialization.data(withJSONObject: bodyParams) {
            request.httpBody = jsonBody
            request.setValue("application/json", forHTTPHeaderField: "Content-Type")
        }
        
        // Merge headers
        let finalHeaders = endpoint.headers?.merging(endpoint.headers ?? [:], uniquingKeysWith: { _, new in new })
        finalHeaders?.forEach { request.setValue($1, forHTTPHeaderField: $0) }
        
        // Perform request
        do {
            let (data, response) = try await session.data(for: request)
            
            ${enableLogging ? `if let jsonObject = try? JSONSerialization.jsonObject(with: data),
               let prettyData = try? JSONSerialization.data(withJSONObject: jsonObject, options: .prettyPrinted),
               let prettyString = String(data: prettyData, encoding: .utf8)
            {
                print("📦 API Response------>>>>>>>:\\n\\(prettyString)")
            } else {
                debugPrint("API Response------>>>>>>>", String(data: data, encoding: .utf8))
            }
            debugPrint("\\n\\n")` : ''}
            guard let httpResponse = response as? HTTPURLResponse else {
                return .failure(.serverError(message: "Invalid API Response"))
            }
            
            // Handle 401 (Unauthorized) - Trigger logout and return session expired error
            if httpResponse.statusCode == 401 {
                return .failure(.sessionExpired(message: "Session expired. Please log in again."))
            }
            
            // Handle non-success status codes
            guard (200...299).contains(httpResponse.statusCode) else {
                // Try to parse the JSON response to extract status and message
                do {
                    let errorResponse = try JSONDecoder().decode(ErrorResponse.self, from: data)
                    ${enableLogging ? `debugPrint("HTTP Status Code: \\(httpResponse.statusCode), Status: \\(errorResponse.status), Message: \\(errorResponse.message)")` : ''}
                    return .failure(.serverError(message: errorResponse.message ?? "Something went wrong"))
                } catch {
                    // Fallback if JSON parsing fails
                    let message = String(data: data, encoding: .utf8) ?? "Unknown server error"
                    ${enableLogging ? `debugPrint("HTTP Status Code: \\(httpResponse.statusCode), Raw Response: \\(message)")` : ''}
                    return .failure(.serverError(message: message))
                }
            }
            
            // Decode success response
            do {
                let decoded = try JSONDecoder().decode(T.self, from: data)
                return .success(decoded)
            } catch let decodingError as DecodingError {
                ${enableLogging ? `debugPrint(decodingError.prettyDescription)` : ''}
                return .failure(.serverError(message: decodingError.prettyDescription))
            } catch {
                ${enableLogging ? `debugPrint("Unexpected error: ", error.localizedDescription)` : ''}
                return .failure(.serverError(message: error.localizedDescription))
            }
        } catch {
            return .failure(.serverError(message: error.localizedDescription))
        }
    }
    
    
    func upload<T: Decodable>(endpoint: APIManager, completion: @escaping (Result<T, APIError>) -> Void) {
        guard case .uploadFiles = endpoint else {
            DispatchQueue.main.async {
                completion(.failure(.serverError(message: "Invalid endpoint for file upload")))
            }
            return
        }
        
        guard let url = URL(string: endpoint.path),
              let filesData = endpoint.formBody else {
            DispatchQueue.main.async {
                completion(.failure(.serverError(message: "Invalid URL or Files Data")))
            }
            return
        }
        
        var request = MultipartFormDataRequest(url: url)
        request.addDataField(keyName: "myfile", data: filesData) // 🔹 Use correct key here
        
        URLSession.shared.dataTask(with: request) { dataResponse, response, error in
            guard let data = dataResponse else {
                DispatchQueue.main.async {
                    completion(.failure(.serverError(message: "Invalid API Response")))
                }
                return
            }
            
            ${enableLogging ? `debugPrint("API Response------>>>>>>>", String(data: data, encoding: .utf8) ?? "No response body")` : ''}
            guard let httpResponse = response as? HTTPURLResponse else {
                DispatchQueue.main.async {
                    completion(.failure(.serverError(message: "Invalid API Response")))
                }
                return
            }
            
            if httpResponse.statusCode == 401 {
                DispatchQueue.main.async {
                    completion(.failure(.sessionExpired(message: "Session expired. Please log in again.")))
                }
                return
            }
            
            guard (200...299).contains(httpResponse.statusCode) else {
                do {
                    let errorResponse = try JSONDecoder().decode(ErrorResponse.self, from: data)
                    DispatchQueue.main.async {
                        completion(.failure(.serverError(message: errorResponse.message ?? "Something went wrong")))
                    }
                } catch {
                    let message = String(data: data, encoding: .utf8) ?? "Unknown server error"
                    DispatchQueue.main.async {
                        completion(.failure(.serverError(message: message)))
                    }
                }
                return
            }
            
            do {
                let decoded = try JSONDecoder().decode(T.self, from: data)
                DispatchQueue.main.async {
                    completion(.success(decoded))
                }
            } catch {
                DispatchQueue.main.async {
                    completion(.failure(.serverError(message: error.localizedDescription)))
                }
            }
        }.resume()
    }
    
}`;
    };

    // Generate APIError enum (matching reference pattern)
    const generateAPIError = () => {
      return `// Custom error enum for better handling
enum APIError: Error {
    case sessionExpired(message: String)
    case serverError(message: String)
    
    var localizedDescription: String {
        switch self {
        case .sessionExpired(let message):
            return message
        case .serverError(let message):
            return message
        }
    }
}`;
    };

    // Generate ErrorResponse struct (matching reference pattern)
    const generateErrorResponse = () => {
      return `struct ErrorResponse: Codable {
    let status: Bool
    let message: String?
}`;
    };

    // Generate Dictionary extension (matching reference pattern)
    const generateDictionaryExtension = () => {
      return `extension Dictionary where Value == Any? {
    /// Removes keys with nil values and returns \`[String: Any]\`
    func removingNilValues() -> [Key: Any] {
        var result: [Key: Any] = [:]
        for (key, value) in self {
            if let unwrapped = value {
                result[key] = unwrapped
            }
        }
        return result
    }
}`;
    };

    // Generate pattern-specific wrapper/service classes
    const generatePatternWrapper = (pattern, endpoints) => {
      switch (pattern) {
        case 'singleton':
          return generateSingletonWrapper();
        case 'generic':
          return generateGenericWrapper(endpoints);
        case 'repository':
          return generateRepositoryWrapper(endpoints);
        case 'functional':
          return generateFunctionalWrapper();
        case 'interceptor':
          return generateInterceptorWrapper();
        default:
          return generateSingletonWrapper();
      }
    };

    // Pattern 1: Singleton Wrapper
    const generateSingletonWrapper = () => {
      return `// MARK: - Singleton Pattern Wrapper

class APIService {
    static let shared = APIService()
    private init() {}
    
    func call<T: Decodable>(endpoint: APIManager) async throws -> Result<T, APIError> {
        await endpoint.request()
    }
    
    func upload<T: Decodable>(endpoint: APIManager, completion: @escaping (Result<T, APIError>) -> Void) {
        endpoint.upload(completion: completion)
    }
}`;
    };

    // Pattern 2: Generic Base Class Wrapper
    const generateGenericWrapper = (endpoints) => {
      // Group endpoints by feature (simplified - use folder structure if available)
      const authEndpoints = endpoints.filter(ep => 
        ep.functionName.toLowerCase().includes('login') || 
        ep.functionName.toLowerCase().includes('auth') ||
        ep.functionName.toLowerCase().includes('signup')
      );
      const userEndpoints = endpoints.filter(ep => 
        ep.functionName.toLowerCase().includes('user') && 
        !authEndpoints.includes(ep)
      );
      
      let code = `// MARK: - Generic Base Class Pattern

class BaseAPIService {
    internal let client = APIClient.shared
    
    func call<T: Decodable>(endpoint: APIManager) async throws -> Result<T, APIError> {
        await endpoint.request()
    }
    
    func upload<T: Decodable>(endpoint: APIManager, completion: @escaping (Result<T, APIError>) -> Void) {
        endpoint.upload(completion: completion)
    }
}`;

      // Generate example AuthService if auth endpoints exist
      if (authEndpoints.length > 0) {
        const firstAuth = authEndpoints[0];
        code += `\n\n// MARK: - Feature-Specific Services\n\nclass AuthService: BaseAPIService {\n`;
        code += `    func ${firstAuth.functionName}(${extractParameters(firstAuth).map(p => `${p.name}: ${p.type}`).join(', ')}) async throws -> Result<YourResponseModel, APIError> {\n`;
        code += `        await APIManager.${firstAuth.functionName}(${extractParameters(firstAuth).map(p => `${p.name}: ${p.name}`).join(', ')}).request()\n`;
        code += `    }\n`;
        code += `}`;
      }

      // Generate example UserService if user endpoints exist
      if (userEndpoints.length > 0) {
        const firstUser = userEndpoints[0];
        code += `\n\nclass UserService: BaseAPIService {\n`;
        code += `    func ${firstUser.functionName}(${extractParameters(firstUser).map(p => `${p.name}: ${p.type}`).join(', ')}) async throws -> Result<YourResponseModel, APIError> {\n`;
        code += `        await APIManager.${firstUser.functionName}(${extractParameters(firstUser).map(p => `${p.name}: ${p.name}`).join(', ')}).request()\n`;
        code += `    }\n`;
        code += `}`;
      }

      return code;
    };

    // Pattern 3: Repository Pattern Wrapper
    const generateRepositoryWrapper = (endpoints) => {
      const userEndpoints = endpoints.filter(ep => 
        ep.functionName.toLowerCase().includes('user')
      );
      
      let code = `// MARK: - Repository Pattern

protocol APIRepository {
    func call<T: Decodable>(endpoint: APIManager) async throws -> Result<T, APIError>
}

class DefaultAPIRepository: APIRepository {
    private let client = APIClient.shared
    
    func call<T: Decodable>(endpoint: APIManager) async throws -> Result<T, APIError> {
        await endpoint.request()
    }
}`;

      // Generate example UserRepository
      if (userEndpoints.length > 0) {
        const firstUser = userEndpoints[0];
        code += `\n\n// MARK: - Feature Repositories\n\nclass UserRepository {\n`;
        code += `    private let repository: APIRepository\n    \n`;
        code += `    init(repository: APIRepository = DefaultAPIRepository()) {\n`;
        code += `        self.repository = repository\n`;
        code += `    }\n    \n`;
        code += `    func ${firstUser.functionName}(${extractParameters(firstUser).map(p => `${p.name}: ${p.type}`).join(', ')}) async throws -> Result<YourResponseModel, APIError> {\n`;
        code += `        await repository.call(endpoint: .${firstUser.functionName}(${extractParameters(firstUser).map(p => `${p.name}: ${p.name}`).join(', ')}))\n`;
        code += `    }\n`;
        code += `}`;
      }

      return code;
    };

    // Pattern 4: Functional Helpers Wrapper
    const generateFunctionalWrapper = () => {
      return `// MARK: - Functional Helpers Pattern

struct APIHelpers {
    static func call<T: Decodable>(endpoint: APIManager) async throws -> Result<T, APIError> {
        await endpoint.request()
    }
    
    static func upload<T: Decodable>(endpoint: APIManager, completion: @escaping (Result<T, APIError>) -> Void) {
        endpoint.upload(completion: completion)
    }
}`;
    };

    // Pattern 5: Interceptor-Based Wrapper
    const generateInterceptorWrapper = () => {
      return `// MARK: - Interceptor-Based Pattern

protocol RequestInterceptor {
    func intercept(_ request: inout URLRequest) throws
}

protocol ResponseInterceptor {
    func intercept(_ response: URLResponse, data: Data) throws
}

class APIService {
    private var requestInterceptors: [RequestInterceptor] = []
    private var responseInterceptors: [ResponseInterceptor] = []
    
    static let shared = APIService()
    private init() {}
    
    func addRequestInterceptor(_ interceptor: RequestInterceptor) {
        requestInterceptors.append(interceptor)
    }
    
    func addResponseInterceptor(_ interceptor: ResponseInterceptor) {
        responseInterceptors.append(interceptor)
    }
    
    func call<T: Decodable>(endpoint: APIManager) async throws -> Result<T, APIError> {
        // Note: Interceptors would need to be applied in APIClient
        // For now, this is a wrapper that uses the base APIClient
        await endpoint.request()
    }
}

// MARK: - Example Interceptors

class AuthInterceptor: RequestInterceptor {
    private let token: String
    
    init(token: String) {
        self.token = token
    }
    
    func intercept(_ request: inout URLRequest) throws {
        request.setValue("Bearer \\(token)", forHTTPHeaderField: "Authorization")
    }
}

class LoggingInterceptor: ResponseInterceptor {
    func intercept(_ response: URLResponse, data: Data) throws {
        if let httpResponse = response as? HTTPURLResponse {
            print("[API] Response: \\(httpResponse.statusCode)")
        }
    }
}`;
    };

    // OLD CODE - Pattern generators (simplified versions) - REMOVED
    // eslint-disable-next-line no-unused-vars
    const generateSingletonService_DEPRECATED = (safeBaseURL, endpoints, timeout, enableLogging, enableRetry, useAsyncAwait) => {
      const retryCode = enableRetry ? `
    // Retry logic with exponential backoff
    private func retryRequest(
        _ request: URLRequest,
        maxRetries: Int = 3,
        currentRetry: Int = 0
    ) async throws -> (Data, URLResponse) {
        do {
            return try await URLSession.shared.data(for: request)
        } catch {
            if currentRetry < maxRetries {
                let delay = pow(2.0, Double(currentRetry))
                try await Task.sleep(nanoseconds: UInt64(delay * 1_000_000_000))
                return try await retryRequest(request, maxRetries: maxRetries, currentRetry: currentRetry + 1)
            }
            throw error
        }
    }` : '';

      let code = `// MARK: - API Service
// MARK: - Singleton API Client

class APIService {
    static let shared = APIService()
    private let baseURL: String
    private var defaultHeaders: [String: String] = [:]
    ${enableLogging ? 'private let enableLogging = true' : ''}
    
    init(baseURL: String = "${safeBaseURL}") {
        self.baseURL = baseURL
        self.defaultHeaders = ["Content-Type": "application/json"]
    }
    
    func setDefaultHeaders(_ headers: [String: String]) {
        self.defaultHeaders = headers
    }
    
    // MARK: - Dynamic Request Method
    func request<T: Codable>(
        endpoint: String,
        method: HTTPMethod = .get,
        headers: [String: String]? = nil,
        queryParams: [String: String]? = nil,
        body: (any Codable)? = nil,
        authToken: String? = nil
    ) async throws -> T {
        var urlString = baseURL + endpoint
        
        if let params = queryParams, !params.isEmpty {
            let queryString = QueryParamEncoder.encode(params)
            urlString += (urlString.contains("?") ? "&" : "?") + queryString
        }
        
        guard URLValidator.isValid(urlString), let url = URL(string: urlString) else {
            throw APIError.invalidURL
        }
        
        var request = URLRequest(url: url)
        request.httpMethod = method.rawValue
        request.timeoutInterval = ${timeout}
        
        for (key, value) in defaultHeaders {
            request.setValue(value, forHTTPHeaderField: key)
        }
        
        if let headers = headers {
            for (key, value) in headers {
                request.setValue(value, forHTTPHeaderField: key)
            }
        }
        
        if let token = authToken {
            request.setValue("Bearer \\(token)", forHTTPHeaderField: "Authorization")
        }
        
        if let body = body {
            do {
                request.httpBody = try JSONEncoder().encode(body)
            } catch {
                throw APIError.invalidBody
            }
        }
        
        ${enableLogging ? `print("[API] \\(method.rawValue) \\(urlString)")` : ''}
        
        ${enableRetry ? `let (data, response) = try await retryRequest(request)` : `let (data, response) = try await URLSession.shared.data(for: request)`}
        
        guard let httpResponse = response as? HTTPURLResponse else {
            throw APIError.invalidResponse
        }
        
        ${enableLogging ? `print("[API] Response: \\(httpResponse.statusCode)")` : ''}
        
        guard (200...299).contains(httpResponse.statusCode) else {
            let errorMessage = String(data: data, encoding: .utf8)
            throw APIError.httpError(httpResponse.statusCode, errorMessage)
        }
        
        do {
            return try JSONDecoder().decode(T.self, from: data)
        } catch {
            throw APIError.decodingError(error)
        }
    }
    
    // MARK: - Multipart Upload Support
    func uploadFile(
        endpoint: String,
        method: HTTPMethod = .post,
        fileData: Data,
        fileName: String,
        mimeType: String,
        additionalFields: [String: String] = [:],
        headers: [String: String]? = nil,
        authToken: String? = nil
    ) async throws -> Data {
        var urlString = baseURL + endpoint
        guard URLValidator.isValid(urlString), let url = URL(string: urlString) else {
            throw APIError.invalidURL
        }
        
        var request = URLRequest(url: url)
        request.httpMethod = method.rawValue
        request.timeoutInterval = ${timeout}
        
        let boundary = UUID().uuidString
        request.setValue("multipart/form-data; boundary=\\(boundary)", forHTTPHeaderField: "Content-Type")
        
        if let token = authToken {
            request.setValue("Bearer \\(token)", forHTTPHeaderField: "Authorization")
        }
        
        if let headers = headers {
            for (key, value) in headers {
                request.setValue(value, forHTTPHeaderField: key)
            }
        }
        
        var body = Data()
        
        // Add additional fields
        for (key, value) in additionalFields {
            body.append("--\\(boundary)\\r\\n".data(using: .utf8)!)
            body.append("Content-Disposition: form-data; name=\\"\\(key)\\"\\r\\n\\r\\n".data(using: .utf8)!)
            body.append("\\(value)\\r\\n".data(using: .utf8)!)
        }
        
        // Add file
        body.append("--\\(boundary)\\r\\n".data(using: .utf8)!)
        body.append("Content-Disposition: form-data; name=\\"file\\"; filename=\\"\\(fileName)\\"\\r\\n".data(using: .utf8)!)
        body.append("Content-Type: \\(mimeType)\\r\\n\\r\\n".data(using: .utf8)!)
        body.append(fileData)
        body.append("\\r\\n".data(using: .utf8)!)
        body.append("--\\(boundary)--\\r\\n".data(using: .utf8)!)
        
        request.httpBody = body
        
        ${enableLogging ? `print("[API] Upload \\(method.rawValue) \\(urlString)")` : ''}
        
        ${enableRetry ? `let (data, response) = try await retryRequest(request)` : `let (data, response) = try await URLSession.shared.data(for: request)`}
        
        guard let httpResponse = response as? HTTPURLResponse else {
            throw APIError.invalidResponse
        }
        
        guard (200...299).contains(httpResponse.statusCode) else {
            let errorMessage = String(data: data, encoding: .utf8)
            throw APIError.httpError(httpResponse.statusCode, errorMessage)
        }
        
        return data
    }${retryCode}
    
    // MARK: - Endpoint-specific methods
`;

      // Generate methods for each endpoint
      endpoints.forEach(ep => {
        const methodName = ep.functionName;
        const hasBody = ['POST', 'PUT', 'PATCH'].includes(ep.method) && ep.body.type !== 'none';
        const hasQuery = ep.queryParams.length > 0;
        const hasAuth = ep.auth !== null;
        
        code += `
    // ${ep.name}
    func ${methodName}${hasBody ? '<T: Codable>' : ''}(
        ${hasQuery ? ep.queryParams.map((p, idx) => `${p.key}: String${idx === 0 ? '' : ''}`).join(',\n        ') + ',' : ''}
        ${hasBody && ep.body.type === 'raw' ? 'body: Data' : hasBody ? 'body: (any Codable)?' : ''}
        ${hasAuth && ep.auth.type === 'bearer' ? 'authToken: String? = APIConstants.defaultBearerToken' : ''}
    ) async throws -> ${ep.body.type === 'multipart' || ep.body.type === 'file' ? 'Data' : 'T'} {
        let endpoint = APIEndpoint.${methodName}.path
        let method = APIEndpoint.${methodName}.method
        var queryParams: [String: String] = APIEndpoint.${methodName}.defaultQueryParams
        var headers = APIEndpoint.${methodName}.defaultHeaders
        
        ${hasQuery ? ep.queryParams.map(p => `queryParams["${p.key}"] = ${p.key}`).join('\n        ') : ''}
        
        ${hasAuth && ep.auth.type === 'bearer' ? `let token = authToken ?? APIConstants.defaultBearerToken` : ''}
        
        ${ep.body.type === 'multipart' || ep.body.type === 'file' 
          ? `// File upload endpoint
        guard let fileData = body as? Data else {
            throw APIError.invalidBody
        }
        return try await uploadFile(
            endpoint: endpoint,
            method: method,
            fileData: fileData,
            fileName: "file",
            mimeType: "application/octet-stream",
            additionalFields: [:],
            headers: headers,
            ${hasAuth && ep.auth.type === 'bearer' ? 'authToken: token' : 'authToken: nil'}
        )`
          : hasBody && ep.body.type === 'raw'
          ? `return try await request<Data>(
            endpoint: endpoint,
            method: method,
            headers: headers,
            queryParams: queryParams.isEmpty ? nil : queryParams,
            body: nil,
            authToken: ${hasAuth && ep.auth.type === 'bearer' ? 'token' : 'nil'}
        )`
          : hasBody
          ? `return try await request<T>(
            endpoint: endpoint,
            method: method,
            headers: headers,
            queryParams: queryParams.isEmpty ? nil : queryParams,
            body: body,
            authToken: ${hasAuth && ep.auth.type === 'bearer' ? 'token' : 'nil'}
        )`
          : `return try await request<T>(
            endpoint: endpoint,
            method: method,
            headers: headers,
            queryParams: queryParams.isEmpty ? nil : queryParams,
            body: nil,
            authToken: ${hasAuth && ep.auth.type === 'bearer' ? 'token' : 'nil'}
        )`
        }
    }
`;
      });

      code += `}`;
      return code;
    };

    // eslint-disable-next-line no-unused-vars
    const generateGenericService = (safeBaseURL, endpoints, timeout, enableLogging) => {
      return `// MARK: - Generic Base API Service

class BaseAPIService {
    protected let baseURL: String
    protected var defaultHeaders: [String: String]
    ${enableLogging ? 'protected let enableLogging: Bool' : ''}
    
    init(baseURL: String, defaultHeaders: [String: String] = [:]) {
        self.baseURL = baseURL
        self.defaultHeaders = defaultHeaders.isEmpty ? ["Content-Type": "application/json"] : defaultHeaders
        ${enableLogging ? 'self.enableLogging = true' : ''}
    }
    
    func request<T: Codable>(
        endpoint: String,
        method: HTTPMethod = .get,
        headers: [String: String]? = nil,
        queryParams: [String: String]? = nil,
        body: (any Codable)? = nil,
        authToken: String? = nil
    ) async throws -> T {
        var urlString = baseURL + endpoint
        
        if let params = queryParams, !params.isEmpty {
            let queryString = QueryParamEncoder.encode(params)
            urlString += (urlString.contains("?") ? "&" : "?") + queryString
        }
        
        guard URLValidator.isValid(urlString), let url = URL(string: urlString) else {
            throw APIError.invalidURL
        }
        
        var request = URLRequest(url: url)
        request.httpMethod = method.rawValue
        request.timeoutInterval = ${timeout}
        
        for (key, value) in defaultHeaders {
            request.setValue(value, forHTTPHeaderField: key)
        }
        
        if let headers = headers {
            for (key, value) in headers {
                request.setValue(value, forHTTPHeaderField: key)
            }
        }
        
        if let token = authToken {
            request.setValue("Bearer \\(token)", forHTTPHeaderField: "Authorization")
        }
        
        if let body = body {
            do {
                request.httpBody = try JSONEncoder().encode(body)
            } catch {
                throw APIError.invalidBody
            }
        }
        
        ${enableLogging ? `print("[API] \\(method.rawValue) \\(urlString)")` : ''}
        
        let (data, response) = try await URLSession.shared.data(for: request)
        
        guard let httpResponse = response as? HTTPURLResponse else {
            throw APIError.invalidResponse
        }
        
        ${enableLogging ? `print("[API] Response: \\(httpResponse.statusCode)")` : ''}
        
        guard (200...299).contains(httpResponse.statusCode) else {
            let errorMessage = String(data: data, encoding: .utf8)
            throw APIError.httpError(httpResponse.statusCode, errorMessage)
        }
        
        do {
            return try JSONDecoder().decode(T.self, from: data)
        } catch {
            throw APIError.decodingError(error)
        }
    }
}

// MARK: - Main API Service
class APIService: BaseAPIService {
    static let shared = APIService()
    
    init() {
        super.init(baseURL: "${safeBaseURL}")
    }
}`;
    };

    // eslint-disable-next-line no-unused-vars
    const generateRepositoryService = (safeBaseURL, endpoints, timeout, enableLogging) => {
      return `// MARK: - Repository Pattern API Service

protocol APIRepository {
    func request<T: Codable>(
        endpoint: String,
        method: HTTPMethod,
        headers: [String: String]?,
        queryParams: [String: String]?,
        body: Data?,
        authToken: String?
    ) async throws -> T
}

class DefaultAPIRepository: APIRepository {
    private let baseURL: String
    private let session: URLSession
    private var defaultHeaders: [String: String] = [:]
    
    init(baseURL: String, session: URLSession = .shared) {
        self.baseURL = baseURL
        self.session = session
        self.defaultHeaders = ["Content-Type": "application/json"]
    }
    
    func request<T: Codable>(
        endpoint: String,
        method: HTTPMethod,
        headers: [String: String]? = nil,
        queryParams: [String: String]? = nil,
        body: Data? = nil,
        authToken: String? = nil
    ) async throws -> T {
        var urlString = baseURL + endpoint
        
        if let params = queryParams, !params.isEmpty {
            let queryString = QueryParamEncoder.encode(params)
            urlString += (urlString.contains("?") ? "&" : "?") + queryString
        }
        
        guard URLValidator.isValid(urlString), let url = URL(string: urlString) else {
            throw APIError.invalidURL
        }
        
        var request = URLRequest(url: url)
        request.httpMethod = method.rawValue
        request.timeoutInterval = ${timeout}
        
        for (key, value) in defaultHeaders {
            request.setValue(value, forHTTPHeaderField: key)
        }
        
        if let headers = headers {
            for (key, value) in headers {
                request.setValue(value, forHTTPHeaderField: key)
            }
        }
        
        if let token = authToken {
            request.setValue("Bearer \\(token)", forHTTPHeaderField: "Authorization")
        }
        
        if let body = body {
            request.httpBody = body
        }
        
        ${enableLogging ? `print("[API] \\(method.rawValue) \\(urlString)")` : ''}
        
        let (data, response) = try await session.data(for: request)
        
        guard let httpResponse = response as? HTTPURLResponse,
              (200...299).contains(httpResponse.statusCode) else {
            let errorMessage = String(data: data, encoding: .utf8)
            throw APIError.httpError((response as? HTTPURLResponse)?.statusCode ?? 0, errorMessage)
        }
        
        do {
            return try JSONDecoder().decode(T.self, from: data)
        } catch {
            throw APIError.decodingError(error)
        }
    }
}

// MARK: - Main API Service
class APIService {
    static let shared = APIService()
    private let repository: APIRepository
    
    init() {
        self.repository = DefaultAPIRepository(baseURL: "${safeBaseURL}")
    }
    
    func getRepository() -> APIRepository {
        return repository
    }
}`;
    };

    // eslint-disable-next-line no-unused-vars
    const generateFunctionalService = (safeBaseURL, endpoints, timeout, enableLogging) => {
      return `// MARK: - Functional API Helpers

struct APIHelpers {
    static func request<T: Codable>(
        baseURL: String,
        endpoint: String,
        method: HTTPMethod = .get,
        headers: [String: String] = [:],
        queryParams: [String: String]? = nil,
        body: (any Codable)? = nil,
        authToken: String? = nil,
        timeout: TimeInterval = ${timeout}
    ) async throws -> T {
        var urlString = baseURL + endpoint
        
        if let params = queryParams, !params.isEmpty {
            let queryString = QueryParamEncoder.encode(params)
            urlString += (urlString.contains("?") ? "&" : "?") + queryString
        }
        
        guard URLValidator.isValid(urlString), let url = URL(string: urlString) else {
            throw APIError.invalidURL
        }
        
        var request = URLRequest(url: url)
        request.httpMethod = method.rawValue
        request.timeoutInterval = timeout
        
        if !headers.keys.contains("Content-Type") {
            request.setValue("application/json", forHTTPHeaderField: "Content-Type")
        }
        
        for (key, value) in headers {
            request.setValue(value, forHTTPHeaderField: key)
        }
        
        if let token = authToken {
            request.setValue("Bearer \\(token)", forHTTPHeaderField: "Authorization")
        }
        
        if let body = body {
            do {
                request.httpBody = try JSONEncoder().encode(body)
            } catch {
                throw APIError.invalidBody
            }
        }
        
        ${enableLogging ? `print("[API] \\(method.rawValue) \\(urlString)")` : ''}
        
        let (data, response) = try await URLSession.shared.data(for: request)
        
        guard let httpResponse = response as? HTTPURLResponse else {
            throw APIError.invalidResponse
        }
        
        ${enableLogging ? `print("[API] Response: \\(httpResponse.statusCode)")` : ''}
        
        guard (200...299).contains(httpResponse.statusCode) else {
            let errorMessage = String(data: data, encoding: .utf8)
            throw APIError.httpError(httpResponse.statusCode, errorMessage)
        }
        
        do {
            return try JSONDecoder().decode(T.self, from: data)
        } catch {
            throw APIError.decodingError(error)
        }
    }
}

// MARK: - Main API Service (wrapper)
class APIService {
    static let shared = APIService()
    private let baseURL = "${safeBaseURL}"
    
    private init() {}
}`;
    };

    // eslint-disable-next-line no-unused-vars
    const generateInterceptorService = (safeBaseURL, endpoints, timeout, enableLogging, enableRetry) => {
      const retryCode = enableRetry ? `
    private func retryRequest(
        _ request: URLRequest,
        maxRetries: Int = 3,
        currentRetry: Int = 0
    ) async throws -> (Data, URLResponse) {
        do {
            return try await URLSession.shared.data(for: request)
        } catch {
            if currentRetry < maxRetries {
                let delay = pow(2.0, Double(currentRetry))
                try await Task.sleep(nanoseconds: UInt64(delay * 1_000_000_000))
                return try await retryRequest(request, maxRetries: maxRetries, currentRetry: currentRetry + 1)
            }
            throw error
        }
    }` : '';

      return `// MARK: - Interceptor-Based API Service

protocol RequestInterceptor {
    func intercept(_ request: inout URLRequest) throws
}

protocol ResponseInterceptor {
    func intercept(_ response: URLResponse, data: Data) throws
}

class APIService {
    private let baseURL: String
    private var requestInterceptors: [RequestInterceptor] = []
    private var responseInterceptors: [ResponseInterceptor] = []
    private var defaultHeaders: [String: String] = [:]
    ${enableLogging ? 'private let enableLogging = true' : ''}
    
    static let shared = APIService()
    
    init(baseURL: String = "${safeBaseURL}") {
        self.baseURL = baseURL
        self.defaultHeaders = ["Content-Type": "application/json"]
    }
    
    func setDefaultHeaders(_ headers: [String: String]) {
        self.defaultHeaders = headers
    }
    
    func addRequestInterceptor(_ interceptor: RequestInterceptor) {
        requestInterceptors.append(interceptor)
    }
    
    func addResponseInterceptor(_ interceptor: ResponseInterceptor) {
        responseInterceptors.append(interceptor)
    }
    
    func request<T: Codable>(
        endpoint: String,
        method: HTTPMethod = .get,
        headers: [String: String]? = nil,
        queryParams: [String: String]? = nil,
        body: (any Codable)? = nil,
        authToken: String? = nil
    ) async throws -> T {
        var urlString = baseURL + endpoint
        
        if let params = queryParams, !params.isEmpty {
            let queryString = QueryParamEncoder.encode(params)
            urlString += (urlString.contains("?") ? "&" : "?") + queryString
        }
        
        guard URLValidator.isValid(urlString), let url = URL(string: urlString) else {
            throw APIError.invalidURL
        }
        
        var request = URLRequest(url: url)
        request.httpMethod = method.rawValue
        request.timeoutInterval = ${timeout}
        
        for (key, value) in defaultHeaders {
            request.setValue(value, forHTTPHeaderField: key)
        }
        
        if let headers = headers {
            for (key, value) in headers {
                request.setValue(value, forHTTPHeaderField: key)
            }
        }
        
        if let token = authToken {
            request.setValue("Bearer \\(token)", forHTTPHeaderField: "Authorization")
        }
        
        if let body = body {
            do {
                request.httpBody = try JSONEncoder().encode(body)
            } catch {
                throw APIError.invalidBody
            }
        }
        
        for interceptor in requestInterceptors {
            try interceptor.intercept(&request)
        }
        
        ${enableLogging ? `print("[API] \\(method.rawValue) \\(urlString)")` : ''}
        
        ${enableRetry ? `let (data, response) = try await retryRequest(request)` : `let (data, response) = try await URLSession.shared.data(for: request)`}
        
        for interceptor in responseInterceptors {
            try interceptor.intercept(response, data: data)
        }
        
        guard let httpResponse = response as? HTTPURLResponse else {
            throw APIError.invalidResponse
        }
        
        ${enableLogging ? `print("[API] Response: \\(httpResponse.statusCode)")` : ''}
        
        guard (200...299).contains(httpResponse.statusCode) else {
            let errorMessage = String(data: data, encoding: .utf8)
            throw APIError.httpError(httpResponse.statusCode, errorMessage)
        }
        
        do {
            return try JSONDecoder().decode(T.self, from: data)
        } catch {
            throw APIError.decodingError(error)
        }
    }${retryCode}
}

// MARK: - Example Interceptors

class AuthInterceptor: RequestInterceptor {
    private let token: String
    
    init(token: String) {
        self.token = token
    }
    
    func intercept(_ request: inout URLRequest) throws {
        request.setValue("Bearer \\(token)", forHTTPHeaderField: "Authorization")
    }
}

class LoggingInterceptor: ResponseInterceptor {
    func intercept(_ response: URLResponse, data: Data) throws {
        if let httpResponse = response as? HTTPURLResponse {
            print("[API] Response: \\(httpResponse.statusCode)")
        }
    }
}`;
    };

    // Generate pattern-specific usage examples
    const generateUsageExamples = () => {
      if (endpoints.length === 0) return '// No endpoints available';
      
      const firstEndpoint = endpoints[0];
      const exampleCase = firstEndpoint.functionName;
      const params = extractParameters(firstEndpoint);
      const paramString = params.length > 0 ? '(' + params.map(p => `${p.name}: "value"`).join(', ') + ')' : '';
      
      let code = `// MARK: - Usage Examples

import SwiftUI

`;
      
      if (pattern === 'singleton') {
        code += `// Singleton Pattern Usage
Task {
    do {
        let endpoint = APIManager.${exampleCase}${paramString}
        let result = try await APIService.shared.call(endpoint: endpoint) as Result<YourResponseModel, APIError>
        
        switch result {
        case .success(let data):
            print("Success: \\(data)")
        case .failure(let error):
            print("Error: \\(error.localizedDescription)")
        }
    } catch {
        print("Error: \\(error)")
    }
}`;
      } else if (pattern === 'generic') {
        code += `// Generic Base Class Pattern Usage
Task {
    do {
        let authService = AuthService()
        let endpoint = APIManager.${exampleCase}${paramString}
        let result = try await authService.call(endpoint: endpoint) as Result<YourResponseModel, APIError>
        
        switch result {
        case .success(let data):
            print("Success: \\(data)")
        case .failure(let error):
            print("Error: \\(error.localizedDescription)")
        }
    } catch {
        print("Error: \\(error)")
    }
}`;
      } else if (pattern === 'repository') {
        code += `// Repository Pattern Usage
Task {
    do {
        let userRepository = UserRepository()
        let endpoint = APIManager.${exampleCase}${paramString}
        let result = try await userRepository.repository.call(endpoint: endpoint) as Result<YourResponseModel, APIError>
        
        switch result {
        case .success(let data):
            print("Success: \\(data)")
        case .failure(let error):
            print("Error: \\(error.localizedDescription)")
        }
    } catch {
        print("Error: \\(error)")
    }
}`;
      } else if (pattern === 'functional') {
        code += `// Functional Helpers Pattern Usage
Task {
    do {
        let endpoint = APIManager.${exampleCase}${paramString}
        let result = try await APIHelpers.call(endpoint: endpoint) as Result<YourResponseModel, APIError>
        
        switch result {
        case .success(let data):
            print("Success: \\(data)")
        case .failure(let error):
            print("Error: \\(error.localizedDescription)")
        }
    } catch {
        print("Error: \\(error)")
    }
}`;
      } else if (pattern === 'interceptor') {
        code += `// Interceptor-Based Pattern Usage
let apiService = APIService.shared
apiService.addRequestInterceptor(AuthInterceptor(token: "your-token"))
apiService.addResponseInterceptor(LoggingInterceptor())

Task {
    do {
        let endpoint = APIManager.${exampleCase}${paramString}
        let result = try await apiService.call(endpoint: endpoint) as Result<YourResponseModel, APIError>
        
        switch result {
        case .success(let data):
            print("Success: \\(data)")
        case .failure(let error):
            print("Error: \\(error.localizedDescription)")
        }
    } catch {
        print("Error: \\(error)")
    }
}`;
      }
      
      return code;
    };

    // Assemble final code blocks (matching reference pattern order)
    const blocks = [
      {
        name: 'HttpMethods Enum',
        code: generateHttpMethodsEnum(),
        requiresImport: false,
        order: 1
      },
      {
        name: 'ContentType Enum',
        code: generateContentTypeEnum(),
        requiresImport: false,
        order: 2
      },
      {
        name: 'APIEndpoints Struct',
        code: generateAPIEndpoints(),
        requiresImport: false,
        order: 3
      },
      {
        name: 'APIManager Enum',
        code: generateAPIManager(),
        requiresImport: false,
        order: 4
      },
      {
        name: 'APIClient Class',
        code: generateAPIClient(),
        requiresImport: false,
        order: 5
      },
      {
        name: `${pattern === 'singleton' ? 'Singleton' : pattern === 'generic' ? 'Generic Base Class' : pattern === 'repository' ? 'Repository' : pattern === 'functional' ? 'Functional Helpers' : 'Interceptor-Based'} Pattern Wrapper`,
        code: generatePatternWrapper(pattern, endpoints),
        requiresImport: false,
        order: 6
      },
      {
        name: 'APIError Enum',
        code: generateAPIError(),
        requiresImport: false,
        order: 7
      },
      {
        name: 'ErrorResponse Struct',
        code: generateErrorResponse(),
        requiresImport: false,
        order: 8
      },
      {
        name: 'Dictionary Extension',
        code: generateDictionaryExtension(),
        requiresImport: false,
        order: 9
      },
      {
        name: 'Usage Examples',
        code: generateUsageExamples(),
        requiresImport: true,
        order: 10
      }
    ];

    const imports = 'import Foundation\nimport UniformTypeIdentifiers';

    return {
      blocks,
      importStatement: imports
    };
  } catch (e) {
    return `// Error processing Postman collection: ${e.message}\n// Please ensure the JSON is a valid Postman collection export.`;
  }
};

/**
 * Generate code based on tool type and config
 */
export const generateCode = (toolType, config) => {
  switch (toolType) {
    case TOOL_TYPES.LOADER:
      return generateLoaderCode(config);
    case TOOL_TYPES.GRADIENT:
      return generateGradientCode(config);
    case TOOL_TYPES.CARD:
      return generateCardCode(config);
    case TOOL_TYPES.ANIMATION:
      return generateAnimationCode(config);
    case TOOL_TYPES.ALERT:
      return generateAlertCode(config);
    case TOOL_TYPES.PHOTO_PICKER:
      return generatePhotoPickerCode(config);
    case TOOL_TYPES.SHADOW:
      return generateShadowCode(config);
    case TOOL_TYPES.BUTTON:
      return generateButtonCode(config);
    case TOOL_TYPES.GRID:
      return generateGridCode(config);
    case TOOL_TYPES.PALETTE:
      return generatePaletteCode(config);
    case TOOL_TYPES.JSON_TO_MODEL:
      return generateJsonToModelCode(config);
    case TOOL_TYPES.API_GENERATOR:
      return generateApiCode(config);
    case TOOL_TYPES.STORYBOARD_CONVERTER:
      return generateStoryboardCode(config);
    case TOOL_TYPES.POSTMAN_IMPORTER:
      return generatePostmanCode(config);
    default:
      return '// Code generator for this tool is coming soon...';
  }
};

const clamp01 = (n) => Math.max(0, Math.min(1, Number(n) || 0));

const parseHex = (hex) => {
  const raw = (hex || '').trim().replace('#', '');
  if (raw.length === 3) {
    const r = parseInt(raw[0] + raw[0], 16);
    const g = parseInt(raw[1] + raw[1], 16);
    const b = parseInt(raw[2] + raw[2], 16);
    return { r, g, b };
  }
  if (raw.length === 6) {
    const r = parseInt(raw.slice(0, 2), 16);
    const g = parseInt(raw.slice(2, 4), 16);
    const b = parseInt(raw.slice(4, 6), 16);
    return { r, g, b };
  }
  return null;
};

const colorExprFromHex = (hex, opacity) => {
  const rgb = parseHex(hex);
  const o = clamp01(opacity);
  if (!rgb || [rgb.r, rgb.g, rgb.b].some((v) => Number.isNaN(v))) {
    return `Color.black.opacity(${o.toFixed(2)})`;
  }
  const r = (rgb.r / 255).toFixed(3);
  const g = (rgb.g / 255).toFixed(3);
  const b = (rgb.b / 255).toFixed(3);
  return `Color(red: ${r}, green: ${g}, blue: ${b}, opacity: ${o.toFixed(2)})`;
};

const sanitizeSwiftIdentifier = (name) => {
  const raw = (name || '').trim();
  const cleaned = raw
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .replace(/^[^a-zA-Z_]+/g, '')
    .replace(/_+/g, '_');
  return cleaned.length ? cleaned : 'color';
};

const generateShadowCode = (config) => {
  const radius = Number(config.radius) || 0;
  const x = Number(config.x) || 0;
  const y = Number(config.y) || 0;
  const colorExpr = colorExprFromHex(config.color, config.opacity);

  return `import SwiftUI\n\n// Shadow modifier\n${`.shadow(color: ${colorExpr}, radius: ${radius}, x: ${x}, y: ${y})`}\n`;
};

const generateButtonCode = (config) => {
  const title = (config.title || 'Button').replace(/"/g, '\\"');
  const activeStyle =
    config.activeStyle === 'outline' || config.activeStyle === 'ghost' ? config.activeStyle : 'filled';
  const activeSize = config.activeSize === 'sm' || config.activeSize === 'lg' ? config.activeSize : 'md';

  const variants = (config.variants && typeof config.variants === 'object') ? config.variants : {};

  const cornerRadius = Number(config.cornerRadius) || 0;

  const borderWidth = Number(config.borderWidth) || 1;
  const shadowRadius = Number(config.shadowRadius) || 0;
  const shadowX = Number(config.shadowX) || 0;
  const shadowY = Number(config.shadowY) || 0;
  const shadowOpacity = clamp01(config.shadowOpacity);

  const vPrimary = variants.primary || {};
  const vSecondary = variants.secondary || {};
  const vTertiary = variants.tertiary || {};

  const expr = (v, key, fallback) => {
    const value = v && v[key] != null ? v[key] : fallback;
    return value;
  };

  const bgExpr = (v) => {
    const mode = expr(v, 'backgroundMode', 'solid') === 'gradient' ? 'gradient' : 'solid';
    const direction = (expr(v, 'gradientDirection', 'leftToRight') || 'leftToRight').toString();
    const points = (() => {
      switch (direction) {
        case 'topToBottom':
          return { start: '.top', end: '.bottom' };
        case 'topLeftToBottomRight':
          return { start: '.topLeading', end: '.bottomTrailing' };
        case 'topRightToBottomLeft':
          return { start: '.topTrailing', end: '.bottomLeading' };
        case 'leftToRight':
        default:
          return { start: '.leading', end: '.trailing' };
      }
    })();
    const startColor = colorExprFromHex(expr(v, 'gradientStartColor', '#3B82F6'), 1);
    const endColor = colorExprFromHex(expr(v, 'gradientEndColor', '#22C55E'), 1);
    const solid = colorExprFromHex(expr(v, 'backgroundColor', '#3B82F6'), 1);
    return mode === 'gradient'
      ? `LinearGradient(colors: [${startColor}, ${endColor}], startPoint: ${points.start}, endPoint: ${points.end})`
      : solid;
  };

  const textExpr = (v) => colorExprFromHex(expr(v, 'textColor', '#FFFFFF'), 1);
  const borderColorExpr = (v) => colorExprFromHex(expr(v, 'borderColor', '#3B82F6'), 1);
  const shadowColorExpr = (v) => colorExprFromHex(expr(v, 'shadowColor', '#000000'), shadowOpacity);

  return `import SwiftUI

enum AppButtonVariant {
  case primary
  case secondary
  case tertiary
}

enum AppButtonStyle {
  case filled
  case outline
  case ghost
}

enum AppButtonSize {
  case sm
  case md
  case lg
}

struct AppButtonModifier: ViewModifier {
  let variant: AppButtonVariant
  let style: AppButtonStyle
  let size: AppButtonSize

  let cornerRadius: CGFloat = ${cornerRadius}
  let borderWidth: CGFloat = ${borderWidth}
  let shadowEnabled: Bool = ${!!config.shadowEnabled}
  let shadowRadius: CGFloat = ${shadowRadius}
  let shadowX: CGFloat = ${shadowX}
  let shadowY: CGFloat = ${shadowY}

  private var padding: (v: CGFloat, h: CGFloat, font: CGFloat) {
    switch size {
      case .sm: return (8, 12, 14)
      case .md: return (12, 16, 16)
      case .lg: return (14, 20, 18)
    }
  }

  private var background: AnyView {
    switch variant {
      case .primary: return AnyView(${bgExpr(vPrimary)})
      case .secondary: return AnyView(${bgExpr(vSecondary)})
      case .tertiary: return AnyView(${bgExpr(vTertiary)})
    }
  }

  private var textColor: Color {
    switch variant {
      case .primary: return ${textExpr(vPrimary)}
      case .secondary: return ${textExpr(vSecondary)}
      case .tertiary: return ${textExpr(vTertiary)}
    }
  }

  private var borderColor: Color {
    switch variant {
      case .primary: return ${borderColorExpr(vPrimary)}
      case .secondary: return ${borderColorExpr(vSecondary)}
      case .tertiary: return ${borderColorExpr(vTertiary)}
    }
  }

  private var shadowColor: Color {
    switch variant {
      case .primary: return ${shadowColorExpr(vPrimary)}
      case .secondary: return ${shadowColorExpr(vSecondary)}
      case .tertiary: return ${shadowColorExpr(vTertiary)}
    }
  }

  func body(content: Content) -> some View {
    let shape = RoundedRectangle(cornerRadius: cornerRadius, style: .continuous)

    let resolvedText: Color =
      style == .filled ? textColor : borderColor

    return content
      .font(.system(size: padding.font, weight: .semibold))
      .padding(.vertical, padding.v)
      .padding(.horizontal, padding.h)
      .foregroundStyle(resolvedText)
      .background(style == .filled ? AnyView(background) : AnyView(Color.clear))
      .clipShape(shape)
      .overlay(
        Group {
          if style == .outline {
            shape.stroke(borderColor, lineWidth: borderWidth)
          }
        }
      )
      .shadow(
        color: shadowEnabled ? shadowColor : .clear,
        radius: shadowEnabled ? shadowRadius : 0,
        x: shadowEnabled ? shadowX : 0,
        y: shadowEnabled ? shadowY : 0
      )
  }
}

extension View {
  func appButton(variant: AppButtonVariant, style: AppButtonStyle, size: AppButtonSize) -> some View {
    modifier(AppButtonModifier(variant: variant, style: style, size: size))
  }
}

struct ButtonsDemo: View {
  var body: some View {
    VStack(alignment: .leading, spacing: 12) {
      Button("${title}") { }
        .appButton(variant: .primary, style: .${activeStyle}, size: .${activeSize})

      Button("${title}") { }
        .appButton(variant: .secondary, style: .${activeStyle}, size: .${activeSize})

      Button("${title}") { }
        .appButton(variant: .tertiary, style: .${activeStyle}, size: .${activeSize})
    }
    .padding(16)
  }
}
`;
};

const generateGridCode = (config) => {
  const mode = config.mode === 'fixed' ? 'fixed' : 'adaptive';
  const columnSpacing = Number(config.columnSpacing) || 0;
  const rowSpacing = Number(config.rowSpacing) || 0;

  const columns =
    mode === 'fixed'
      ? `let columns: [GridItem] = Array(repeating: GridItem(.flexible(), spacing: ${columnSpacing}), count: ${Math.max(
          1,
          Number(config.fixedCount) || 2
        )})`
      : `let columns: [GridItem] = [\n  GridItem(.adaptive(minimum: ${Math.max(40, Number(config.adaptiveMinWidth) || 120)}), spacing: ${columnSpacing})\n]`;

  return `import SwiftUI\n\nstruct GridView: View {\n  let items = Array(1...${Math.max(1, Number(config.itemCount) || 12)})\n\n  var body: some View {\n    ${columns}\n\n    ScrollView {\n      LazyVGrid(columns: columns, spacing: ${rowSpacing}) {\n        ForEach(items, id: \\.self) { item in\n          RoundedRectangle(cornerRadius: 12)\n            .fill(Color.gray.opacity(0.15))\n            .frame(height: 80)\n            .overlay(Text("\\\\(item)"))\n        }\n      }\n      .padding(16)\n    }\n  }\n}\n`;
};

const generatePaletteCode = (config) => {
  const colors = Array.isArray(config.colors) ? config.colors : [];
  const lines = colors
    .map((c) => {
      const name = sanitizeSwiftIdentifier(c.name);
      const expr = colorExprFromHex(c.hex, 1);
      return `  static let ${name} = ${expr}`;
    })
    .join('\n');

  return `import SwiftUI\n\n// Named colors generated from hex\nextension Color {\n${lines || '  // Add colors in the UI to generate output'}\n}\n\n// Usage:\n// Text("Hello").foregroundStyle(.brandPrimary)\n`;
};

